import { set, toggle } from '@/utils/vuex'
import Vue from 'vue'
import Vuex from 'vuex'
import router from './components/router'
import { auth, db, functions } from './firebase'
import {
  IABF_DANCER_YEAR,
  IABF_REGISTRATION_YEAR,
  IABF_OPERABLE_YEARS,
  IABF_ADMIN_YEARS,
  IABF_ARCHIVE_YEARS,
  IABF_PEOPLE_COLLECTION,
  facultyCollection,
  IABF_FESTIVAL_INVENTORY_COLLECTION,
  IABF_COSTUME_CLOSET_COLLECTION,
  IABF_CLOSET_LOANS_COLLECTION,
  IABF_DANCER_LEDGER_COLLECTION,
  dancersCollection,
  isArchiveYear
} from '@/config/iabf-event'
import { IABF, iabfForYear, col } from '@/config/iabf-collections'
import { festivalWeek } from '@/config/iabf-festival-dates'
import { normalizeWhatsappInviteUrl } from '@/config/iabf-intro'
import {
  setDeadlineOverrides,
  setRegistrationAccessRuntime,
  resolveRegistrationAccess
} from '@/config/iabf-deadlines'
import { ONSITE_ROOM_LIMIT_KEYS, setRegistrationDeadlineOverrides } from '@/config/iabf-registration-2027'
import { mailingAddressError, normalizeCountryCode } from '@/config/iabf-mailing-address'
import { orderedCoffeeMakerStock } from '@/config/iabf-dietary'
import { getRegistrationRoomAvailability } from '@/utils/iabf-registration-api'
import {
  adjustIabfStayInventory,
  recountRegistrationInventory,
  refreshRegistrationInventory
} from '@/utils/iabf-room-inventory'
import { initialLayout } from '@/utils/app-layout'
import { isOffsiteLodgingRoom } from '@/utils/iabf-registration-package'
import {
  DEFAULT_PHOTOSHOOT_CONFIG,
  locationForSetting,
  photoshootSlotId,
  splitBlockIntoSlots
} from '@/config/iabf-photoshoot'
import { isSoloPiece, MAX_SOLO_PIECES, soloPieceCount } from '@/utils/iabf-stage-time'

const MONTH_INDEX = {
  January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
  July: 6, August: 7, September: 8, October: 9, November: 10, December: 11
}
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

/** Days between festival arrival dates (for shifting carpool slot labels year→year). */
function carpoolFestivalDayOffset (fromYear, toYear) {
  const from = festivalWeek(fromYear).arrivalIso
  const to = festivalWeek(toYear).arrivalIso
  const a = new Date(from + 'T12:00:00')
  const b = new Date(to + 'T12:00:00')
  return Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000))
}

/**
 * Shift a carpool date label like "Monday, July 6" by dayOffset within fromYear's calendar,
 * then re-format (weekday updates with the new date).
 */
function shiftCarpoolDateLabel (dateStr, fromYear, dayOffset) {
  if (!dateStr || typeof dateStr !== 'string') {
    return dateStr || ''
  }
  const trimmed = dateStr.trim()
  if (!trimmed) {
    return ''
  }
  // "Monday, July 6" or "Monday, July 6, 2026"
  const m = trimmed.match(/^([A-Za-z]+),\s+([A-Za-z]+)\s+(\d{1,2})(?:,\s*(\d{4}))?$/)
  if (!m) {
    return trimmed
  }
  const month = MONTH_INDEX[m[2]]
  if (month === undefined) {
    return trimmed
  }
  const day = parseInt(m[3], 10)
  const year = parseInt(m[4] || fromYear, 10)
  const d = new Date(year, month, day, 12, 0, 0)
  d.setDate(d.getDate() + (dayOffset || 0))
  return `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`
}
import {
  absorbMergedPersonStub,
  changePersonPrimaryEmail,
  enrollmentWithPersonProfile,
  findOrCreatePerson,
  findPersonByEmail,
  findPersonByUid,
  isMergedPersonRecord,
  linkPersonEmailAlias,
  linkUidToPerson,
  loadPersonById,
  normalizeEmail,
  removeYearFromPerson,
  resolvePersonForEnrollment,
  uniqueEmails,
  updatePersonProfile
} from '@/utils/iabf-people'
import {
  computeAttendanceYears,
  earlyRosterYearsForEmails,
  enrollmentYearsToScan
} from '@/utils/iabf-attendance-years'
import { facultyAsDancer, facultyMoneyNum, facultyYearsAtIabfNum } from '@/utils/iabf-faculty'
import {
  COMPETITION_RESULTS_COLLECTION,
  ARCHIVE_FESTIVAL_YEARS,
  SHOWCASE_HISTORY_COLLECTION,
  competitionResultMatchesDancer,
  dancersCollectionName,
  normalizeCompetitionResult,
  piecesCollectionName,
  showcaseHistoryPieceMatchesDancer,
  historyPersonIdList,
  shouldReplaceResultForPiece,
  isNamedSpecialAwardResult,
  normalizeSpecialAward,
  yearsForFestivalHistory,
  corpsPiecesFromEnrollment,
  isArchiveGroupPiece,
  pieceTitleKey,
  findCollapsibleTitleKey,
  mergeHistoryPieceRow,
  dedupeCompetitionResults
} from '@/utils/iabf-festival-history'

Vue.use(Vuex)

const ADMIN_YEAR_STORAGE_KEY = 'iabfAdminFestivalYear'
const MASQUERADE_STORAGE_KEY = 'iabf-masquerade-dancer'

function readStoredAdminYear () {
  try {
    const raw = localStorage.getItem(ADMIN_YEAR_STORAGE_KEY)
    const y = parseInt(raw, 10)
    if (IABF_ADMIN_YEARS.includes(y)) {
      return y
    }
  } catch (e) { /* private mode / SSR */ }
  return IABF_DANCER_YEAR
}

function readStoredMasquerade () {
  try {
    const raw = sessionStorage.getItem(MASQUERADE_STORAGE_KEY)
    if (!raw) {
      return { id: null, dancer: null }
    }
    const parsed = JSON.parse(raw)
    if (!parsed || !parsed.id) {
      return { id: null, dancer: null }
    }
    return { id: parsed.id, dancer: parsed.dancer || null }
  } catch (e) {
    return { id: null, dancer: null }
  }
}

function writeStoredMasquerade (id, dancer) {
  try {
    if (!id || !dancer) {
      sessionStorage.removeItem(MASQUERADE_STORAGE_KEY)
      return
    }
    sessionStorage.setItem(MASQUERADE_STORAGE_KEY, JSON.stringify({ id, dancer }))
  } catch (e) { /* private mode */ }
}

const storedMasquerade = readStoredMasquerade()

/** Admin year collection map (year switcher). */
function adminIabf (state) {
  return iabfForYear(state.adminFestivalYear || IABF_DANCER_YEAR)
}

function piecesCol (state, useAdminYear) {
  if (useAdminYear) {
    const cols = adminIabf(state)
    if (cols && cols.pieces) {
      return cols.pieces
    }
  }
  return IABF.pieces
}

function dancersCol (state, useAdminYear) {
  if (useAdminYear) {
    const cols = adminIabf(state)
    if (cols && cols.dancers) {
      return cols.dancers
    }
  }
  return IABF.dancers
}

/**
 * Collection map for the logged-in dancer's enrollment year.
 * 2027 registration when present; otherwise their wrap-up year (e.g. 2026).
 * Admin masquerade uses the admin year switcher.
 */
function dancerIabf (state) {
  if (state.masqueradeDancerId) {
    const y = Number(
      (state.masqueradeDancer && state.masqueradeDancer.festivalYear) ||
      state.adminFestivalYear ||
      IABF_DANCER_YEAR
    )
    return iabfForYear(Number.isFinite(y) && y > 0 ? y : IABF_DANCER_YEAR)
  }
  return iabfForYear(state.iabfDancerYear || IABF_DANCER_YEAR)
}

/** Faculty year docs live in iabf-faculty-{year}, not the dancer ticket collection. */
function yearPersonRef (state, getters, id) {
  const cols = dancerIabf(state)
  const d = (getters && getters.currentIABFDancer) || {}
  const colName = (d.isFaculty || d.personType === 'faculty') ? cols.faculty : cols.dancers
  return db.collection(colName).doc(id)
}

/** Newest operable year first: 2027 registration, then 2026 wrap-up, etc. */
function dancerLoginYears () {
  const years = IABF_OPERABLE_YEARS.slice().sort((a, b) => b - a)
  if (!years.includes(IABF_DANCER_YEAR)) {
    years.unshift(IABF_DANCER_YEAR)
  }
  return years
}

async function lookupDocInCollection (colName, { uid, email } = {}) {
  if (!colName) {
    return null
  }
  if (uid) {
    const byUid = await db.collection(colName).where('uid', '==', uid).limit(1).get()
    if (!byUid.empty) {
      return byUid.docs[0]
    }
  }
  const lower = normalizeEmail(email)
  if (lower) {
    let byEmail = await db.collection(colName).where('email', '==', lower).limit(1).get()
    if (byEmail.empty && email && email !== lower) {
      byEmail = await db.collection(colName).where('email', '==', email).limit(1).get()
    }
    if (!byEmail.empty) {
      return byEmail.docs[0]
    }
  }
  return null
}

/**
 * This dancer's enrollment in a year-scoped collection.
 * List queries must constrain uid or email so isOwner() rules allow them.
 * personId is last — dancers cannot query by personId (admin can).
 */
async function relatedHistoryPersonIds ({ personId, uid, email } = {}) {
  const ids = new Set(historyPersonIdList({ personId }))
  const years = []
  const add = (id) => {
    const s = String(id || '').trim()
    if (s) {
      ids.add(s)
    }
  }
  const addYears = (raw) => {
    const list = Array.isArray(raw) ? raw : []
    list.forEach((v) => {
      const n = Number(v)
      if (Number.isFinite(n) && n > 0) {
        years.push(n)
      }
    })
  }
  const colRef = db.collection(IABF_PEOPLE_COLLECTION)
  const docs = []
  if (personId) {
    try {
      const snap = await colRef.doc(personId).get()
      if (snap.exists) {
        docs.push(snap)
      }
    } catch (e) { /* ignore */ }
  }
  if (uid) {
    try {
      const snap = await colRef.where('uid', '==', uid).get()
      snap.forEach((doc) => { docs.push(doc) })
    } catch (e) { /* ignore */ }
  }
  if (email) {
    try {
      const snap = await colRef.where('email', '==', email).get()
      snap.forEach((doc) => { docs.push(doc) })
    } catch (e) { /* ignore */ }
  }
  docs.forEach((doc) => {
    add(doc.id)
    const d = doc.data() || {}
    add(d.mergedInto)
    addYears(d.yearsAttended)
  })
  return { personIds: Array.from(ids), yearsAttended: years }
}

async function lookupHistoryEnrollment (colName, { uid, email, rawEmail, personId, personIds } = {}) {
  if (!colName) {
    return null
  }
  const attempts = []
  if (uid) {
    attempts.push(['uid', uid])
  }
  if (email) {
    attempts.push(['email', email])
  }
  if (rawEmail && rawEmail !== email) {
    attempts.push(['email', rawEmail])
  }
  const pids = historyPersonIdList({ personId, personIds })
  pids.forEach((id) => {
    attempts.push(['personId', id])
  })
  for (let i = 0; i < attempts.length; i++) {
    const field = attempts[i][0]
    const value = attempts[i][1]
    try {
      const snap = await db.collection(colName).where(field, '==', value).limit(1).get()
      if (!snap.empty) {
        return snap.docs[0]
      }
    } catch (e) {
      console.warn('history enrollment lookup failed', colName, field, e)
    }
  }
  return null
}

/**
 * Newest festival year wins. In the same year a dancer ticket beats faculty
 * (a 2027 registrant who also teaches still gets dancer prep). A 2027 faculty
 * invite beats a leftover 2026 dancer row — otherwise teachers who danced last
 * year land on the alumni “register for 2027” screen.
 */
async function findLoginAccount ({ uid, email, personId } = {}) {
  for (const year of dancerLoginYears()) {
    const dancerDoc = await lookupDocInCollection(dancersCollection(year), { uid, email })
    if (dancerDoc) {
      return { kind: 'dancer', year, doc: dancerDoc }
    }
    const facultyDoc = await lookupDocInCollection(facultyCollection(year), { uid, email })
    if (facultyDoc) {
      return { kind: 'faculty', year, doc: facultyDoc }
    }
  }
  let pid = personId || null
  if (!pid && email) {
    try {
      const person = await findPersonByEmail(db, normalizeEmail(email))
      pid = person && person.id
    } catch (e) {
      console.warn('findLoginAccount person lookup failed', e)
    }
  }
  if (!pid) {
    return null
  }
  for (const year of dancerLoginYears()) {
    const dancerCol = dancersCollection(year)
    if (dancerCol) {
      const snap = await db.collection(dancerCol).where('personId', '==', pid).limit(1).get()
      if (!snap.empty) {
        return { kind: 'dancer', year, doc: snap.docs[0] }
      }
    }
    const facCol = facultyCollection(year)
    if (facCol) {
      const snap = await db.collection(facCol).where('personId', '==', pid).limit(1).get()
      if (!snap.empty) {
        return { kind: 'faculty', year, doc: snap.docs[0] }
      }
    }
  }
  return null
}

async function hydrateFacultyRecord (commit, doc, year, uid) {
  let data = { id: doc.id, ...doc.data() }
  if (uid && data.uid !== uid) {
    try {
      await doc.ref.update({ uid, updatedAt: Date.now() })
      data = { ...data, uid }
    } catch (e) {
      console.warn('faculty uid link failed', e)
    }
  }
  if (uid && data.personId) {
    try {
      await linkUidToPerson(db, data.personId, uid)
    } catch (e) {
      console.warn('faculty person uid link failed', e)
    }
  }
  return hydrateEnrollmentWithPerson(commit, facultyAsDancer(data, year), year)
}

/**
 * Collections that may hold a canceled dancer.
 * Archive years are included so a removal is not put back the next time
 * attendance years are rebuilt from the 2022/2023 roster or an enrollment.
 * 2026 also has the unsuffixed legacy collection.
 */
function canceledDancerCollections () {
  const years = IABF_ARCHIVE_YEARS.concat(IABF_OPERABLE_YEARS)
  const list = years.map((year) => ({
    name: col('canceled-dancers', year),
    year
  }))
  list.push({ name: 'iabf-canceled-dancers', year: 2026 })
  return list
}

/**
 * Find a canceled-dancer record with a held deposit (for login when not actively registered).
 * Returns { data, id, year, collection } or null.
 */
function dollarsToCents (raw) {
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) {
    return 0
  }
  return Math.round(n * 100)
}

/** Paid festival deposit on an enrollment (dollars on initialPayment, not legacy `deposit`). */
function depositOnFileCentsFromEnrollment (d) {
  const rec = d || {}
  const fromCents = Math.round(Number(rec.depositOnFileCents) || 0)
  if (fromCents > 0) {
    return fromCents
  }
  const initial = dollarsToCents(rec.initialPayment)
  if (initial > 0) {
    return initial
  }
  const credit = Math.round(Number(rec.depositCreditCents) || 0)
  if (credit > 0) {
    return credit
  }
  return dollarsToCents(rec.deposit)
}

function paymentsOnFileCentsFromEnrollment (d) {
  const rec = d || {}
  let cents = 0
  for (let i = 1; i <= 6; i++) {
    if (rec['payment' + i + 'Status'] === 'Paid') {
      cents += dollarsToCents(rec['payment' + i + 'Due'])
    }
  }
  return cents
}

async function findCanceledDancerByEmail (email, personId) {
  const lower = normalizeEmail(email)
  if (!lower && !personId) {
    return null
  }
  for (const c of canceledDancerCollections()) {
    try {
      let snapshot = lower
        ? await db.collection(c.name).where('email', '==', lower).limit(5).get()
        : { empty: true, docs: [] }
      if (snapshot.empty && email && email !== lower) {
        snapshot = await db.collection(c.name).where('email', '==', email).limit(5).get()
      }
      if (snapshot.empty && personId) {
        snapshot = await db.collection(c.name).where('personId', '==', personId).limit(5).get()
      }
      for (const doc of snapshot.docs) {
        const data = doc.data() || {}
        const cents = Math.round(Number(data.depositOnFileCents) || 0)
        // Allow login even if deposit is 0 so they can see cancel status;
        // prefer records that still have a transferable deposit.
        return {
          id: doc.id,
          data,
          year: c.year,
          collection: c.name,
          depositOnFileCents: cents
        }
      }
    } catch (e) {
      console.warn('canceled dancer lookup failed for', c.name, e)
    }
  }
  return null
}

/** Build a dashboard-safe dancer object from a canceled record. */
function canceledAccountFromRecord (found) {
  const d = found.data || {}
  return {
    id: 'canceled:' + found.id,
    canceledRecordId: found.id,
    canceledCollection: found.collection,
    isCanceledAccount: true,
    firstName: d.firstName || '',
    lastName: d.lastName || '',
    email: normalizeEmail(d.email),
    personId: d.personId || null,
    festivalYear: found.year,
    depositOnFileCents: Math.round(Number(d.depositOnFileCents) || 0),
    paymentsOnFileCents: Math.round(Number(d.paymentsOnFileCents) || 0),
    depositCreditConsumed: !!d.depositCreditConsumed,
    depositCreditConsumedYear: d.depositCreditConsumedYear || null,
    canceledAt: d.canceledAt || null,
    cancelReason: d.cancelReason || '',
    notes: d.notes || '',
    // Keep prep flags false so checklist/cards stay quiet
    dietaryRestrictionsEntered: false,
    emergencyContactEntered: false,
    travelDetailsEntered: false,
    carpoolResponded: false
  }
}

/** Attach person profile onto an enrollment and commit both to state. */
async function hydrateEnrollmentWithPerson (commit, enrollment, year) {
  if (!enrollment || !enrollment.id) {
    commit('setCurrentIABFDancer', enrollment || {})
    commit('setCurrentIABFPerson', null)
    return enrollment
  }
  const festivalYear = Number(year || enrollment.festivalYear) || IABF_DANCER_YEAR
  commit('setIabfDancerYear', festivalYear)
  enrollment = { ...enrollment, festivalYear }

  let person = null
  try {
    person = await resolvePersonForEnrollment(db, enrollment)
  } catch (e) {
    // Non-fatal: pre-migration or rules race — enrollment-only still works.
    console.warn('iabf-people resolve failed', e)
  }
  if (person && !enrollment.personId && !enrollment.isFaculty) {
    // Best-effort back-link if migration missed this enrollment.
    try {
      const colName = dancersCollection(festivalYear)
      await db.collection(colName).doc(enrollment.id).update({
        personId: person.id,
        festivalYear
      })
      enrollment = { ...enrollment, personId: person.id, festivalYear }
    } catch (e) {
      console.warn('iabf personId back-link failed', e)
    }
  }
  commit('setCurrentIABFPerson', person)
  const merged = enrollmentWithPersonProfile(enrollment, person)
  const next = { ...merged, festivalYear }
  commit('setCurrentIABFDancer', next)
  commit('patchEffectiveIABFDancer', next)
  return merged
}

const MAILING_ADDRESS_FIELDS = [
  'addressStreet',
  'addressStreet2',
  'addressCity',
  'addressState',
  'addressZip',
  'addressCountry'
]

async function attachPersonAttendanceYears (dancers) {
  const ids = Array.from(new Set((dancers || []).map(d => d.personId).filter(Boolean)))
  const byId = new Map()
  await Promise.all(ids.map(async (id) => {
    try {
      const snap = await db.collection(IABF_PEOPLE_COLLECTION).doc(id).get()
      if (snap.exists) {
        byId.set(id, snap.data() || {})
      }
    } catch (e) {
      console.warn('yearsAttended lookup failed', id, e)
    }
  }))
  return (dancers || []).map((d) => {
    const person = (d.personId && byId.get(d.personId)) || {}
    const years = person.yearsAttended != null
      ? unionAttendanceYears([person.yearsAttended])
      : unionAttendanceYears([d.yearsAttended])
    const mailing = {}
    MAILING_ADDRESS_FIELDS.forEach((key) => {
      mailing[key] = String(d[key] || person[key] || '').trim()
    })
    return {
      ...d,
      ...mailing,
      yearsAttended: years,
      yearsAttendedCount: years.length,
      yearsAttendedLabel: years.length ? years.join(', ') : ''
    }
  })
}

function unionAttendanceYears (lists) {
  const set = new Set()
  for (const list of lists) {
    for (const raw of list || []) {
      const n = Number(raw)
      if (Number.isFinite(n) && n > 0) {
        set.add(n)
      }
    }
  }
  return Array.from(set).sort((a, b) => a - b)
}

async function writePersonAttendanceYears (commit, person, years, dancerId) {
  if (!person || !person.id) {
    return person
  }
  const nextYears = unionAttendanceYears([years])
  const prev = unionAttendanceYears([person.yearsAttended])
  if (nextYears.join(',') === prev.join(',')) {
    if (dancerId) {
      commit('patchEffectiveIABFDancer', { id: dancerId, yearsAttended: nextYears })
    }
    return { ...person, yearsAttended: nextYears }
  }
  await db.collection(IABF_PEOPLE_COLLECTION).doc(person.id).set({
    yearsAttended: nextYears,
    updatedAt: Date.now()
  }, { merge: true })
  const nextPerson = { ...person, yearsAttended: nextYears }
  commit('setCurrentIABFPerson', nextPerson)
  commit('patchEffectiveIABFDancer', dancerId
    ? { id: dancerId, yearsAttended: nextYears }
    : { yearsAttended: nextYears })
  return nextPerson
}

async function loadCanceledYearsForIdentity (email, personId) {
  const years = new Set()
  const lower = normalizeEmail(email)
  if (!lower && !personId) {
    return years
  }
  await Promise.all(canceledDancerCollections().map(async (c) => {
    try {
      let snapshot = { empty: true, docs: [] }
      if (lower) {
        snapshot = await db.collection(c.name).where('email', '==', lower).limit(1).get()
      }
      if (snapshot.empty && personId) {
        snapshot = await db.collection(c.name).where('personId', '==', personId).limit(1).get()
      }
      if (!snapshot.empty) {
        years.add(c.year)
      }
    } catch (e) {
      console.warn('canceled year lookup failed', c.name, e)
    }
  }))
  return years
}

async function findEnrollmentForPersonYear (year, email, personId) {
  const colName = dancersCollection(year)
  if (!colName) {
    return null
  }
  const lower = normalizeEmail(email)
  try {
    if (personId) {
      const byPerson = await db.collection(colName).where('personId', '==', personId).limit(1).get()
      if (!byPerson.empty) {
        return { id: byPerson.docs[0].id, ...(byPerson.docs[0].data() || {}) }
      }
    }
    if (lower) {
      const byEmail = await db.collection(colName).where('email', '==', lower).limit(1).get()
      if (!byEmail.empty) {
        return { id: byEmail.docs[0].id, ...(byEmail.docs[0].data() || {}) }
      }
    }
  } catch (e) {
    console.warn('enrollment year lookup failed', year, e)
  }
  return null
}

/**
 * Rebuild yearsAttended from enrollments (2024+) + 2022/2023 roster emails.
 * Showcase/competition are not used. Call on register, cancel, or admin Recalculate.
 */
async function recalculateAttendanceYearsForPerson (commit, person, enrollment) {
  if (!person || !person.id) {
    return person
  }
  const emails = uniqueEmails([
    person.email,
    enrollment && enrollment.email,
    ...(person.previousEmails || [])
  ])
  const canceledYears = await loadCanceledYearsForIdentity(
    emails[0] || person.email,
    person.id
  )
  const liveYear = (enrollment && enrollment.id && !enrollment.isCanceledAccount)
    ? Number(enrollment.festivalYear)
    : NaN
  const enrollmentYears = []
  const enrollmentEmails = []
  await Promise.all(enrollmentYearsToScan().map(async (y) => {
    if (canceledYears.has(y)) {
      return
    }
    if (Number.isFinite(liveYear) && y === liveYear) {
      enrollmentYears.push(y)
      return
    }
    const found = await findEnrollmentForPersonYear(y, emails[0] || person.email, person.id)
    if (found) {
      enrollmentYears.push(y)
      if (found.email) {
        enrollmentEmails.push(found.email)
      }
    }
  }))
  emails.push(...uniqueEmails(enrollmentEmails))
  const next = computeAttendanceYears({
    enrollmentYears,
    earlyRosterYears: earlyRosterYearsForEmails(uniqueEmails(emails)),
    liveYear,
    canceledYears: Array.from(canceledYears)
  })
  const dancerId = enrollment && enrollment.id ? enrollment.id : null
  const nextPerson = await writePersonAttendanceYears(commit, person, next, dancerId)
  if (dancerId) {
    commit('patchEffectiveIABFDancer', {
      id: dancerId,
      yearsAttended: nextPerson.yearsAttended || next,
      canceledYears: Array.from(canceledYears).sort((a, b) => a - b)
    })
  }
  return nextPerson
}

/**
 * Remove year-scoped docs that hang off a dancer enrollment (test-delete / hard-delete).
 * Failures are logged, not thrown — enrollment delete should still proceed.
 */
async function cleanupDeletedDancerRelated (cols, dancerId, travelDetailsId) {
  if (!cols || !dancerId) {
    return
  }
  const warn = (label, e) => {
    console.warn('deleteIABFDancer cleanup (' + label + ') failed', e)
  }

  if (cols.dancerIntros) {
    try {
      await db.collection(cols.dancerIntros).doc(dancerId).delete()
    } catch (e) {
      warn('intro', e)
    }
  }

  if (cols.travelDetails) {
    const seen = {}
    if (travelDetailsId) {
      seen[travelDetailsId] = true
      try {
        await db.collection(cols.travelDetails).doc(travelDetailsId).delete()
      } catch (e) {
        warn('travelDetailsId', e)
      }
    }
    try {
      const snap = await db.collection(cols.travelDetails).where('personId', '==', dancerId).get()
      await Promise.all(snap.docs.map(doc => {
        if (seen[doc.id]) {
          return null
        }
        seen[doc.id] = true
        return doc.ref.delete()
      }))
    } catch (e) {
      warn('travelDetails query', e)
    }
  }

  const pieceCols = [cols.pieces].filter(Boolean)
  for (const piecesCol of pieceCols) {
    try {
      const snap = await db.collection(piecesCol).get()
      const ops = []
      snap.docs.forEach(doc => {
        const data = doc.data() || {}
        const raw = Array.isArray(data.dancers) ? data.dancers : []
        const dancerIds = raw.map(d => typeof d === 'string' ? d : (d && d.id)).filter(Boolean)
        const inDancers = dancerIds.indexOf(dancerId) !== -1
        const submitted = data.submittedByDancerId === dancerId
        const canceledBy = data.canceledByDancerId === dancerId
        const entrants = Array.isArray(data.competitionEntrants) ? data.competitionEntrants : []
        const inEntrants = entrants.indexOf(dancerId) !== -1
        if (!inDancers && !submitted && !canceledBy && !inEntrants) {
          return
        }
        const nextDancers = raw.filter(d => {
          const did = typeof d === 'string' ? d : (d && d.id)
          return did && did !== dancerId
        })
        const nextEntrants = entrants.filter(eid => eid !== dancerId)
        const soloGone = !nextDancers.length && (inDancers || submitted || canceledBy)
        if (soloGone) {
          ops.push(doc.ref.delete())
          return
        }
        const patch = {}
        if (inDancers) {
          patch.dancers = nextDancers
        }
        if (inEntrants) {
          patch.competitionEntrants = nextEntrants
        }
        if (submitted) {
          patch.submittedByDancerId = null
        }
        if (Object.keys(patch).length) {
          ops.push(doc.ref.update(patch))
        }
      })
      await Promise.all(ops)
    } catch (e) {
      warn('pieces ' + piecesCol, e)
      throw e
    }
  }

  const carpoolCols = [cols.carpools].filter(Boolean)
  for (const carpoolsCol of carpoolCols) {
    try {
      const snap = await db.collection(carpoolsCol).get()
      await Promise.all(snap.docs.map(doc => {
        const dancers = (doc.data() || {}).dancers || []
        const next = dancers.filter(d => {
          if (!d) {
            return false
          }
          if (typeof d === 'string') {
            if (d.indexOf('guest-') === 0) {
              return true
            }
            return d !== dancerId
          }
          if (d.isGuest || d.isFaculty || d.personType === 'faculty') {
            return true
          }
          const rid = d.id
          if (typeof rid === 'string' && rid.indexOf('guest-') === 0) {
            return true
          }
          return rid !== dancerId
        })
        if (next.length === dancers.length) {
          return null
        }
        return doc.ref.update({ dancers: next })
      }))
    } catch (e) {
      warn('carpools ' + carpoolsCol, e)
    }
  }

  if (cols.guestRegistrations) {
    try {
      const snap = await db.collection(cols.guestRegistrations).get()
      await Promise.all(snap.docs.map(doc => {
        const data = doc.data() || {}
        if (isFacultyLike(data)) {
          return null
        }
        const hostId = data.dancerId || data.personId || ''
        return hostId === dancerId ? doc.ref.delete() : null
      }))
    } catch (e) {
      warn('guestRegistrations', e)
    }
  }
}

function isFacultyLike (data) {
  return !!(data && (data.isFaculty || data.personType === 'faculty' || data.type === 'faculty'))
}

function carpoolRiderId (rider) {
  if (!rider) {
    return ''
  }
  if (typeof rider === 'string') {
    return rider
  }
  return rider.id || ''
}

/** Keep faculty, named guests, and anyone still on this year's roster. */
function keepCarpoolRider (rider, isAlivePerson) {
  if (!rider) {
    return false
  }
  if (typeof rider !== 'string' && isFacultyLike(rider)) {
    return true
  }
  if (typeof rider !== 'string' && rider.isGuest) {
    return true
  }
  const id = carpoolRiderId(rider)
  if (!id) {
    return true
  }
  if (id.indexOf('guest-') === 0) {
    return true
  }
  return isAlivePerson(id)
}

/**
 * Guest/meal rows to drop only when they clearly belonged to a dancer who
 * is gone. Faculty meals often have type:faculty and no dancerId — keep those.
 */
function isOrphanGuestRegistration (data, isAlivePerson) {
  if (!data || isFacultyLike(data)) {
    return false
  }
  const hostId = data.dancerId || data.personId || ''
  if (!hostId) {
    return false
  }
  return !isAlivePerson(hostId)
}

/**
 * Leftover dancer-enrollment docs after a cancel/delete.
 * Faculty ids count as live. Guest riders and faculty meals stay.
 * Never deletes piece documents — only drops dead dancer ids.
 * Never walks store, corps, waitlist, inventory, closet, or accounting.
 */
async function sweepOrphanedYearData (cols) {
  if (!cols || !cols.dancers) {
    return
  }
  const dancerSnap = await db.collection(cols.dancers).get()
  const alive = {}
  dancerSnap.docs.forEach(doc => {
    alive[doc.id] = true
  })
  // Faculty intros / travel / carpools / guests use faculty record ids.
  if (cols.faculty) {
    try {
      const facSnap = await db.collection(cols.faculty).get()
      facSnap.docs.forEach(doc => {
        alive[doc.id] = true
      })
    } catch (e) {
      console.warn('orphan sweep faculty roster failed', e)
    }
  }

  const isAlivePerson = (id) => !!(id && alive[id])

  if (cols.dancerIntros) {
    const snap = await db.collection(cols.dancerIntros).get()
    await Promise.all(snap.docs.map(doc => {
      const data = doc.data() || {}
      if (isFacultyLike(data)) {
        return null
      }
      return isAlivePerson(doc.id) ? null : doc.ref.delete()
    }))
  }

  if (cols.travelDetails) {
    const snap = await db.collection(cols.travelDetails).get()
    await Promise.all(snap.docs.map(doc => {
      const data = doc.data() || {}
      if (isFacultyLike(data)) {
        return null
      }
      const pid = data.personId
      if (pid && isAlivePerson(pid)) {
        return null
      }
      if (pid && !isAlivePerson(pid)) {
        return doc.ref.delete()
      }
      return null
    }))
  }

  // Only this admin year's pieces. Never delete the doc; only drop ids that
  // are no longer a dancer or faculty record in this year.
  if (cols.pieces) {
    const snap = await db.collection(cols.pieces).get()
    await Promise.all(snap.docs.map(doc => {
      const data = doc.data() || {}
      const raw = Array.isArray(data.dancers) ? data.dancers : []
      const nextDancers = raw.filter(d => {
        const did = typeof d === 'string' ? d : (d && d.id)
        return isAlivePerson(did)
      })
      const submitted = data.submittedByDancerId
      const submittedAlive = isAlivePerson(submitted)
      const entrants = Array.isArray(data.competitionEntrants) ? data.competitionEntrants : []
      const nextEntrants = entrants.filter(isAlivePerson)
      const patch = {}
      if (raw.length !== nextDancers.length) {
        patch.dancers = nextDancers.map(d => (typeof d === 'string' ? d : d.id)).filter(Boolean)
      }
      if (entrants.length !== nextEntrants.length) {
        patch.competitionEntrants = nextEntrants
      }
      if (submitted && !submittedAlive) {
        patch.submittedByDancerId = null
      }
      if (Object.keys(patch).length) {
        return doc.ref.update(patch)
      }
      return null
    }))
  }

  if (cols.carpools) {
    const snap = await db.collection(cols.carpools).get()
    await Promise.all(snap.docs.map(doc => {
      const riders = (doc.data() || {}).dancers || []
      const next = riders.filter(r => keepCarpoolRider(r, isAlivePerson))
      if (next.length === riders.length) {
        return null
      }
      return doc.ref.update({ dancers: next })
    }))
  }

  if (cols.guestRegistrations) {
    const snap = await db.collection(cols.guestRegistrations).get()
    await Promise.all(snap.docs.map(doc => {
      return isOrphanGuestRegistration(doc.data() || {}, isAlivePerson)
        ? doc.ref.delete()
        : null
    }))
  }
}

function isPermissionError (err) {
  const code = err && (err.code || err.name || '')
  const msg = String((err && err.message) || err || '').toLowerCase()
  return code === 'permission-denied' ||
    code === 'FirebaseError' && msg.includes('permission') ||
    msg.includes('missing or insufficient permissions') ||
    msg.includes('permission-denied')
}

async function linkUidToEnrollmentAndPerson (enrollmentDoc, uid) {
  if (!enrollmentDoc || !uid) {
    return enrollmentDoc ? { id: enrollmentDoc.id, ...enrollmentDoc.data() } : null
  }
  const data = enrollmentDoc.data() || {}
  const updates = {}
  if (data.uid !== uid) {
    updates.uid = uid
  }
  if (Object.keys(updates).length) {
    await enrollmentDoc.ref.update(updates)
  }
  if (data.personId) {
    await linkUidToPerson(db, data.personId, uid)
  } else {
    // Resolve person by email and link both ways when possible.
    try {
      const person = await resolvePersonForEnrollment(db, { id: enrollmentDoc.id, ...data, uid })
      if (person) {
        await linkUidToPerson(db, person.id, uid)
        await enrollmentDoc.ref.update({ personId: person.id, festivalYear: data.festivalYear || IABF_DANCER_YEAR })
        return { id: enrollmentDoc.id, ...data, uid, personId: person.id, festivalYear: data.festivalYear || IABF_DANCER_YEAR }
      }
    } catch (e) {
      console.warn('link uid to person failed', e)
    }
  }
  return { id: enrollmentDoc.id, ...data, uid }
}

export default new Vuex.Store({
  state: {
    drawer: null,
    layout: initialLayout(),
    loadedVideos: [],
    loadedBalletClasses: [],
    loadedAttendanceForCurrentDancer: [],
    loadedBookmarksForCurrentDancer: [],
    loadedWatchHistoryForCurrentDancer: [],
    loadedDancers: [],
    loadedIABFDancers: [],
    loadedIABFPieces: [],
    loadedIABFCorpsPieces: [],
    currentIABFPerson: null,
    // Enrollment year for the logged-in dancer (2027 if registered, else 2026 wrap-up, …).
    iabfDancerYear: null,
    // Admin ops year (wrap-up 2026 / prep 2027). Persisted for the year switcher.
    adminFestivalYear: readStoredAdminYear(),
    currentIABFCorpsPiece: null,
    loadedIABFCorpsPiecesForDancer: [],
    costumeInventory: [],
    festivalInventory: [],
    costumeCloset: [],
    closetLoans: [],
    dancerLedger: [],
    guestRegistrations: [],
    allGuestRegistrations: [],
    iabfCarpoolSlots: [],
    setupPlan: { rooms: [], updatedAt: null },
    accountingProductLines: [],
    accountingTransactions: [],
    canceledDancers: [],
    loadedIABFPiecesforDancer: [],
    corpsRecordings: {},
    corpsNotes: {},
    corpsStartPositions: {},
    loadedDancersforClass: {},
    loadedRecommendations: [],
    loadedTravelDetails: {},
    loadedAllTravelDetails: [],
    upcomingBalletClasses: [],
    user: null,
    currentBalletClass: null,
    currentBalletClassRecommendations: null,
    currentIABFDancer: null,
    masqueradeDancerId: storedMasquerade.id,
    masqueradeDancer: storedMasquerade.dancer,
    currentIABFPiece: null,
    currentTravelDetails: null,
    currentDancer: null,
    loading: false,
    error: null,
    iabfCommunityLinks: { whatsappInviteUrl: '' }
  },
  mutations: {
    setLoadedAttendanceForCurrentDancer (state, payload) {
      state.loadedAttendanceForCurrentDancer = payload
    },
    setBookmarkedVideosForCurrentDancer (state, payload) {
      state.loadedBookmarksForCurrentDancer = payload
    },
    setVideoWatchHistoryForCurrentDancer (state, payload) {
      state.loadedWatchHistoryForCurrentDancer = payload
    },
    setLoadedVideos (state, payload) {
      state.loadedVideos = payload
    },
    setLoadedBalletClasses (state, payload) {
      state.loadedBalletClasses = payload
    },
    setLoadedDancers (state, payload) {
      state.loadedDancers = payload
    },
    setLoadedIABFDancers (state, payload) {
      state.loadedIABFDancers = payload
    },
    setIabfCommunityLinks (state, payload) {
      const url = normalizeWhatsappInviteUrl(payload && payload.whatsappInviteUrl)
      state.iabfCommunityLinks = { whatsappInviteUrl: url }
    },
    setCostumeInventory (state, payload) {
      state.costumeInventory = payload
    },
    setFestivalInventory (state, payload) {
      state.festivalInventory = payload
    },
    setCostumeCloset (state, payload) {
      state.costumeCloset = payload
    },
    setClosetLoans (state, payload) {
      state.closetLoans = payload
    },
    setDancerLedger (state, payload) {
      state.dancerLedger = payload
    },
    setGuestRegistrations (state, payload) {
      state.guestRegistrations = payload
    },
    setAllGuestRegistrations (state, payload) {
      state.allGuestRegistrations = payload
    },
    setSetupPlan (state, payload) {
      state.setupPlan = payload || { rooms: [], updatedAt: null }
    },
    setAccountingProductLines (state, payload) {
      state.accountingProductLines = payload || []
    },
    setAccountingTransactions (state, payload) {
      state.accountingTransactions = payload || []
    },
    setCanceledDancers (state, payload) {
      state.canceledDancers = payload || []
    },
    setIABFCarpoolSlots (state, payload) {
      state.iabfCarpoolSlots = payload
    },
    setIabfDancerYear (state, year) {
      const y = parseInt(year, 10)
      state.iabfDancerYear = Number.isFinite(y) ? y : null
    },
    setLoadedIABFPieces (state, payload) {
      state.loadedIABFPieces = payload
    },
    patchLoadedIABFPiece (state, { id, fields }) {
      const piece = state.loadedIABFPieces.find(p => p.id === id)
      if (!piece) return
      Object.assign(piece, fields || {})
    },
    setLoadedIABFPiecesforDancer (state, payload) {
      state.loadedIABFPiecesforDancer = payload
    },
    setCorpsRecordings (state, payload) {
      state.corpsRecordings = payload || {}
    },
    setCorpsNotes (state, payload) {
      state.corpsNotes = payload || {}
    },
    setCorpsStartPositions (state, payload) {
      state.corpsStartPositions = payload || {}
    },
    setLoadedIABFCorpsPieces (state, payload) {
      state.loadedIABFCorpsPieces = payload
    },
    setCurrentIABFCorpsPiece (state, payload) {
      state.currentIABFCorpsPiece = payload
    },
    setLoadedIABFCorpsPiecesForDancer (state, payload) {
      state.loadedIABFCorpsPiecesForDancer = payload
    },
    setLoadedAllTravelDetails (state, payload) {
      state.loadedAllTravelDetails = payload
    },
    setLoadedTravelDetails (state, payload) {
      state.loadedTravelDetails = payload
    },
    setLoadedRecommendations (state, payload) {
      state.loadedRecommendations = payload
    },
    setLoadedDancersForClass (state, payload) {
      if (!(payload.balletClassId in state.loadedDancersforClass)) {
        Vue.set(state.loadedDancersforClass, payload.balletClassId, [])
      }
      var dancerIds = state.loadedDancersforClass[payload.balletClassId].map(item => {
        return item.dancerId
      })
      if (!dancerIds.includes(payload.dancerId)) {
        state.loadedDancersforClass[payload.balletClassId].push(payload)
      }
    },
    setCurrentDancer (state, payload) {
      state.currentDancer = payload
    },
    setCurrentIABFDancer (state, payload) {
      state.currentIABFDancer = payload
    },
    /**
     * Patch the dancer the UI is actually showing — including admin masquerade.
     * setCurrentIABFDancer is ignored while masquerading (getter returns the snapshot).
     */
    patchEffectiveIABFDancer (state, patch) {
      if (!patch || typeof patch !== 'object') {
        return
      }
      const id = patch.id
      if (state.currentIABFDancer && (!id || state.currentIABFDancer.id === id)) {
        state.currentIABFDancer = { ...state.currentIABFDancer, ...patch }
      }
      // Require an id match while masquerading so another person's attendance
      // patch cannot leak onto the dancer on screen.
      if (state.masqueradeDancerId && id && state.masqueradeDancerId === id) {
        const base = state.masqueradeDancer || {}
        state.masqueradeDancer = {
          ...base,
          ...patch,
          id: state.masqueradeDancerId
        }
        writeStoredMasquerade(state.masqueradeDancerId, state.masqueradeDancer)
      }
    },
    setCurrentIABFPerson (state, payload) {
      state.currentIABFPerson = payload
    },
    setAdminFestivalYear (state, year) {
      const y = parseInt(year, 10)
      state.adminFestivalYear = IABF_ADMIN_YEARS.includes(y) ? y : IABF_DANCER_YEAR
      try {
        localStorage.setItem(ADMIN_YEAR_STORAGE_KEY, String(state.adminFestivalYear))
      } catch (e) { /* ignore */ }
    },
    setMasqueradeDancer (state, payload) {
      if (!payload) {
        state.masqueradeDancerId = null
        state.masqueradeDancer = null
        writeStoredMasquerade(null, null)
        return
      }
      const fromList = (id) => (state.loadedIABFDancers || []).find(d => d.id === id) || null
      let snap = null
      if (typeof payload === 'string') {
        snap = fromList(payload)
        state.masqueradeDancerId = payload
      } else if (payload.id) {
        snap = { ...payload }
        state.masqueradeDancerId = payload.id
      } else {
        state.masqueradeDancerId = null
        state.masqueradeDancer = null
        writeStoredMasquerade(null, null)
        return
      }
      if (snap) {
        const year = Number(snap.festivalYear) || state.adminFestivalYear || IABF_DANCER_YEAR
        state.masqueradeDancer = { ...snap, festivalYear: year }
        writeStoredMasquerade(state.masqueradeDancerId, state.masqueradeDancer)
      } else {
        state.masqueradeDancer = null
        writeStoredMasquerade(null, null)
      }
    },
    setCurrentIABFDancerLedger (state, payload) {
      state.currentIABFDancer.ledger = payload
    },
    setCurrentTravelDetails (state, payload) {
      state.currentTravelDetails = payload
    },
    setCurrentIABFPiece (state, payload) {
      state.currentIABFPiece = payload
    },
    setCurrentBalletClass (state, payload) {
      state.currentBalletClass = payload
    },
    setCurrentBalletClassRecommendations (state, payload) {
      state.currentBalletClassRecommendations = payload
    },
    createVideo (state, payload) {
      state.loadedVideos.push(payload)
    },
    createDancer (state, payload) {
      state.loadedDancers.push(payload)
    },
    createIABFDancer (state, payload) {
      state.loadedIABFDancers.push(payload)
    },
    createBalletClass (state, payload) {
      state.loadedBalletClasses.push(payload)
    },
    setUser (state, payload) {
      state.user = payload
    },
    setLoading (state, payload) {
      state.loading = payload
    },
    setError (state, payload) {
      state.error = payload
    },
    clearError (state) {
      state.error = null
    },
    setUpcomingBalletClasses (state, payload) {
      state.upcomingBalletClasses = payload
    },
    setDrawer: set('drawer'),
    toggleDrawer: toggle('drawer'),
    setLayout (state, payload) {
      state.layout = payload
    }
  },
  actions: {
    loadVideos ({ commit }) {
      commit('setLoading', true)
      db.collection('videos').get().then((querySnapshot) => {
        const videos = []
        querySnapshot.forEach((doc) => {
          videos.push({
            id: doc.id,
            title: doc.data().title,
            description: doc.data().description,
            imageUrl: doc.data().imageUrl,
            videoUrl: doc.data().videoUrl,
            date: doc.data().date
          })
        })
        commit('setLoadedVideos', videos)
        commit('setLoading', false)
      })
    },
    loadBalletClasses ({ commit }) {
      commit('setLoading', true)
      db.collection('balletClasses').get().then((querySnapshot) => {
        const balletClasses = []
        querySnapshot.forEach((doc) => {
          balletClasses.push({
            id: doc.id,
            title: doc.data().title,
            balletClassType: doc.data().balletClassType,
            duration: doc.data().duration,
            date: doc.data().date
          })
        })
        commit('setLoadedBalletClasses', balletClasses)
        commit('setLoading', false)
      })
    },
    loadDancers ({ commit }) {
      commit('setLoading', true)
      db.collection('dancers').get().then((querySnapshot) => {
        const dancers = []
        querySnapshot.forEach((doc) => {
          dancers.push({
            firstName: doc.data().firstName,
            lastName: doc.data().lastName,
            email: doc.data().email,
            fullName: doc.data().firstName + ' ' + doc.data().lastName,
            id: doc.id
          })
        })
        commit('setLoadedDancers', dancers)
        commit('setLoading', false)
      })
    },
    updatePieceDetails ({ commit }, payload) {
      commit('setLoading', true)
      return new Promise((resolve, reject) => {
        db.collection(IABF.pieces).doc(payload.id).update({
          programDancer: payload.programDancer,
          programTeacher: payload.programTeacher,
          programTitle: payload.programTitle
        })
        .then(() => {
          commit('setLoading', false)
          resolve()
        }
        ).catch(
          error => {
            reject(error)
          }
        )
      })
    },
    /**
     * Dancer-facing: save "what this solo means to me" on a piece they own.
     */
    async updatePieceMeaning ({ state }, { pieceId, meaningToMe, dancerId }) {
      if (!pieceId || !dancerId) {
        throw new Error('Missing piece or dancer')
      }
      const cols = dancerIabf(state)
      const ref = db.collection(cols.pieces).doc(pieceId)
      const snap = await ref.get()
      if (!snap.exists) {
        throw new Error('Piece not found')
      }
      const data = snap.data() || {}
      if (!(data.dancers || []).includes(dancerId)) {
        throw new Error('You can only edit your own pieces')
      }
      if (data.canceledByDancer) {
        throw new Error('This piece was canceled')
      }
      if (data.approvalStatus === 'pending') {
        throw new Error('You can share your story after the piece is approved')
      }
      const text = String(meaningToMe || '').trim().slice(0, 600)
      await ref.update({
        meaningToMe: text,
        meaningToMeUpdatedAt: new Date()
      })
      return text
    },
    /**
     * Dancer-facing soft-delete: hide from their list / lineup; notify via Zapier.
     * Admin can still see the doc (canceledByDancer flag).
     */
    async cancelOwnPiece ({ state, getters }, { pieceId, dancerId }) {
      if (!pieceId || !dancerId) {
        throw new Error('Missing piece or dancer')
      }
      const cols = dancerIabf(state)
      const ref = db.collection(cols.pieces).doc(pieceId)
      const snap = await ref.get()
      if (!snap.exists) {
        throw new Error('Piece not found')
      }
      const data = snap.data() || {}
      if (!(data.dancers || []).includes(dancerId)) {
        throw new Error('You can only cancel your own pieces')
      }
      await ref.update({
        canceledByDancer: true,
        canceledByDancerAt: new Date(),
        canceledByDancerId: dancerId
      })
      const dancer = getters.currentIABFDancer || {}
      // Clear competition entry if this was their competing piece
      if (dancer.competitionPieceId === pieceId) {
        await db.collection(cols.dancers).doc(dancerId).update({
          competitionPieceId: null,
          competitionRegistered: false
        })
      }
      try {
        await fetch('https://hooks.zapier.com/hooks/catch/2220729/uctgi1w/', {
          method: 'POST',
          body: JSON.stringify({
            changeType: 'Piece canceled by dancer',
            dancerName: `${dancer.firstName || ''} ${dancer.lastName || ''}`.trim(),
            dancerEmail: dancer.email || '',
            dancerId,
            title: data.title || '',
            songArtist: data.songArtist || '',
            pieceId
          })
        })
      } catch (e) {
        console.warn('Piece cancel webhook failed:', e)
      }
    },
    loadIABFPiecesForDancer ({ commit, state }, payload) {
      commit('setLoading', true)
      const cols = dancerIabf(state)
      return new Promise((resolve, reject) => {
        db.collection(cols.pieces).where('dancers', 'array-contains', payload).get().then((querySnapshot) => {
          var pieces = []
          querySnapshot.forEach((doc) => {
            const data = doc.data() || {}
            // Soft-deleted by the dancer — hide from their showcase list
            if (data.canceledByDancer) {
              return
            }
            pieces.push({
              title: data.title,
              songArtist: data.songArtist,
              programDancer: data.programDancer,
              programTeacher: data.programTeacher,
              programTitle: data.programTitle,
              musicReceived: data.musicReceived,
              musicUrl: data.musicUrl || '',
              musicFileName: data.musicFileName || '',
              lengthInSeconds: data.lengthInSeconds,
              pieceType: data.pieceType,
              groupSize: data.groupSize,
              dancers: data.dancers,
              competition: data.competition,
              performanceRecordingUrl: data.performanceRecordingUrl || '',
              meaningToMe: data.meaningToMe || '',
              // missing/legacy = approved (admin-created before this field existed)
              approvalStatus: data.approvalStatus || 'approved',
              submissionNotes: data.submissionNotes || '',
              submittedAt: data.submittedAt || null,
              id: doc.id
            })
          })
          commit('setLoadedIABFPiecesforDancer', pieces)
          commit('setLoading', false)
          resolve()
        }
        ).catch(
          error => {
            reject(error)
          }
        )
      })
    },
    // ── Festival deadlines + registration access (iabf-config-{year}) ───
    /**
     * Load deadline overrides + registration switches for a festival year.
     * Applies overrides into is*Closed helpers for the current session.
     */
    async loadIabfDeadlinesConfig ({ state }, year) {
      const y = Number(year) || state.adminFestivalYear || IABF_REGISTRATION_YEAR || IABF_DANCER_YEAR
      const cols = iabfForYear(y)
      const configCol = cols.config
      let deadlines = {}
      let registration = null
      if (configCol) {
        try {
          const [dSnap, rSnap] = await Promise.all([
            db.collection(configCol).doc('deadlines').get(),
            db.collection(configCol).doc('registration').get()
          ])
          if (dSnap.exists) {
            deadlines = dSnap.data() || {}
          }
          if (rSnap.exists) {
            registration = rSnap.data() || {}
          }
        } catch (e) {
          console.warn('loadIabfDeadlinesConfig failed', e)
        }
      }
      // Strip meta fields before applying as ISO overrides
      const isoOverrides = { ...deadlines }
      delete isoOverrides.updatedAt
      delete isoOverrides.updatedBy
      setDeadlineOverrides(isoOverrides)
      setRegistrationDeadlineOverrides(isoOverrides)
      const access = resolveRegistrationAccess(registration)
      setRegistrationAccessRuntime(access)
      return { year: y, deadlines: isoOverrides, registration: access, rawRegistration: registration }
    },
    async saveIabfDeadlineOverrides ({ state }, { year, overrides }) {
      const y = Number(year) || state.adminFestivalYear || IABF_REGISTRATION_YEAR
      const cols = iabfForYear(y)
      if (!cols.config) {
        throw new Error('No config collection for this year.')
      }
      const payload = {
        ...(overrides || {}),
        updatedAt: Date.now()
      }
      await db.collection(cols.config).doc('deadlines').set(payload, { merge: true })
      setDeadlineOverrides(payload)
      setRegistrationDeadlineOverrides(payload)
      return payload
    },
    async saveIabfRegistrationAccess ({ state }, { year, publicRegistrationOpen, earlyAccessOpen, publicOpensAt, earlyAccessOpensAt, notes }) {
      const y = Number(year) || state.adminFestivalYear || IABF_REGISTRATION_YEAR
      const cols = iabfForYear(y)
      if (!cols.config) {
        throw new Error('No config collection for this year.')
      }
      const payload = {
        publicRegistrationOpen: !!publicRegistrationOpen,
        earlyAccessOpen: !!earlyAccessOpen,
        publicOpensAt: publicOpensAt || null,
        earlyAccessOpensAt: earlyAccessOpensAt || null,
        notes: String(notes || '').trim(),
        updatedAt: Date.now()
      }
      await db.collection(cols.config).doc('registration').set(payload, { merge: true })
      const access = resolveRegistrationAccess(payload)
      setRegistrationAccessRuntime(access)
      return access
    },
    async loadIabfCommunityLinks ({ commit, state }, year) {
      const y = Number(year) || state.iabfDancerYear || state.adminFestivalYear || IABF_DANCER_YEAR
      const cols = iabfForYear(y)
      let whatsappInviteUrl = ''
      try {
        if (cols.config) {
          const snap = await db.collection(cols.config).doc('community').get()
          if (snap.exists) {
            whatsappInviteUrl = normalizeWhatsappInviteUrl((snap.data() || {}).whatsappInviteUrl)
          }
        }
      } catch (e) {
        console.warn('loadIabfCommunityLinks failed', e)
      }
      const links = { whatsappInviteUrl }
      commit('setIabfCommunityLinks', links)
      return links
    },
    async saveIabfCommunityLinks ({ commit, state }, { year, whatsappInviteUrl }) {
      const y = Number(year) || state.adminFestivalYear || IABF_DANCER_YEAR
      const cols = iabfForYear(y)
      if (!cols.config) {
        throw new Error('No config collection for this year.')
      }
      const url = normalizeWhatsappInviteUrl(whatsappInviteUrl)
      await db.collection(cols.config).doc('community').set({
        whatsappInviteUrl: url,
        updatedAt: Date.now()
      }, { merge: true })
      const links = { whatsappInviteUrl: url }
      commit('setIabfCommunityLinks', links)
      return links
    },
    /**
     * Public registration gate for /register (uses registration year config).
     * Missing doc → closed until scheduled early access (never public).
     * Saved earlyAccessOpen: false closes it even after the scheduled open.
     */
    async loadRegistrationAccessGate () {
      const y = IABF_REGISTRATION_YEAR
      const cols = iabfForYear(y)
      let registration = null
      try {
        if (cols.config) {
          const snap = await db.collection(cols.config).doc('registration').get()
          if (snap.exists) {
            registration = snap.data()
          }
        }
      } catch (e) {
        console.warn('loadRegistrationAccessGate failed', e)
      }
      const access = resolveRegistrationAccess(registration)
      setRegistrationAccessRuntime(access)
      return access
    },
    /**
     * Room inventory + overall ticket cap for registration year.
     * @returns {{ limits: Object, totalTickets: number|null }}
     *   limits = physical onsite rooms only (Standard Dorm / ADA Dorm / Cottage)
     *   totalTickets = every registration counts (null = unlimited)
     */
    async loadIabfRoomLimits ({ state }, year) {
      const y = Number(year) || state.adminFestivalYear || IABF_REGISTRATION_YEAR
      const cols = iabfForYear(y)
      if (!cols.config) return { limits: {}, totalTickets: null }
      try {
        const snap = await db.collection(cols.config).doc('room-limits').get()
        if (!snap.exists) return { limits: {}, totalTickets: null }
        const d = snap.data() || {}
        const limits = (d.limits && typeof d.limits === 'object') ? d.limits : {}
        const cleaned = {}
        ONSITE_ROOM_LIMIT_KEYS.forEach(k => {
          if (limits[k] !== undefined) cleaned[k] = limits[k]
          else if (d[k] !== undefined) cleaned[k] = d[k]
        })
        let totalTickets = null
        const rawTotal = d.totalTickets !== undefined ? d.totalTickets : d.totalTicketLimit
        if (rawTotal !== null && rawTotal !== undefined && rawTotal !== '') {
          const n = Number(rawTotal)
          totalTickets = Number.isFinite(n) && n >= 0 ? Math.floor(n) : null
        }
        return { limits: cleaned, totalTickets }
      } catch (e) {
        console.warn('loadIabfRoomLimits failed', e)
        return { limits: {}, totalTickets: null }
      }
    },
    async saveIabfRoomLimits ({ state }, { year, limits, totalTickets }) {
      const y = Number(year) || state.adminFestivalYear || IABF_REGISTRATION_YEAR
      const cols = iabfForYear(y)
      if (!cols.config) {
        throw new Error('No config collection for this year.')
      }
      let total = null
      if (totalTickets !== null && totalTickets !== undefined && totalTickets !== '') {
        const n = Number(totalTickets)
        total = Number.isFinite(n) && n >= 0 ? Math.floor(n) : null
      }
      // Only persist physical onsite room caps + overall ticket headcount
      const roomOnly = {}
      ONSITE_ROOM_LIMIT_KEYS.forEach(k => {
        if (limits && limits[k] !== undefined) {
          roomOnly[k] = limits[k]
        }
      })
      await db.collection(cols.config).doc('room-limits').set({
        limits: roomOnly,
        totalTickets: total,
        updatedAt: Date.now()
      }, { merge: true })
      // Reconcile + publish summary so register page picks up new limits live
      try {
        await getRegistrationRoomAvailability({ reconcile: true })
      } catch (e) {
        console.warn('Could not refresh room-availability snapshot', e)
      }
      return { limits: roomOnly, totalTickets: total }
    },

    /**
     * Email-linked onsite room set-asides for registration year.
     * Path: iabf-config-{year}/room-email-reservations/entries/{id}
     * (same config tree as room-limits — admin write already allowed)
     */
    async loadIabfRoomEmailReservations ({ state }, year) {
      const y = Number(year) || state.adminFestivalYear || IABF_REGISTRATION_YEAR
      try {
        const colRef = db.collection(`iabf-config-${y}`).doc('room-email-reservations').collection('entries')
        const result = await colRef.get()
        return result.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const as = a.status === 'reserved' ? 0 : 1
            const bs = b.status === 'reserved' ? 0 : 1
            if (as !== bs) return as - bs
            return String(a.email || '').localeCompare(String(b.email || ''))
          })
      } catch (e) {
        console.warn('loadIabfRoomEmailReservations failed', e)
        throw e
      }
    },
    async createIabfRoomEmailReservation ({ state }, { year, email, room, notes }) {
      const y = Number(year) || state.adminFestivalYear || IABF_REGISTRATION_YEAR
      const e = normalizeEmail(email)
      if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
        throw new Error('A valid email is required.')
      }
      const roomKey = String(room || '').trim()
      if (ONSITE_ROOM_LIMIT_KEYS.indexOf(roomKey) === -1) {
        throw new Error('Choose Standard Dorm, Larger Dorm, or Cottage.')
      }
      const docId = e.replace(/@/g, '_at_').replace(/[^a-z0-9._at-]+/gi, '_')
      const colRef = db.collection(`iabf-config-${y}`).doc('room-email-reservations').collection('entries')
      // Ensure parent doc exists (helps some clients / console browsing)
      await db.collection(`iabf-config-${y}`).doc('room-email-reservations').set({
        kind: 'room-email-reservations',
        updatedAt: Date.now()
      }, { merge: true })
      const ref = colRef.doc(docId)
      const existing = await ref.get()
      if (existing.exists && (existing.data() || {}).status === 'reserved') {
        throw new Error('That email already has an active room reservation.')
      }
      if (existing.exists && (existing.data() || {}).status === 'claimed') {
        throw new Error('That email already claimed a reserved room. Clear the claimed record first if re-reserving.')
      }
      const now = Date.now()
      await ref.set({
        email: e,
        room: roomKey,
        status: 'reserved',
        notes: String(notes || '').trim(),
        totalCounted: true,
        roomInventoryExempt: true,
        createdAt: now,
        updatedAt: now
      }, { merge: true })

      // Recount total tickets server-side (includes unclaimed email set-asides).
      // Do not touch inventory counters from the client — reserved doc ids like
      // __total_tickets__ are rejected by the Firestore client API (400 batchGet).
      try {
        await getRegistrationRoomAvailability({ reconcile: true })
      } catch (err) {
        console.warn('Could not refresh room-availability after email reservation', err)
      }
      return { id: docId, email: e, room: roomKey, status: 'reserved' }
    },
    async releaseIabfRoomEmailReservation ({ state }, { year, id }) {
      const y = Number(year) || state.adminFestivalYear || IABF_REGISTRATION_YEAR
      const ref = db.collection(`iabf-config-${y}`).doc('room-email-reservations')
        .collection('entries').doc(id)
      const snap = await ref.get()
      if (!snap.exists) {
        throw new Error('Reservation not found.')
      }
      await ref.set({
        status: 'released',
        releasedAt: Date.now(),
        updatedAt: Date.now()
      }, { merge: true })

      try {
        await getRegistrationRoomAvailability({ reconcile: true })
      } catch (err) {
        console.warn('Could not refresh room-availability after releasing email reservation', err)
      }
      return { id, status: 'released' }
    },
    async joinIabfRegistrationWaitlist (_, { email, firstName }) {
      const e = normalizeEmail(email)
      if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
        throw new Error('Please enter a valid email address.')
      }
      const y = IABF_REGISTRATION_YEAR
      const cols = iabfForYear(y)
      const docId = e.replace(/@/g, '_at_').replace(/[^a-z0-9._at-]+/gi, '_')
      const ref = db.collection(cols.registrationWaitlist).doc(docId)
      const payload = {
        email: e,
        firstName: String(firstName || '').trim(),
        festivalYear: y,
        status: 'waiting',
        createdAt: Date.now()
      }
      try {
        await ref.set(payload)
        return { id: docId, already: false }
      } catch (err) {
        const code = (err && err.code) || ''
        if (code === 'permission-denied' || code === 'already-exists') {
          return { id: docId, already: true }
        }
        throw err
      }
    },
    async loadIabfRegistrationWaitlist ({ state }, year) {
      const y = Number(year) || state.adminFestivalYear || IABF_REGISTRATION_YEAR
      const cols = iabfForYear(y)
      if (!cols.registrationWaitlist) {
        return []
      }
      const snap = await db.collection(cols.registrationWaitlist).orderBy('createdAt', 'asc').get()
      const now = Date.now()
      const rows = []
      for (let i = 0; i < snap.docs.length; i++) {
        const doc = snap.docs[i]
        const data = doc.data() || {}
        let status = data.status || 'waiting'
        if (status === 'offered' && Number(data.offerExpiresAt) && now > Number(data.offerExpiresAt)) {
          status = 'expired'
          doc.ref.set({ status: 'expired', updatedAt: now }, { merge: true }).catch(() => {})
        }
        rows.push({
          id: doc.id,
          position: i + 1,
          ...data,
          status
        })
      }
      return rows
    },
    async offerIabfRegistrationWaitlist ({ state }, { year, id }) {
      const y = Number(year) || state.adminFestivalYear || IABF_REGISTRATION_YEAR
      const cols = iabfForYear(y)
      const ref = db.collection(cols.registrationWaitlist).doc(id)
      const snap = await ref.get()
      if (!snap.exists) {
        throw new Error('Waitlist entry not found.')
      }
      const prior = snap.data() || {}
      const bytes = new Uint8Array(24)
      crypto.getRandomValues(bytes)
      const token = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
      const now = Date.now()
      const expiresAt = now + (7 * 24 * 60 * 60 * 1000)
      if (prior.offerToken) {
        await db.collection(`iabf-registration-waitlist-offers-${y}`).doc(String(prior.offerToken)).delete().catch(() => {})
      }
      await ref.set({
        status: 'offered',
        offeredAt: now,
        offerExpiresAt: expiresAt,
        offerToken: token,
        updatedAt: now
      }, { merge: true })
      await db.collection(`iabf-registration-waitlist-offers-${y}`).doc(token).set({
        waitlistId: id,
        email: prior.email || '',
        firstName: prior.firstName || '',
        festivalYear: y,
        expiresAt,
        createdAt: now
      })
      return {
        id,
        token,
        expiresAt,
        offerUrl: 'https://dashboard.iabf.dance/register?offer=' + token
      }
    },
    async declineIabfRegistrationWaitlist ({ state }, { year, id }) {
      const y = Number(year) || state.adminFestivalYear || IABF_REGISTRATION_YEAR
      const cols = iabfForYear(y)
      const ref = db.collection(cols.registrationWaitlist).doc(id)
      const snap = await ref.get()
      if (!snap.exists) {
        throw new Error('Waitlist entry not found.')
      }
      const prior = snap.data() || {}
      const now = Date.now()
      if (prior.offerToken) {
        await db.collection(`iabf-registration-waitlist-offers-${y}`).doc(String(prior.offerToken)).delete().catch(() => {})
      }
      await ref.set({
        status: 'declined',
        declinedAt: now,
        updatedAt: now
      }, { merge: true })
      return { id, status: 'declined' }
    },
    async lookupIabfWaitlistOffer (_, tokenRaw) {
      const token = String(tokenRaw || '').trim()
      if (!token || token.length < 16) {
        return null
      }
      const y = IABF_REGISTRATION_YEAR
      const snap = await db.collection(`iabf-registration-waitlist-offers-${y}`).doc(token).get()
      if (!snap.exists) {
        return null
      }
      const data = snap.data() || {}
      if (Number(data.expiresAt) && Date.now() > Number(data.expiresAt)) {
        return { expired: true, email: data.email || '', waitlistId: data.waitlistId || '', token }
      }
      return {
        expired: false,
        email: String(data.email || '').trim().toLowerCase(),
        waitlistId: data.waitlistId || '',
        firstName: data.firstName || '',
        expiresAt: Number(data.expiresAt) || 0,
        token
      }
    },
    async markIabfWaitlistRegistered (_, { waitlistId, offerToken, enrollmentId }) {
      if (!waitlistId || !offerToken) {
        return
      }
      const y = IABF_REGISTRATION_YEAR
      const cols = iabfForYear(y)
      await db.collection(cols.registrationWaitlist).doc(waitlistId).set({
        status: 'registered',
        offerToken,
        enrollmentId: enrollmentId || null,
        registeredAt: Date.now(),
        updatedAt: Date.now()
      }, { merge: true })
    },

    // ── Introduce yourself / community waves ─────────────────────────────
    async loadDancerIntros ({ state }) {
      // Live festival board — not the admin year switcher / wrap-up year.
      const cols = iabfForYear(IABF_DANCER_YEAR)
      const snap = await db.collection(cols.dancerIntros).get()
      const introTime = (i) => {
        const t = i && i.updatedAt
        if (!t) {
          return 0
        }
        if (typeof t.toMillis === 'function') {
          return t.toMillis()
        }
        if (typeof t.toDate === 'function') {
          return t.toDate().getTime()
        }
        if (typeof t.seconds === 'number') {
          return t.seconds * 1000
        }
        const n = new Date(t).getTime()
        return Number.isFinite(n) ? n : 0
      }
      return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(i => i.published !== false)
        .sort((a, b) => {
          const af = (a.isFaculty || a.personType === 'faculty') ? 1 : 0
          const bf = (b.isFaculty || b.personType === 'faculty') ? 1 : 0
          if (af !== bf) {
            return bf - af
          }
          return introTime(b) - introTime(a)
        })
    },
    async loadMyDancerIntro ({ state }, { dancerId }) {
      if (!dancerId) return null
      const cols = iabfForYear(IABF_DANCER_YEAR)
      const snap = await db.collection(cols.dancerIntros).doc(dancerId).get()
      if (!snap.exists) return null
      return { id: snap.id, ...snap.data() }
    },
    async saveDancerIntro ({ state, getters }, payload) {
      const dancer = getters.currentIABFDancer || {}
      const user = getters.user || {}
      const dancerId = payload.dancerId || dancer.id
      if (!dancerId) {
        throw new Error('Missing dancer')
      }
      const authUid = user.id || user.uid || null
      if (!authUid) {
        throw new Error('You must be signed in')
      }
      // Intros rules require dancerUid == signed-in auth uid (unless admin
      // write is deployed). Always use the logged-in account so masquerade saves.
      const uid = authUid
      const cols = iabfForYear(
        Number((getters.currentIABFDancer && getters.currentIABFDancer.festivalYear) ||
          state.iabfDancerYear ||
          IABF_DANCER_YEAR)
      )
      const ref = db.collection(cols.dancerIntros).doc(dancerId)
      const existing = await ref.get()
      const prev = existing.exists ? (existing.data() || {}) : {}
      const comingFrom = String(payload.comingFrom || '').trim().slice(0, 80)
      const yearsDancing = String(payload.yearsDancing || '').trim().slice(0, 80)
      const note = String(
        payload.note != null ? payload.note : (prev.note || '')
      ).trim().slice(0, 160)
      // Multi-select feelings (legacy single string still accepted from older clients)
      let feeling = []
      if (Array.isArray(payload.feeling)) {
        feeling = payload.feeling.map(s => String(s || '').trim()).filter(Boolean).slice(0, 12)
      } else if (payload.feeling) {
        feeling = [String(payload.feeling).trim()].filter(Boolean)
      }
      const stayKindRaw = String(payload.stayKind != null ? payload.stayKind : (prev.stayKind || '')).toLowerCase()
      const stayKind = (stayKindRaw === 'onsite' || stayKindRaw === 'offsite') ? stayKindRaw : ''
      const hotel = String(payload.hotel != null ? payload.hotel : (prev.hotel || '')).trim().slice(0, 80)
      // Opt-in visibility (default hide)
      const showStay = payload.showStay != null ? !!payload.showStay : !!prev.showStay
      let yearsAtIabf = Number(payload.yearsAtIabf != null ? payload.yearsAtIabf : prev.yearsAtIabf)
      if (!Number.isFinite(yearsAtIabf) || yearsAtIabf < 0) {
        yearsAtIabf = 0
      }
      yearsAtIabf = Math.min(40, Math.round(yearsAtIabf))
      const showYearsAtIabf = payload.showYearsAtIabf != null
        ? !!payload.showYearsAtIabf
        : !!prev.showYearsAtIabf
      const headshotUrl = payload.headshotUrl != null
        ? String(payload.headshotUrl || '')
        : (prev.headshotUrl || '')
      const firstName = dancer.firstName || prev.firstName || ''
      const lastName = dancer.lastName || prev.lastName || ''
      // nameDisplayPreference: first | first_last_initial | first_last
      const namePrefRaw = payload.nameDisplayPreference != null
        ? payload.nameDisplayPreference
        : (prev.nameDisplayPreference || 'first_last')
      const nameDisplayPreference =
        namePrefRaw === 'first' || namePrefRaw === 'first_last_initial' || namePrefRaw === 'first_last'
          ? namePrefRaw
          : 'first_last'
      let displayName = ''
      if (nameDisplayPreference === 'first') {
        displayName = firstName || lastName || ''
      } else if (nameDisplayPreference === 'first_last_initial') {
        displayName = firstName && lastName
          ? `${firstName} ${lastName.charAt(0).toUpperCase()}.`
          : (firstName || lastName || '')
      } else {
        displayName = [firstName, lastName].filter(Boolean).join(' ')
      }
      if (payload.displayName && String(payload.displayName).trim()) {
        displayName = String(payload.displayName).trim().slice(0, 80)
      }
      if (!displayName) {
        displayName = prev.displayName || 'Dancer'
      }
      // All fields optional — publish whenever they save an intro card
      const published = true
      const body = {
        dancerId,
        dancerUid: uid,
        personId: dancer.personId || prev.personId || null,
        isFaculty: !!(dancer.isFaculty || dancer.personType === 'faculty'),
        personType: (dancer.isFaculty || dancer.personType === 'faculty') ? 'faculty' : 'dancer',
        firstName,
        lastName,
        nameDisplayPreference,
        displayName: String(displayName).slice(0, 80),
        comingFrom,
        yearsDancing,
        note,
        feeling,
        stayKind,
        hotel,
        showStay,
        yearsAtIabf,
        showYearsAtIabf,
        headshotUrl,
        published,
        waveCount: typeof prev.waveCount === 'number' ? prev.waveCount : 0,
        wavedBy: Array.isArray(prev.wavedBy) ? prev.wavedBy : [],
        updatedAt: new Date()
      }
      if (!existing.exists) {
        body.createdAt = new Date()
      }
      await ref.set(body, { merge: true })
      return { id: dancerId, ...prev, ...body }
    },
    async waveAtDancerIntro ({ state, getters }, { introId }) {
      const user = getters.user || {}
      const uid = user.id || user.uid
      if (!uid) {
        throw new Error('Sign in to wave')
      }
      if (!introId) {
        throw new Error('Missing intro')
      }
      const cols = dancerIabf(state)
      const ref = db.collection(cols.dancerIntros).doc(introId)
      await db.runTransaction(async (tx) => {
        const snap = await tx.get(ref)
        if (!snap.exists) {
          throw new Error('Intro not found')
        }
        const data = snap.data() || {}
        const wavedBy = Array.isArray(data.wavedBy) ? data.wavedBy.slice() : []
        if (wavedBy.includes(uid)) {
          return
        }
        // Don't wave at yourself
        if (data.dancerUid === uid || data.dancerId === (getters.currentIABFDancer && getters.currentIABFDancer.id)) {
          throw new Error('That’s you — no need to wave at yourself!')
        }
        wavedBy.push(uid)
        tx.update(ref, {
          wavedBy,
          waveCount: (Number(data.waveCount) || 0) + 1
        })
      })
    },
    createIABFTravelDetails ({ commit, getters, state }, payload) {
      commit('setLoading', true)
      const cols = dancerIabf(state)
      // If an existing travelDetailsId is supplied, update in place so we don't
      // pile up duplicate docs every time the dancer edits + resubmits. Strip
      // travelDetailsId from the written body so the doc doesn't store its own id.
      const { travelDetailsId, ...docBody } = payload
      return new Promise((resolve, reject) => {
        const write = travelDetailsId
          ? db.collection(cols.travelDetails).doc(travelDetailsId).set(docBody, { merge: false }).then(() => ({ id: travelDetailsId }))
          : db.collection(cols.travelDetails).add(docBody)
        write
        .then(function (docRef) {
          console.log('Document written with ID: ', docRef.id)
          yearPersonRef(state, getters, payload.personId).update({
            travelDetailsEntered: true,
            travelDetailsId: docRef.id,
            arrivalType: payload.arrivalType || '',
            departureType: payload.departureType || ''
          }).then(() => {
            if (payload.extraNight1 || payload.extraNight2 || payload.extraNight3 || payload.extraNight4 || payload.extraNight5 || payload.extraNight6 || payload.arrivalSharedCar || payload.departureSharedCar) {
              var notification = {
                personId: payload.personId,
                dancerName: payload.dancerName || '',
                dancerEmail: payload.dancerEmail || '',
                extraNight1: payload.extraNight1,
                extraNight2: payload.extraNight2,
                extraNight3: payload.extraNight3,
                extraNight4: payload.extraNight4,
                extraNight5: payload.extraNight5,
                extraNight6: payload.extraNight6,
                extraNightsTotal: payload.extraNightsTotal || 0,
                extraNightPaymentMethod: payload.extraNightPaymentMethod || '',
                selectedNights: payload.selectedNights || [],
                arrivalSharedCar: payload.arrivalSharedCar,
                departureSharedCar: payload.departureSharedCar,
                changeType: payload.changeType || 'Travel details with extras'
              }
              fetch('https://hooks.zapier.com/hooks/catch/2220729/3cw3znz/', {
                method: 'POST',
                body: JSON.stringify(notification)
              })
              .then(response => response.json())
              .then(data => {})
              .catch((error) => {
                console.error('Error:', error)
              })
            }
            commit('setLoading', false)
            commit('setLoadedTravelDetails', {
              ...payload,
              id: docRef.id
            })
            commit('patchEffectiveIABFDancer', {
              id: payload.personId,
              travelDetailsEntered: true,
              travelDetailsId: docRef.id,
              arrivalType: payload.arrivalType || '',
              departureType: payload.departureType || ''
            })
            resolve()
          }).catch(
            error => {
              reject(error)
            }
          )
        })
        .catch(function (error) {
          console.error('Error adding document: ', error)
          reject(error)
        })
      })
    },
    /**
     * Dancer requests extra nights only (does not require full trip form).
     * Merges onto existing travel-details doc when present; notifies via Zapier.
     */
    async requestExtraNights ({ commit, state, getters }, payload) {
      if (!payload || !payload.personId) {
        throw new Error('Missing dancer for extra nights request.')
      }
      const cols = dancerIabf(state)
      const dancer = getters.currentIABFDancer || {}
      // Preserve status strings: requested | confirmed | paid
      const nightVal = (v) => {
        if (v === true) return 'requested'
        if (!v || v === false) return null
        const s = String(v).toLowerCase().trim()
        if (s === 'requested' || s === 'confirmed' || s === 'paid') return s
        if (s === 'false' || s === 'null' || s === '') return null
        return 'requested'
      }
      // Only write night fields that are set — omit null so merge doesn't clear
      // other nights if a partial payload is sent.
      const nightFields = {}
      for (const key of ['extraNight1', 'extraNight2', 'extraNight3', 'extraNight4', 'extraNight5', 'extraNight6']) {
        const v = nightVal(payload[key])
        if (v) {
          nightFields[key] = v
        }
      }
      const fields = {
        personId: payload.personId,
        personType: payload.personType || 'dancer',
        ...nightFields,
        extraNightsTotal: payload.extraNightsTotal || 0,
        extraNightPaymentMethod: payload.extraNightPaymentMethod || '',
        checkIn: payload.checkIn || '',
        checkOut: payload.checkOut || '',
        extraNightsRequestedAt: Date.now(),
        updatedAt: Date.now()
      }
      let docId = payload.travelDetailsId || dancer.travelDetailsId || null
      if (docId) {
        await db.collection(cols.travelDetails).doc(docId).set(fields, { merge: true })
      } else {
        // Prefer personId lookup so we don't create duplicates
        try {
          const existing = await db.collection(cols.travelDetails)
            .where('personId', '==', payload.personId)
            .limit(1)
            .get()
          if (!existing.empty) {
            docId = existing.docs[0].id
            await db.collection(cols.travelDetails).doc(docId).set(fields, { merge: true })
          }
        } catch (e) {
          console.warn('extra nights personId lookup failed', e)
        }
        if (!docId) {
          const ref = await db.collection(cols.travelDetails).add({
            ...fields,
            createdAt: Date.now()
          })
          docId = ref.id
        }
      }
      // Always link enrollment → travel-details so reload finds the doc
      // (extra-nights-only requests do not set travelDetailsEntered).
      try {
        await yearPersonRef(state, getters, payload.personId).update({
          travelDetailsId: docId
        })
      } catch (e) {
        console.warn('Could not link travelDetailsId on dancer', e)
      }
      // Keep in-memory dancer in sync so reload path finds the doc
      if (getters.currentIABFDancer && getters.currentIABFDancer.id === payload.personId) {
        commit('setCurrentIABFDancer', {
          ...getters.currentIABFDancer,
          travelDetailsId: docId
        })
      }
      // Re-read the full doc so returned/local state includes any nights
      // that were already on file (merge write only sends new fields).
      let saved = { ...fields, id: docId }
      try {
        const snap = await db.collection(cols.travelDetails).doc(docId).get()
        if (snap.exists) {
          saved = { ...snap.data(), id: docId }
        }
      } catch (e) {
        console.warn('extra nights re-read failed', e)
      }
      // Notify admin (existing Zapier catch for extra nights / lodging extras)
      try {
        await fetch('https://hooks.zapier.com/hooks/catch/2220729/3cw3znz/', {
          method: 'POST',
          body: JSON.stringify({
            personId: payload.personId,
            dancerName: payload.dancerName || `${dancer.firstName || ''} ${dancer.lastName || ''}`.trim(),
            dancerEmail: payload.dancerEmail || dancer.email || '',
            room: payload.room || dancer.room || '',
            extraNight1: saved.extraNight1 || null,
            extraNight2: saved.extraNight2 || null,
            extraNight3: saved.extraNight3 || null,
            extraNight4: saved.extraNight4 || null,
            extraNight5: saved.extraNight5 || null,
            extraNight6: saved.extraNight6 || null,
            extraNightsTotal: saved.extraNightsTotal || fields.extraNightsTotal,
            extraNightPaymentMethod: saved.extraNightPaymentMethod || fields.extraNightPaymentMethod,
            selectedNights: payload.selectedNights || [],
            newlyRequestedNights: payload.newlyRequestedNights || [],
            checkIn: saved.checkIn || fields.checkIn,
            checkOut: saved.checkOut || fields.checkOut,
            changeType: 'Extra nights request'
          })
        })
      } catch (e) {
        console.warn('Extra nights webhook failed', e)
      }
      commit('setLoadedTravelDetails', saved)
      return saved
    },
    loadAllTravelDetails ({ commit }) {
      commit('setLoading', true)
      return new Promise((resolve, reject) => {
        db.collection(IABF.travelDetails).get().then((querySnapshot) => {
          var travelDetails = []
          querySnapshot.forEach((doc) => {
            travelDetails.push({
              id: doc.id,
              personId: doc.data().personId,
              arrivalDate: doc.data().arrivalDate,
              arrivalTime: doc.data().arrivalTime,
              arrivalFlight: doc.data().arrivalFlight,
              arrivalAirport: doc.data().arrivalAirport,
              arrivalFlightNumber: doc.data().arrivalFlightNumber,
              arrivalSharedCar: doc.data().arrivalSharedCar,
              departureDate: doc.data().departureDate,
              departureTime: doc.data().departureTime,
              departureFlight: doc.data().departureFlight,
              departureAirport: doc.data().departureAirport,
              departureFlightNumber: doc.data().departureFlightNumber,
              departureSharedCar: doc.data().departureSharedCar,
              extraNight1: doc.data().extraNight1,
              extraNight2: doc.data().extraNight2,
              extraNight3: doc.data().extraNight3,
              extraNight4: doc.data().extraNight4,
              extraNight5: doc.data().extraNight5,
              extraNight6: doc.data().extraNight6
            })
          })
          commit('setLoadedAllTravelDetails', travelDetails)
          commit('setLoading', false)
          resolve()
        }).catch(
          error => {
            reject(error)
          }
        )
      })
    },
    async loadTravelDetailsByPersonId ({ commit, state }, personId) {
      const cols = dancerIabf(state)
      const snap = await db.collection(cols.travelDetails).where('personId', '==', personId).get()
      if (snap.empty) return null
      // When duplicates exist (e.g. multiple "Edit + Save" cycles created new docs),
      // pick the one with the most populated fields.
      const score = data => Object.values(data || {}).filter(v => v !== null && v !== undefined && v !== '' && v !== false).length
      const best = snap.docs.reduce((a, b) => (score(a.data()) >= score(b.data()) ? a : b))
      const payload = { ...best.data(), id: best.id }
      commit('setLoadedTravelDetails', payload)
      return payload
    },
    async markTravelDetailsEntered ({ commit, getters, state }, payload) {
      const dancerId = payload && payload.dancerId
      if (!dancerId) {
        return
      }
      const patch = {
        travelDetailsEntered: true
      }
      if (payload.travelDetailsId) {
        patch.travelDetailsId = payload.travelDetailsId
      }
      if (payload.arrivalType) {
        patch.arrivalType = payload.arrivalType
      }
      if (payload.departureType) {
        patch.departureType = payload.departureType
      }
      await yearPersonRef(state, getters, dancerId).update(patch)
      commit('patchEffectiveIABFDancer', { id: dancerId, ...patch })
    },
    async updateDancerTravelDetailsId ({ state, getters }, { dancerId, travelDetailsId }) {
      if (!dancerId || !travelDetailsId) return
      try {
        await yearPersonRef(state, getters, dancerId).update({ travelDetailsId })
      } catch (e) {
        console.warn('Could not update dancer travelDetailsId:', e)
      }
    },
    loadIABFTravelDetails ({ commit, state }, payload) {
      if (!payload) {
        commit('setLoading', false)
        return Promise.resolve(null)
      }
      commit('setLoading', true)
      const cols = dancerIabf(state)
      return new Promise((resolve, reject) => {
        db.collection(cols.travelDetails).doc(payload).get()
        .then(function (doc) {
          const data = doc.exists ? { ...doc.data(), id: doc.id } : null
          if (data) commit('setLoadedTravelDetails', data)
          commit('setLoading', false)
          resolve(data)
        }
        ).catch(error => {
          reject(error)
        }
        )
      })
    },
    load2024IABFDancer ({ commit }, payload) {
      commit('setLoading', true)
      return new Promise((resolve, reject) => {
        db.collection('iabf-dancers').doc(payload).get()
        .then(function (doc) {
          commit('setCurrentIABFDancer', doc.data())
          commit('setLoading', false)
          resolve()
        }).catch(error => {
          reject(error)
        })
      })
    },
    load2025IABFDancer ({ commit }, payload) {
      commit('setLoading', true)
      return new Promise((resolve, reject) => {
        db.collection('iabf-dancers').doc(payload).get()
        .then(function (doc) {
          commit('setCurrentIABFDancer', doc.data())
          commit('setLoading', false)
          resolve()
        }).catch(error => {
          reject(error)
        })
      })
    },
    /**
     * Re-read the enrollment the UI is showing (masquerade snapshot is stale
     * after hairdryer/coffee/carpool saves). No global loading spinner.
     */
    async refreshEffectiveDancer ({ commit, state }) {
      const id = state.masqueradeDancerId
      if (!id) {
        return null
      }
      const cols = dancerIabf(state)
      const year = Number(cols.year || (state.masqueradeDancer && state.masqueradeDancer.festivalYear) || IABF_DANCER_YEAR)
      const masquerade = state.masqueradeDancer || {}
      const tryFaculty = !!(masquerade.isFaculty || masquerade.personType === 'faculty' || (cols && cols.faculty))
      if (tryFaculty && cols.faculty) {
        const fac = await db.collection(cols.faculty).doc(id).get()
        if (fac.exists) {
          return hydrateEnrollmentWithPerson(commit, facultyAsDancer({ id: fac.id, ...fac.data() }, year), year)
        }
      }
      if (!cols || !cols.dancers) {
        return null
      }
      const snap = await db.collection(cols.dancers).doc(id).get()
      if (!snap.exists) {
        return null
      }
      return hydrateEnrollmentWithPerson(commit, { id: snap.id, ...snap.data() }, year)
    },
    loadIABFDancer ({ commit, state }, payload) {
      commit('setLoading', true)
      // Admin edit pages pass { id, useAdminYear: true } so the year switcher
      // collection is used. Dancer app paths use the enrollment year.
      const opts = payload && typeof payload === 'object' ? payload : { id: payload }
      const id = opts.id || payload
      const cols = opts.useAdminYear ? adminIabf(state) : dancerIabf(state)
      const dancersCol = (cols && cols.dancers) || IABF.dancers
      const year = (cols && cols.year) || state.iabfDancerYear || IABF_DANCER_YEAR
      return new Promise((resolve, reject) => {
        db.collection(dancersCol).doc(id).get()
        .then(async function (doc) {
          if (!doc.exists) {
            commit('setCurrentIABFDancer', {})
            commit('setCurrentIABFPerson', null)
            commit('setLoading', false)
            resolve()
            return
          }
          await hydrateEnrollmentWithPerson(commit, { id: doc.id, ...doc.data() }, year)
          commit('setLoading', false)
          resolve()
        }).catch(error => {
          commit('setLoading', false)
          reject(error)
        })
      })
    },
    load2024Dancers ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        commit('setLoading', true)
        db.collection('iabf-dancers').get().then((querySnapshot) => {
          const dancers = []
          querySnapshot.forEach((doc) => {
            dancers.push({
              id: doc.id,
              firstName: doc.data().firstName,
              lastName: doc.data().lastName,
              email: doc.data().email,
              phone: doc.data().phone,
              last4: doc.data().phone.substring((doc.data().phone.length - 4), doc.data().phone.length),
              registrationDate: doc.data().registrationDate,
              registrationGift: doc.data().registrationGift,
              room: doc.data().room,
              primaryRoomId: doc.data().primaryRoomId,
              initialPayment: doc.data().initialPayment,
              installments: doc.data().installments,
              payment1Due: doc.data().payment1Due,
              payment2Due: doc.data().payment2Due,
              payment3Due: doc.data().payment3Due,
              payment4Due: doc.data().payment4Due,
              payment5Due: doc.data().payment5Due,
              payment1Status: doc.data().payment1Status,
              payment2Status: doc.data().payment2Status,
              payment3Status: doc.data().payment3Status,
              payment4Status: doc.data().payment4Status,
              payment5Status: doc.data().payment5Status,
              diet: doc.data().diet,
              foodsNotConsumed: doc.data().foodsNotConsumed,
              foodAllergies: doc.data().foodAllergies,
              dietDetails: doc.data().dietDetails || [],
              gluten: doc.data().gluten || '',
              coffee: doc.data().coffee,
              coffeeMakerStock: doc.data().coffeeMakerStock || [],
              dietaryNotes: doc.data().dietaryNotes,
              dietaryRestrictionsEntered: doc.data().dietaryRestrictionsEntered,
              emergencyContactName: doc.data().emergencyContactName,
              emergencyContactPhone: doc.data().emergencyContactPhone,
              emergencyContactRelationship: doc.data().emergencyContactRelationship,
              emergencyContactEntered: doc.data().emergencyContactEntered,
              travelDetailsEntered: doc.data().travelDetailsEntered,
              firstRole: doc.data().firstRole,
              secondRole: doc.data().secondRole
            })
          })
          commit('setLoadedIABFDancers', dancers)
          commit('setLoading', false)
          resolve()
        }).catch(
          error => {
            reject(error)
          }
        )
      })
    },
    load2025Dancers ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        commit('setLoading', true)
        db.collection('iabf-dancers-2025').get().then((querySnapshot) => {
          const dancers = []
          querySnapshot.forEach((doc) => {
            dancers.push({
              id: doc.id,
              firstName: doc.data().firstName,
              lastName: doc.data().lastName,
              email: doc.data().email,
              phone: doc.data().phone,
              last4: doc.data().phone.substring((doc.data().phone.length - 4), doc.data().phone.length),
              registrationDate: doc.data().registrationDate,
              registrationGift: doc.data().registrationGift,
              room: doc.data().room,
              primaryRoomId: doc.data().primaryRoomId,
              initialPayment: doc.data().initialPayment,
              installments: doc.data().installments,
              payment1Due: doc.data().payment1Due,
              payment2Due: doc.data().payment2Due,
              payment3Due: doc.data().payment3Due,
              payment4Due: doc.data().payment4Due,
              payment5Due: doc.data().payment5Due,
              payment1Status: doc.data().payment1Status,
              payment2Status: doc.data().payment2Status,
              payment3Status: doc.data().payment3Status,
              payment4Status: doc.data().payment4Status,
              payment5Status: doc.data().payment5Status,
              diet: doc.data().diet,
              foodsNotConsumed: doc.data().foodsNotConsumed,
              foodAllergies: doc.data().foodAllergies,
              dietDetails: doc.data().dietDetails || [],
              gluten: doc.data().gluten || '',
              coffee: doc.data().coffee,
              coffeeMakerStock: doc.data().coffeeMakerStock || [],
              dietaryNotes: doc.data().dietaryNotes,
              dietaryRestrictionsEntered: doc.data().dietaryRestrictionsEntered,
              emergencyContactName: doc.data().emergencyContactName,
              emergencyContactPhone: doc.data().emergencyContactPhone,
              emergencyContactRelationship: doc.data().emergencyContactRelationship,
              emergencyContactEntered: doc.data().emergencyContactEntered,
              travelDetailsEntered: doc.data().travelDetailsEntered,
              firstRole: doc.data().firstRole,
              secondRole: doc.data().secondRole
            })
          })
          commit('setLoadedIABFDancers', dancers)
          commit('setLoading', false)
          resolve()
        }).catch(
          error => {
            reject(error)
          }
        )
      })
    },
    loadIABFDancers ({ commit, state }) {
      return new Promise((resolve, reject) => {
        commit('setLoading', true)
        const year = state.adminFestivalYear || IABF_DANCER_YEAR
        const dancersColName = dancersCollection(year)

        // 2022–2023: no enrollment collection — roster from people.yearsAttended
        if (!dancersColName) {
          db.collection(IABF_PEOPLE_COLLECTION)
            .where('yearsAttended', 'array-contains', year)
            .get()
            .then((querySnapshot) => {
              const dancers = []
              const stubs = []
              querySnapshot.forEach((doc) => {
                const d = { id: doc.id, ...doc.data() }
                if (isMergedPersonRecord(d)) {
                  stubs.push(d)
                  return
                }
                dancers.push({
                  id: doc.id,
                  personId: doc.id,
                  firstName: d.firstName || '',
                  lastName: d.lastName || '',
                  email: d.email || '',
                  phone: d.phone || '',
                  last4: d.last4 || '',
                  yearsAttended: d.yearsAttended || [],
                  festivalYear: year,
                  archiveOnly: true
                })
              })
              attachPersonAttendanceYears(dancers).then((withYears) => {
                commit('setLoadedIABFDancers', withYears)
                commit('setLoading', false)
                resolve()
              }).catch((error) => {
                commit('setLoading', false)
                reject(error)
              })
              stubs.forEach((stub) => {
                absorbMergedPersonStub(db, stub).catch((e) => {
                  console.warn('absorb merged person stub failed', stub.id, e)
                })
              })
            })
            .catch((error) => {
              commit('setLoading', false)
              reject(error)
            })
          return
        }

        const cols = adminIabf(state)
        db.collection(dancersColName).get().then((querySnapshot) => {
          const dancers = []
          querySnapshot.forEach((doc) => {
            const enrollment = doc.data() || {}
            // Unfinished checkout — not on the roster until deposit is paid.
            if (enrollment.registrationStatus === 'pending_payment') {
              return
            }
            var travelDetailsForDancer = {}
            // Travel details only exist for operable years with that collection.
            if (cols.travelDetails && !isArchiveYear(year)) {
              db.collection(cols.travelDetails).where('personId', '==', doc.id).get().then((travelQuerySnapshot) => {
                travelQuerySnapshot.forEach((travelDoc) => {
                  travelDetailsForDancer = {
                    arrivalAirport: travelDoc.data().arrivalAirport,
                    arrivalDate: travelDoc.data().arrivalDate,
                    arrivalFlightNumber: travelDoc.data().arrivalFlightNumber,
                    arrivalTime: travelDoc.data().arrivalTime,
                    arrivalType: travelDoc.data().arrivalType,
                    checkIn: travelDoc.data().checkIn,
                    checkOut: travelDoc.data().checkOut,
                    departureAirport: travelDoc.data().departureAirport,
                    departureDate: travelDoc.data().departureDate,
                    departureFlightNumber: travelDoc.data().departureFlightNumber,
                    departureTime: travelDoc.data().departureTime,
                    departureType: travelDoc.data().departureType
                  }
                })
              })
            }
            dancers.push({
              ...travelDetailsForDancer,
              id: doc.id,
              personId: doc.data().personId || null,
              festivalYear: year,
              registrationStatus: enrollment.registrationStatus || null,
              firstName: doc.data().firstName,
              lastName: doc.data().lastName,
              email: doc.data().email,
              phone: doc.data().phone,
              last4: doc.data().phone ? doc.data().phone.substring((doc.data().phone.length - 4), doc.data().phone.length) : '',
              registrationDate: doc.data().registrationDate,
              registrationGift1: doc.data().registrationGift1,
              registrationGift2: doc.data().registrationGift2,
              registrationGift3: doc.data().registrationGift3,
              registrationGift4: doc.data().registrationGift4,
              earlyBirdGift: doc.data().earlyBirdGift,
              room: doc.data().room,
              roomNumber: doc.data().roomNumber || '',
              roommateName: doc.data().roommateName || '',
              primaryRoommateName: doc.data().primaryRoommateName || '',
              primaryRoomId: doc.data().primaryRoomId,
              initialPayment: doc.data().initialPayment,
              installments: doc.data().installments,
              payment1Due: doc.data().payment1Due,
              payment2Due: doc.data().payment2Due,
              payment3Due: doc.data().payment3Due,
              payment4Due: doc.data().payment4Due,
              payment5Due: doc.data().payment5Due,
              payment6Due: doc.data().payment6Due,
              payment1Status: doc.data().payment1Status,
              payment2Status: doc.data().payment2Status,
              payment3Status: doc.data().payment3Status,
              payment4Status: doc.data().payment4Status,
              payment5Status: doc.data().payment5Status,
              payment6Status: doc.data().payment6Status,
              diet: doc.data().diet,
              foodsNotConsumed: doc.data().foodsNotConsumed,
              foodAllergies: doc.data().foodAllergies,
              dietDetails: doc.data().dietDetails || [],
              gluten: doc.data().gluten || '',
              coffee: doc.data().coffee,
              coffeeMakerStock: doc.data().coffeeMakerStock || [],
              dietaryNotes: doc.data().dietaryNotes,
              dietaryRestrictionsEntered: doc.data().dietaryRestrictionsEntered,
              emergencyContactName: doc.data().emergencyContactName,
              emergencyContactPhone: doc.data().emergencyContactPhone,
              emergencyContactRelationship: doc.data().emergencyContactRelationship,
              emergencyContactEntered: doc.data().emergencyContactEntered,
              travelDetailsEntered: doc.data().travelDetailsEntered,
              travelDetailsId: doc.data().travelDetailsId || null,
              wantsHairdryer: doc.data().wantsHairdryer,
              showcasePrepStatus: doc.data().showcasePrepStatus || null,
              showcasePrepStatusAt: doc.data().showcasePrepStatusAt || null,
              carpoolResponded: doc.data().carpoolResponded || false,
              carpoolArrivalSlotId: doc.data().carpoolArrivalSlotId || null,
              carpoolDepartureSlotId: doc.data().carpoolDepartureSlotId || null,
              firstRole: doc.data().firstRole,
              secondRole: doc.data().secondRole,
              costumeOrderCompleted: doc.data().costumeOrderCompleted || false,
              costumeOrderPaid: doc.data().costumeOrderPaid || false,
              costumeTotalCost: doc.data().costumeTotalCost || 0,
              costumePaymentMethod: doc.data().costumePaymentMethod || '',
              costumeOrderSubmittedAt: doc.data().costumeOrderSubmittedAt || '',
              waltzOfHours: doc.data().waltzOfHours || null,
              aliceInWonderland: doc.data().aliceInWonderland || null,
              leJazzHot: doc.data().leJazzHot || null,
              becoming: doc.data().becoming || null,
              garlandWaltz: doc.data().garlandWaltz || null,
              checkIn: doc.data().checkIn || null,
              sharedMeasurements: doc.data().sharedMeasurements || null,
              assignedWaltzColor: doc.data().assignedWaltzColor || '',
              assignedGarlandColor: doc.data().assignedGarlandColor || '',
              notInCorps: doc.data().notInCorps || false,
              borrowedWaltzTutu: doc.data().borrowedWaltzTutu || '',
              borrowedAliceTutu: doc.data().borrowedAliceTutu || '',
              borrowedGarlandTutu: doc.data().borrowedGarlandTutu || '',
              borrowedWaltzTutuOrdered: doc.data().borrowedWaltzTutuOrdered || false,
              borrowedAliceTutuOrdered: doc.data().borrowedAliceTutuOrdered || false,
              borrowedGarlandTutuOrdered: doc.data().borrowedGarlandTutuOrdered || false,
              stripeLink: doc.data().stripeLink || '',
              uid: doc.data().uid || null,
              addressStreet: enrollment.addressStreet || '',
              addressStreet2: enrollment.addressStreet2 || '',
              addressCity: enrollment.addressCity || '',
              addressState: enrollment.addressState || '',
              addressZip: enrollment.addressZip || '',
              addressCountry: enrollment.addressCountry || '',
              competitionRegistered: doc.data().competitionRegistered || false,
              competitionPieceId: doc.data().competitionPieceId || null,
              competitionSurvey: doc.data().competitionSurvey || null
            })
          })
          attachPersonAttendanceYears(dancers).then((withYears) => {
            commit('setLoadedIABFDancers', withYears)
            commit('setLoading', false)
            resolve()
          }).catch((error) => {
            commit('setLoading', false)
            reject(error)
          })
        }).catch(
          error => {
            reject(error)
          }
        )
      })
    },
    loadIABFPiece ({ commit, state }, payload) {
      commit('setLoading', true)
      const id = typeof payload === 'string' ? payload : (payload && payload.id)
      const colName = piecesCol(state, true)
      return new Promise((resolve, reject) => {
        if (!id) {
          commit('setCurrentIABFPiece', null)
          commit('setLoading', false)
          resolve()
          return
        }
        db.collection(colName).doc(id).get()
        .then(function (doc) {
          commit('setCurrentIABFPiece', doc.exists
            ? { id: doc.id, ...(doc.data() || {}) }
            : null)
          commit('setLoading', false)
          resolve()
        }).catch(error => {
          commit('setLoading', false)
          reject(error)
        })
      })
    },
    async loadIABFCorpsPieces ({ commit }) {
      commit('setLoading', true)
      const snapshot = await db.collection(IABF.corpsPieces).get()
      const pieces = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
      commit('setLoadedIABFCorpsPieces', pieces)
      commit('setLoading', false)
    },
    async loadIABFCorpsPiece ({ commit }, id) {
      commit('setLoading', true)
      const doc = await db.collection(IABF.corpsPieces).doc(id).get()
      commit('setCurrentIABFCorpsPiece', doc.exists ? { ...doc.data(), id: doc.id } : null)
      commit('setLoading', false)
    },
    async createIABFCorpsPiece ({ commit }, data) {
      const docRef = await db.collection(IABF.corpsPieces).add(data)
      return docRef.id
    },
    async updateIABFCorpsPiece ({ commit }, { id, ...data }) {
      await db.collection(IABF.corpsPieces).doc(id).set(data)
    },
    async deleteIABFCorpsPiece ({ commit }, id) {
      await db.collection(IABF.corpsPieces).doc(id).delete()
    },
    async loadIABFCorpsPiecesForDancer ({ commit, state }, dancerId) {
      const snapshot = await db.collection(dancerIabf(state).corpsPieces)
        .where('allDancerIds', 'array-contains', dancerId)
        .get()
      const pieces = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
      commit('setLoadedIABFCorpsPiecesForDancer', pieces)
    },
    async updateCostumeChecklist ({ commit, state, getters }, { dancerId, itemKey, checked }) {
      await yearPersonRef(state, getters, dancerId).update({
        [`costumeChecklist.${itemKey}`]: checked
      })
    },
    async updateRegistrationGifts ({ commit, getters, state }, payload) {
      if (!payload || !payload.id) {
        throw new Error('Your registration is still loading. Wait a moment and try again.')
      }
      const fields = {
        registrationGift1: payload.registrationGift1 || 'None',
        registrationGift2: payload.registrationGift2 || 'None',
        registrationGift3: payload.registrationGift3 || 'None',
        registrationGift4: payload.registrationGift4 || 'None',
        // No early-bird gift for 2027+ sell-out seasons
        earlyBirdGift: 'None',
        updatedAt: Date.now()
      }
      await yearPersonRef(state, getters, payload.id).update(fields)
      if (getters.currentIABFDancer && getters.currentIABFDancer.id === payload.id) {
        commit('setCurrentIABFDancer', { ...getters.currentIABFDancer, ...fields })
      }
      return fields
    },
    /**
     * Dancer saves “don’t compete this year” (or clears it to re-enter).
     * @param {{ dancerId: string, declined: boolean }} payload
     */
    async setCompetitionDeclined ({ commit, getters, state }, { dancerId, declined }) {
      if (!dancerId) {
        throw new Error('Your registration is still loading. Wait a moment and try again.')
      }
      const fields = {
        competitionDeclined: !!declined,
        competitionDeclinedAt: declined ? Date.now() : null,
        updatedAt: Date.now()
      }
      // Declining clears any partial competition registration flags if they weren't fully in
      if (declined) {
        const dancer = getters.currentIABFDancer
        if (dancer && dancer.id === dancerId && !dancer.competitionRegistered) {
          fields.competitionPieceId = null
        }
      }
      await yearPersonRef(state, getters, dancerId).update(fields)
      if (getters.currentIABFDancer && getters.currentIABFDancer.id === dancerId) {
        commit('setCurrentIABFDancer', { ...getters.currentIABFDancer, ...fields })
      }
      return fields
    },
    /**
     * Dancer marks showcase solos prep: not performing, all set, or clear (null).
     * all_set requires at least one approved piece with music on every approved piece.
     * @param {{ dancerId: string, status: null|'not_performing'|'all_set' }} payload
     */
    async setShowcasePrepStatus ({ commit, getters, state }, { dancerId, status }) {
      if (!dancerId) {
        throw new Error('Your registration is still loading. Wait a moment and try again.')
      }
      const allowed = [null, '', 'not_performing', 'all_set']
      const next = status === '' || status === undefined ? null : status
      if (!allowed.includes(next)) {
        throw new Error('Invalid showcase status.')
      }
      if (next === 'all_set') {
        const cols = dancerIabf(state)
        const snap = await db.collection(cols.pieces)
          .where('dancers', 'array-contains', dancerId)
          .get()
        const pieces = snap.docs
          .map(doc => ({ id: doc.id, ...(doc.data() || {}) }))
          .filter(p => !p.canceledByDancer)
        const approved = pieces.filter(p => (p.approvalStatus || 'approved') !== 'pending')
        if (!approved.length) {
          throw new Error('Upload music for an approved piece before marking all set.')
        }
        const missingMusic = approved.some(p => !(p.musicReceived && p.musicUrl))
        if (missingMusic) {
          throw new Error('Upload music for every approved piece before marking all set.')
        }
      }
      if (next === 'not_performing') {
        const cols = dancerIabf(state)
        const snap = await db.collection(cols.pieces)
          .where('dancers', 'array-contains', dancerId)
          .get()
        const hasActive = snap.docs.some(doc => {
          const d = doc.data() || {}
          return !d.canceledByDancer
        })
        if (hasActive) {
          throw new Error('Remove your showcase pieces before marking not performing.')
        }
      }
      const fields = {
        showcasePrepStatus: next,
        showcasePrepStatusAt: next ? Date.now() : null,
        updatedAt: Date.now()
      }
      await yearPersonRef(state, getters, dancerId).update(fields)
      commit('patchEffectiveIABFDancer', { id: dancerId, ...fields })
      return fields
    },
    async updateDancerPhone ({ commit, getters, state }, payload) {
      if (!payload || !payload.id) {
        throw new Error('Your registration is still loading. Wait a moment and try again.')
      }
      const phone = String(payload.phone || '').trim()
      if (!phone) {
        throw new Error('Please enter a phone number.')
      }
      const profile = {
        phone,
        last4: phone.slice(-4)
      }
      await yearPersonRef(state, getters, payload.id).update(profile)
      const personId = payload.personId ||
        (getters.currentIABFDancer && getters.currentIABFDancer.personId) ||
        (getters.currentIABFPerson && getters.currentIABFPerson.id)
      if (personId) {
        try {
          await updatePersonProfile(db, personId, { phone })
          const person = getters.currentIABFPerson
          if (person && person.id === personId) {
            commit('setCurrentIABFPerson', { ...person, phone })
          }
        } catch (personErr) {
          console.warn('Person dual-write (phone) failed:', personErr)
        }
      }
      if (getters.currentIABFDancer && getters.currentIABFDancer.id === payload.id) {
        commit('setCurrentIABFDancer', { ...getters.currentIABFDancer, ...profile })
      }
      return profile
    },
    /**
     * Update mailing address on enrollment + dual-write to iabf-people.
     */
    /**
     * Onsite / roommate: whether to place a hairdryer in their room.
     * @param {{ dancerId: string, wantsHairdryer: boolean }} payload
     */
    async updateDancerHairdryerPreference ({ commit, getters, state }, payload) {
      const dancerId = payload && payload.dancerId
      if (!dancerId) {
        throw new Error('Missing dancer')
      }
      const wantsHairdryer = payload.wantsHairdryer === true
      const cols = dancerIabf(state)
      await db.collection(cols.dancers).doc(dancerId).update({
        wantsHairdryer,
        wantsHairdryerUpdatedAt: Date.now(),
        updatedAt: Date.now()
      })
      commit('patchEffectiveIABFDancer', { id: dancerId, wantsHairdryer })
      return { wantsHairdryer }
    },
    /**
     * Onsite / roommate: what to stock in the in-room coffee maker.
     * Dual-writes coffee fields onto iabf-people (durable profile).
     * @param {{ dancerId: string, coffeeMakerStock: string[], personId?: string }} payload
     */
    async updateDancerCoffeeMakerStock ({ commit, getters, state }, payload) {
      const dancerId = payload && payload.dancerId
      if (!dancerId) {
        throw new Error('Missing dancer')
      }
      const stock = orderedCoffeeMakerStock(
        Array.isArray(payload.coffeeMakerStock)
          ? payload.coffeeMakerStock.filter(Boolean)
          : []
      )
      const coffee = stock.length ? stock.join(', ') : ''
      const cols = dancerIabf(state)
      await db.collection(cols.dancers).doc(dancerId).update({
        coffee,
        coffeeMakerStock: stock,
        coffeeMakerStockUpdatedAt: Date.now(),
        updatedAt: Date.now()
      })
      const personId = payload.personId ||
        (getters.currentIABFDancer && getters.currentIABFDancer.personId) ||
        (getters.currentIABFPerson && getters.currentIABFPerson.id)
      if (personId) {
        try {
          await updatePersonProfile(db, personId, { coffee, coffeeMakerStock: stock })
        } catch (personErr) {
          console.warn('Person dual-write (coffee maker) failed:', personErr)
        }
      }
      commit('patchEffectiveIABFDancer', {
        id: dancerId,
        coffee,
        coffeeMakerStock: stock
      })
      return { coffee, coffeeMakerStock: stock }
    },
    async updateDancerAddress ({ commit, getters, state }, payload) {
      if (!payload || !payload.id) {
        throw new Error('Your registration is still loading. Wait a moment and try again.')
      }
      const addressStreet = String(payload.addressStreet || '').trim()
      const addressStreet2 = String(payload.addressStreet2 || '').trim()
      const addressCity = String(payload.addressCity || '').trim()
      const addressState = String(payload.addressState || '').trim()
      const addressZip = String(payload.addressZip || '').trim()
      const addressCountry = normalizeCountryCode(payload.addressCountry)
      const mailingErr = mailingAddressError({
        addressStreet,
        addressStreet2,
        addressCity,
        addressState,
        addressZip,
        addressCountry
      })
      if (mailingErr) {
        throw new Error(mailingErr)
      }
      const profile = {
        addressStreet,
        addressStreet2,
        addressCity,
        addressState,
        addressZip,
        addressCountry,
        updatedAt: Date.now()
      }
      await yearPersonRef(state, getters, payload.id).update(profile)
      const personId = payload.personId ||
        (getters.currentIABFDancer && getters.currentIABFDancer.personId) ||
        (getters.currentIABFPerson && getters.currentIABFPerson.id)
      if (personId) {
        try {
          await updatePersonProfile(db, personId, {
            addressStreet,
            addressStreet2,
            addressCity,
            addressState,
            addressZip,
            addressCountry
          })
          const person = getters.currentIABFPerson
          if (person && person.id === personId) {
            commit('setCurrentIABFPerson', { ...person, ...profile })
          }
        } catch (personErr) {
          console.warn('Person dual-write (address) failed:', personErr)
        }
      }
      if (getters.currentIABFDancer && getters.currentIABFDancer.id === payload.id) {
        commit('setCurrentIABFDancer', { ...getters.currentIABFDancer, ...profile })
      }
      return profile
    },
    async updateEmergencyContact ({ commit, getters, state }, payload) {
      commit('setLoading', true)
      try {
        if (!payload.id) {
          throw new Error('Your registration is still loading. Wait a moment and try again.')
        }
        const profile = {
          emergencyContactName: payload.emergencyContactName,
          emergencyContactPhone: payload.emergencyContactPhone,
          emergencyContactRelationship: payload.emergencyContactRelationship,
          emergencyContactEntered: payload.emergencyContactEntered
        }
        // Dual-write: year enrollment (ops) + person (durable profile).
        await yearPersonRef(state, getters, payload.id).update(profile)
        const personId = payload.personId ||
          (getters.currentIABFDancer && getters.currentIABFDancer.personId) ||
          (getters.currentIABFPerson && getters.currentIABFPerson.id)
        if (personId) {
          try {
            await updatePersonProfile(db, personId, profile)
            const person = getters.currentIABFPerson
            if (person && person.id === personId) {
              commit('setCurrentIABFPerson', { ...person, ...profile })
            }
          } catch (personErr) {
            // Enrollment write already succeeded — don't fail the whole save.
            console.warn('Person dual-write (emergency) failed:', personErr)
          }
        }
        if (getters.currentIABFDancer && getters.currentIABFDancer.id === payload.id) {
          commit('setCurrentIABFDancer', { ...getters.currentIABFDancer, ...profile })
        }
        commit('setLoading', false)
      } catch (error) {
        commit('setLoading', false)
        throw error
      }
    },
    async updateDietaryRestrictions ({ commit, getters, state }, payload) {
      commit('setLoading', true)
      try {
        if (!payload.id) {
          throw new Error('Your registration is still loading. Wait a moment and try again.')
        }
        const profile = {
          diet: payload.diet,
          dietDetails: payload.dietDetails || [],
          gluten: payload.gluten || '',
          foodsNotConsumed: payload.foodsNotConsumed,
          foodAllergies: payload.foodAllergies,
          coffee: payload.coffee,
          coffeeMakerStock: payload.coffeeMakerStock || [],
          dietaryNotes: payload.dietaryNotes,
          dietaryRestrictionsEntered: payload.dietaryRestrictionsEntered
        }
        await yearPersonRef(state, getters, payload.id).update(profile)
        const personId = payload.personId ||
          (getters.currentIABFDancer && getters.currentIABFDancer.personId) ||
          (getters.currentIABFPerson && getters.currentIABFPerson.id)
        if (personId) {
          try {
            await updatePersonProfile(db, personId, profile)
            const person = getters.currentIABFPerson
            if (person && person.id === personId) {
              commit('setCurrentIABFPerson', { ...person, ...profile })
            }
          } catch (personErr) {
            console.warn('Person dual-write (dietary) failed:', personErr)
          }
        }
        if (getters.currentIABFDancer && getters.currentIABFDancer.id === payload.id) {
          commit('setCurrentIABFDancer', { ...getters.currentIABFDancer, ...profile })
        }
        commit('setLoading', false)
      } catch (error) {
        commit('setLoading', false)
        throw error
      }
    },
    saveCostumeOrder ({ commit }, payload) {
      commit('setLoading', true)
      return new Promise((resolve, reject) => {
        db.collection(IABF.dancers).doc(payload.id).update({
          costumeOrderCompleted: payload.costumeOrderCompleted || false,
          costumeOrderPaid: payload.costumeOrderPaid || false,
          costumeTotalCost: payload.costumeTotalCost || 0,
          waltzOfHours: payload.waltzOfHours || null,
          aliceInWonderland: payload.aliceInWonderland || null,
          leJazzHot: payload.leJazzHot || null,
          becoming: payload.becoming || null,
          garlandWaltz: payload.garlandWaltz || null,
          checkIn: payload.checkIn || null,
          sharedMeasurements: payload.sharedMeasurements || null,
          costumeOrderSubmittedAt: new Date().toISOString()
        })
        .then(() => {
          commit('setLoading', false)
          resolve()
        }).catch(error => {
          commit('setLoading', false)
          reject(error)
        })
      })
    },
    setCostumeOrderPaid ({ commit }, { id, paid }) {
      return db.collection(IABF.dancers).doc(id).update({
        costumeOrderPaid: paid
      })
    },
    assignCostumeColor ({ commit }, { id, field, color }) {
      return db.collection(IABF.dancers).doc(id).update({
        [field]: color
      })
    },
    loadCostumeInventory ({ commit }) {
      return db.collection('costume-inventory').orderBy('name').get().then(snap => {
        const items = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        commit('setCostumeInventory', items)
      })
    },
    saveCostumeInventoryItem ({ commit, state }, item) {
      const { id, ...data } = item
      if (id) {
        return db.collection('costume-inventory').doc(id).update(data).then(() => {
          commit('setCostumeInventory', state.costumeInventory.map(c => {
            if (c.id !== id) {
              return c
            }
            return { ...c, ...data }
          }))
        })
      } else {
        data.createdAt = new Date()
        return db.collection('costume-inventory').add(data).then(ref => {
          commit('setCostumeInventory', [...state.costumeInventory, { id: ref.id, ...data }])
        })
      }
    },
    deleteCostumeInventoryItem ({ commit, state }, id) {
      return db.collection('costume-inventory').doc(id).delete().then(() => {
        commit('setCostumeInventory', state.costumeInventory.filter(c => c.id !== id))
      })
    },
    loadFestivalInventory ({ commit }) {
      return db.collection(IABF_FESTIVAL_INVENTORY_COLLECTION).get().then(snap => {
        const items = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        commit('setFestivalInventory', items)
        return items
      })
    },
    async saveFestivalInventoryItem ({ dispatch }, item) {
      const { id, ...data } = item
      const payload = {
        ...data,
        updatedAt: Date.now()
      }
      if (id) {
        await db.collection(IABF_FESTIVAL_INVENTORY_COLLECTION).doc(id).set(payload, { merge: true })
      } else {
        payload.createdAt = Date.now()
        await db.collection(IABF_FESTIVAL_INVENTORY_COLLECTION).add(payload)
      }
      return dispatch('loadFestivalInventory')
    },
    async deleteFestivalInventoryItem ({ dispatch }, id) {
      if (!id) {
        return
      }
      await db.collection(IABF_FESTIVAL_INVENTORY_COLLECTION).doc(id).delete()
      return dispatch('loadFestivalInventory')
    },
    loadCostumeCloset ({ commit }) {
      return db.collection(IABF_COSTUME_CLOSET_COLLECTION).get().then(snap => {
        const items = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        commit('setCostumeCloset', items)
        return items
      })
    },
    loadClosetLoans ({ commit }, year) {
      const y = Number(year)
      let q = db.collection(IABF_CLOSET_LOANS_COLLECTION)
      if (Number.isFinite(y) && y > 0) {
        q = q.where('festivalYear', '==', y)
      }
      return q.get().then(snap => {
        const items = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        commit('setClosetLoans', items)
        return items
      })
    },
    loadDancerLedger ({ commit }, { year, dancerId, email, uid } = {}) {
      let q = db.collection(IABF_DANCER_LEDGER_COLLECTION)
      if (uid) {
        q = q.where('uid', '==', uid)
      } else if (dancerId) {
        q = q.where('dancerId', '==', dancerId)
      } else if (email) {
        q = q.where('email', '==', String(email).toLowerCase())
      } else if (year) {
        q = q.where('festivalYear', '==', Number(year))
      }
      return q.get().then(snap => {
        let items = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        if (year && dancerId) {
          items = items.filter(r => Number(r.festivalYear) === Number(year))
        }
        commit('setDancerLedger', items)
        return items
      })
    },
    borrowClosetItem (_, { itemId, year }) {
      const callable = functions.httpsCallable('borrowClosetItem')
      return callable({ itemId, year }).then(res => res.data)
    },
    cancelClosetLoan (_, { loanId }) {
      const callable = functions.httpsCallable('cancelClosetLoan')
      return callable({ loanId }).then(res => res.data)
    },
    checkoutClosetLoan (_, { loanId }) {
      const callable = functions.httpsCallable('checkoutClosetLoan')
      return callable({ loanId }).then(res => res.data)
    },
    returnClosetLoan (_, { loanId }) {
      const callable = functions.httpsCallable('returnClosetLoan')
      return callable({ loanId }).then(res => res.data)
    },
    markDancerLedgerPaid (_, { ledgerId }) {
      const callable = functions.httpsCallable('markDancerLedgerPaid')
      return callable({ ledgerId }).then(res => res.data)
    },
    async saveCostumeClosetItem ({ dispatch }, item) {
      const { id, ...data } = item
      const payload = {
        ...data,
        updatedAt: Date.now()
      }
      if (id) {
        await db.collection(IABF_COSTUME_CLOSET_COLLECTION).doc(id).set(payload, { merge: true })
      } else {
        payload.createdAt = Date.now()
        await db.collection(IABF_COSTUME_CLOSET_COLLECTION).add(payload)
      }
      return dispatch('loadCostumeCloset')
    },
    async deleteCostumeClosetItem ({ dispatch }, id) {
      if (!id) {
        return
      }
      await db.collection(IABF_COSTUME_CLOSET_COLLECTION).doc(id).delete()
      return dispatch('loadCostumeCloset')
    },
    async bulkAddCostumeClosetItems ({ dispatch }, items) {
      const colRef = db.collection(IABF_COSTUME_CLOSET_COLLECTION)
      const existing = await colRef.limit(1).get()
      if (!existing.empty) {
        return dispatch('loadCostumeCloset')
      }
      const now = Date.now()
      const batch = db.batch()
      items.forEach(item => {
        const ref = colRef.doc()
        batch.set(ref, {
          ...item,
          published: item.published !== false,
          photoUrl: item.photoUrl || '',
          createdAt: now,
          updatedAt: now
        })
      })
      await batch.commit()
      return dispatch('loadCostumeCloset')
    },
    async bulkAddFestivalInventoryItems ({ dispatch }, items) {
      const now = Date.now()
      const colRef = db.collection(IABF_FESTIVAL_INVENTORY_COLLECTION)
      const chunks = []
      for (let i = 0; i < items.length; i += 400) {
        chunks.push(items.slice(i, i + 400))
      }
      for (const chunk of chunks) {
        const batch = db.batch()
        chunk.forEach(item => {
          const ref = colRef.doc()
          batch.set(ref, {
            ...item,
            createdAt: now,
            updatedAt: now
          })
        })
        await batch.commit()
      }
      return dispatch('loadFestivalInventory')
    },
    bulkSaveCostumeInventoryItems ({ commit, state }, items) {
      const batch = db.batch()
      const now = new Date()
      const added = []
      items.forEach(item => {
        const ref = db.collection('costume-inventory').doc()
        const data = { name: item.name, type: item.type, color: item.color, createdAt: now }
        if (item.bust != null) data.bust = item.bust
        if (item.waist != null) data.waist = item.waist
        if (item.hip != null) data.hip = item.hip
        if (item.halfGirth != null) data.halfGirth = item.halfGirth
        if (item.girth != null) data.girth = item.girth
        if (item.height != null) data.height = item.height
        batch.set(ref, data)
        added.push({ id: ref.id, ...data })
      })
      return batch.commit().then(() => {
        commit('setCostumeInventory', [...state.costumeInventory, ...added])
      })
    },
    createCostumeCheckoutSession ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        fetch('https://us-central1-broche-ballet-online.cloudfunctions.net/createCostumeCheckoutSession', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dancerId: payload.dancerId,
            lineItems: payload.lineItems,
            successUrl: payload.successUrl,
            cancelUrl: payload.cancelUrl
          })
        })
        .then(response => response.json())
        .then(data => {
          if (data.url) {
            resolve(data.url)
          } else {
            reject(new Error('No checkout URL returned'))
          }
        })
        .catch(error => {
          reject(error)
        })
      })
    },
    loadGuestRegistrationsForDancer ({ commit, state }, dancerEmail) {
      // Dancer-facing: enrollment year (2027 or wrap-up 2026, etc.)
      return db.collection(dancerIabf(state).guestRegistrations).where('dancerEmail', '==', dancerEmail).get().then(snap => {
        const regs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        commit('setGuestRegistrations', regs)
      })
    },
    loadAllGuestRegistrations ({ commit, state }) {
      return db.collection(adminIabf(state).guestRegistrations).get().then(snap => {
        const regs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        commit('setAllGuestRegistrations', regs)
      })
    },
    /**
     * Guest meal names from a prior festival year, keyed to the host dancer's
     * personId / email so we can match returning dancers this year.
     */
    async loadPriorYearMealGuests (_ctx, year) {
      const y = Number(year)
      if (!Number.isFinite(y) || y < 2000) {
        return []
      }
      const cols = iabfForYear(y)
      if (!cols.guestRegistrations) {
        return []
      }
      try {
        const guestSnap = await db.collection(cols.guestRegistrations).get()
        let dancerSnap = { docs: [] }
        if (cols.dancers) {
          dancerSnap = await db.collection(cols.dancers).get()
        }
        const hostById = {}
        dancerSnap.docs.forEach((doc) => {
          const d = doc.data() || {}
          hostById[doc.id] = {
            personId: d.personId || null,
            email: String(d.email || '').trim().toLowerCase()
          }
        })
        return guestSnap.docs.map((doc) => {
          const g = doc.data() || {}
          const host = hostById[g.dancerId] || {}
          return {
            id: doc.id,
            type: g.type || 'guest',
            guestName: String(g.guestName || '').trim(),
            dancerId: g.dancerId || null,
            allMeals: !!g.allMeals,
            meals: Array.isArray(g.meals) ? g.meals : [],
            awardsAfterparty: !!g.awardsAfterparty,
            paymentStatus: g.paymentStatus || '',
            hostPersonId: host.personId || null,
            hostEmail: host.email || ''
          }
        })
      } catch (e) {
        console.warn('loadPriorYearMealGuests failed', y, e)
        return []
      }
    },
    subscribeSetupPlan ({ commit, state }, payload) {
      // Resolves with the unsubscribe function *after* the first snapshot
      // has populated the store, so callers can safely copy local state
      // without racing the network. Admin passes { useAdminYear: true }.
      const useAdminYear = !!(payload && payload.useAdminYear)
      const configCol = useAdminYear ? adminIabf(state).config : IABF.config
      return new Promise((resolve, reject) => {
        let resolved = false
        const unsub = db.collection(configCol).doc('setup-plan').onSnapshot(
          doc => {
            const data = doc.exists ? (doc.data() || {}) : {}
            commit('setSetupPlan', { rooms: data.rooms || [], updatedAt: data.updatedAt || null })
            if (!resolved) {
              resolved = true
              resolve(unsub)
            }
          },
          err => {
            console.error('setup-plan subscribe error:', err)
            if (!resolved) {
              resolved = true
              reject(err)
            }
          }
        )
      })
    },
    async loadSetupPlanForYear (_ctx, year) {
      const y = Number(year)
      if (!Number.isFinite(y) || y < 2000) {
        return { rooms: [], updatedAt: null }
      }
      const cols = iabfForYear(y)
      if (!cols.config) {
        return { rooms: [], updatedAt: null }
      }
      try {
        const snap = await db.collection(cols.config).doc('setup-plan').get()
        const data = snap.exists ? (snap.data() || {}) : {}
        return { rooms: data.rooms || [], updatedAt: data.updatedAt || null }
      } catch (e) {
        console.warn('loadSetupPlanForYear failed', y, e)
        return { rooms: [], updatedAt: null }
      }
    },
    async saveSetupPlan ({ state }, plan) {
      const useAdminYear = !!(plan && plan.useAdminYear)
      const configCol = useAdminYear ? adminIabf(state).config : IABF.config
      const payload = { rooms: plan.rooms || [], updatedAt: Date.now() }
      await db.collection(configCol).doc('setup-plan').set(payload)
    },
    async updateSetupItemImage ({ state }, { roomId, itemId, imageUrl, useAdminYear }) {
      const configCol = useAdminYear ? adminIabf(state).config : IABF.config
      const ref = db.collection(configCol).doc('setup-plan')
      await db.runTransaction(async tx => {
        const snap = await tx.get(ref)
        const data = snap.exists ? (snap.data() || {}) : {}
        const rooms = (data.rooms || []).map(r => {
          if (r.id !== roomId) return r
          const items = (r.items || []).map(it => {
            if (it.id !== itemId) return it
            const next = { ...it }
            if (imageUrl) next.imageUrl = imageUrl
            else delete next.imageUrl
            return next
          })
          return { ...r, items }
        })
        tx.set(ref, { rooms, updatedAt: Date.now() })
      })
    },
    async toggleSetupItem ({ state }, { roomId, itemId, doneBy, useAdminYear }) {
      const configCol = useAdminYear ? adminIabf(state).config : IABF.config
      const ref = db.collection(configCol).doc('setup-plan')
      await db.runTransaction(async tx => {
        const snap = await tx.get(ref)
        const data = snap.exists ? (snap.data() || {}) : {}
        const rooms = (data.rooms || []).map(r => {
          if (r.id !== roomId) return r
          const items = (r.items || []).map(it => {
            if (it.id !== itemId) return it
            if (it.doneAt) return { ...it, doneAt: null, doneBy: null }
            return { ...it, doneAt: Date.now(), doneBy: doneBy || null }
          })
          return { ...r, items }
        })
        tx.set(ref, { rooms, updatedAt: Date.now() })
      })
    },
    subscribeAccountingProductLines ({ commit }) {
      return new Promise((resolve, reject) => {
        let resolved = false
        const unsub = db.collection(IABF.accountingProductLines).onSnapshot(
          snap => {
            const lines = snap.docs.map(d => ({ id: d.id, ...d.data() }))
            commit('setAccountingProductLines', lines)
            if (!resolved) { resolved = true; resolve(unsub) }
          },
          err => {
            console.error('accounting product-lines subscribe error:', err)
            if (!resolved) { resolved = true; reject(err) }
          }
        )
      })
    },
    subscribeAccountingTransactions ({ commit }) {
      return new Promise((resolve, reject) => {
        let resolved = false
        const unsub = db.collection(IABF.accountingTransactions).onSnapshot(
          snap => {
            const txs = snap.docs.map(d => ({ id: d.id, ...d.data() }))
            commit('setAccountingTransactions', txs)
            if (!resolved) { resolved = true; resolve(unsub) }
          },
          err => {
            console.error('accounting transactions subscribe error:', err)
            if (!resolved) { resolved = true; reject(err) }
          }
        )
      })
    },
    async saveAccountingProductLine (_ctx, line) {
      const payload = {
        name: String(line.name || '').trim(),
        sortOrder: Number.isFinite(line.sortOrder) ? line.sortOrder : 0,
        notes: line.notes || '',
        archived: !!line.archived,
        // What the line was planned to bring in and to cost. Kept as the
        // record of the plan — never overwritten by actuals — so the
        // year-end comparison stays honest. budgetCents predates the split
        // into two sides and is read as the expense plan where it survives.
        forecastRevenueCents: Math.round(Number(line.forecastRevenueCents) || 0),
        forecastExpenseCents: Math.round(
          Number(line.forecastExpenseCents) || Number(line.budgetCents) || 0
        ),
        forecastExpenseByYear: (line.forecastExpenseByYear && typeof line.forecastExpenseByYear === 'object')
          ? line.forecastExpenseByYear
          : {},
        forecastRevenueByYear: (line.forecastRevenueByYear && typeof line.forecastRevenueByYear === 'object')
          ? line.forecastRevenueByYear
          : {},
        kind: line.kind === 'investment' ? 'investment' : 'operating',
        updatedAt: Date.now()
      }
      if (line.id) {
        await db.collection(IABF.accountingProductLines).doc(line.id).set(payload, { merge: true })
        return line.id
      }
      payload.createdAt = Date.now()
      const ref = await db.collection(IABF.accountingProductLines).add(payload)
      return ref.id
    },
    async deleteAccountingProductLine (_ctx, id) {
      if (!id) return
      await db.collection(IABF.accountingProductLines).doc(id).delete()
    },
    async saveAccountingTransaction (_ctx, tx) {
      const splits = (tx.splits || [])
        .map(s => ({
          productLineId: String(s.productLineId || ''),
          amountCents: Math.round(Number(s.amountCents) || 0),
          note: s.note || ''
        }))
        .filter(s => s.productLineId && s.amountCents !== 0)
      // Empty-splits transactions are valid: transfers (no product
      // line by design) and uncategorized expense/revenue rows from the
      // bank import. The caller supplies totalCents so the row still
      // shows up in the year totals and the user can categorize later.
      const totalCents = splits.length
        ? splits.reduce((sum, s) => sum + s.amountCents, 0)
        : Math.round(Number(tx.totalCents) || 0)
      const dateStr = String(tx.date || '').slice(0, 10)
      const calYear = parseInt(dateStr.slice(0, 4), 10) || tx.year || new Date().getFullYear()
      const calMonth = parseInt(dateStr.slice(5, 7), 10) || 1
      // Festival year cycle runs Aug → Jul. Aug-Dec of year Y belongs to
      // the (Y+1) festival (saving / planning); Jan-Jul of year Y is the
      // run-up + month-of for festival Y.
      const defaultFestivalYear = calMonth >= 8 ? calYear + 1 : calYear
      const festivalYear = Number.isFinite(parseInt(tx.festivalYear, 10))
        ? parseInt(tx.festivalYear, 10)
        : defaultFestivalYear
      const payload = {
        date: dateStr,
        year: calYear,
        festivalYear,
        type: ['expense', 'revenue', 'transfer'].includes(tx.type) ? tx.type : 'revenue',
        source: tx.source || 'manual',
        sourceRef: tx.sourceRef || '',
        description: tx.description || '',
        splits,
        totalCents,
        notes: tx.notes || '',
        dismissed: !!tx.dismissed,
        updatedAt: Date.now()
      }
      if (tx.id) {
        // Editing an existing transaction = explicit user edit. Flag it so
        // re-imports don't overwrite the description / splits / categorization.
        if (tx.source === 'stripe') payload.userEdited = true
        await db.collection(IABF.accountingTransactions).doc(tx.id).set(payload, { merge: true })
        return tx.id
      }
      payload.createdAt = Date.now()
      const ref = await db.collection(IABF.accountingTransactions).add(payload)
      return ref.id
    },
    async deleteAccountingTransaction (_ctx, id) {
      if (!id) return
      await db.collection(IABF.accountingTransactions).doc(id).delete()
    },
    subscribeCanceledDancers ({ commit, state }) {
      return new Promise((resolve, reject) => {
        let resolved = false
        const unsub = db.collection(adminIabf(state).canceledDancers).onSnapshot(
          snap => {
            const list = snap.docs.map(doc => {
              const data = doc.data() || {}
              const source = data.snapshot && typeof data.snapshot === 'object' ? data.snapshot : data
              let depositOnFileCents = Math.round(Number(data.depositOnFileCents) || 0)
              let paymentsOnFileCents = Math.round(Number(data.paymentsOnFileCents) || 0)
              if (depositOnFileCents <= 0) {
                const next = depositOnFileCentsFromEnrollment(source)
                if (next > 0) {
                  depositOnFileCents = next
                  doc.ref.set({ depositOnFileCents: next, updatedAt: Date.now() }, { merge: true }).catch(() => {})
                }
              }
              if (paymentsOnFileCents <= 0) {
                const next = paymentsOnFileCentsFromEnrollment(source)
                if (next > 0) {
                  paymentsOnFileCents = next
                  doc.ref.set({ paymentsOnFileCents: next, updatedAt: Date.now() }, { merge: true }).catch(() => {})
                }
              }
              return { id: doc.id, ...data, depositOnFileCents, paymentsOnFileCents }
            })
            commit('setCanceledDancers', list)
            if (!resolved) { resolved = true; resolve(unsub) }
          },
          err => {
            console.error('canceled-dancers subscribe error:', err)
            if (!resolved) { resolved = true; reject(err) }
          }
        )
      })
    },
    async saveCanceledDancer ({ state }, dancer) {
      const colName = adminIabf(state).canceledDancers
      const payload = {
        firstName: dancer.firstName || '',
        lastName: dancer.lastName || '',
        email: String(dancer.email || '').trim().toLowerCase(),
        depositOnFileCents: Math.round(Number(dancer.depositOnFileCents) || 0),
        paymentsOnFileCents: Math.round(Number(dancer.paymentsOnFileCents) || 0),
        cancelReason: dancer.cancelReason || '',
        canceledAt: dancer.canceledAt || Date.now(),
        notes: dancer.notes || '',
        updatedAt: Date.now()
      }
      if (dancer.id) {
        await db.collection(colName).doc(dancer.id).set(payload, { merge: true })
        return dancer.id
      }
      payload.createdAt = Date.now()
      const ref = await db.collection(colName).add(payload)
      return ref.id
    },
    async deleteCanceledDancer ({ state }, id) {
      if (!id) return
      await db.collection(adminIabf(state).canceledDancers).doc(id).delete()
    },
    async cancelDancer ({ state }, { dancerId, cancelReason }) {
      // Move an active dancer into the canceled-dancers collection. Copies
      // identifying + payment-summary fields, then deletes the active doc
      // so the rest of the app stops surfacing them.
      // Also drops year-scoped prep: intro, travel details, pieces, carpools.
      if (!dancerId) return
      const cols = adminIabf(state)
      const ref = db.collection(cols.dancers).doc(dancerId)
      const snap = await ref.get()
      if (!snap.exists) return
      const d = snap.data() || {}
      const payload = {
        firstName: d.firstName || '',
        lastName: d.lastName || '',
        email: String(d.email || '').trim().toLowerCase(),
        depositOnFileCents: depositOnFileCentsFromEnrollment(d),
        paymentsOnFileCents: paymentsOnFileCentsFromEnrollment(d),
        cancelReason: cancelReason || '',
        canceledAt: Date.now(),
        originalDancerId: dancerId,
        snapshot: d,
        personId: d.personId || null,
        createdAt: Date.now(),
        updatedAt: Date.now()
      }
      await db.collection(cols.canceledDancers).doc(dancerId).set(payload)
      await cleanupDeletedDancerRelated(cols, dancerId, d.travelDetailsId || null)
      await ref.delete()
      if (d.personId) {
        try {
          await removeYearFromPerson(db, d.personId, cols.year)
        } catch (e) {
          console.warn('cancelDancer could not drop yearsAttended', e)
        }
      }
      try {
        await sweepOrphanedYearData(cols)
      } catch (e) {
        console.warn('cancelDancer orphan sweep failed', e)
      }
      try {
        await adjustIabfStayInventory({
          year: cols.year,
          fromRoom: d.room,
          totalDelta: -1
        })
      } catch (e) {
        console.warn('cancelDancer inventory adjust failed', e)
      }
      await refreshRegistrationInventory()
    },
    async restoreCanceledDancer ({ state }, id) {
      // Move a canceled dancer back into the active dancers collection
      // using their original snapshot.
      if (!id) return
      const cols = adminIabf(state)
      const ref = db.collection(cols.canceledDancers).doc(id)
      const snap = await ref.get()
      if (!snap.exists) return
      const data = snap.data() || {}
      const snapshot = data.snapshot
      if (!snapshot) {
        throw new Error('Canceled dancer has no snapshot to restore from. Recreate manually.')
      }
      const targetId = data.originalDancerId || id
      await db.collection(cols.dancers).doc(targetId).set(snapshot)
      await ref.delete()
      try {
        await adjustIabfStayInventory({
          year: cols.year,
          toRoom: snapshot.room,
          totalDelta: 1
        })
      } catch (e) {
        console.warn('restoreCanceledDancer inventory adjust failed', e)
      }
      await refreshRegistrationInventory()
    },
    /**
     * Load carpool slots. Pass { useAdminYear: true } from admin UI so the
     * year switcher is respected; dancer dashboard uses IABF (dancer year).
     */
    loadCarpoolSlots ({ commit, state }, payload = {}) {
      const useAdminYear = !!(payload && payload.useAdminYear)
      const cols = useAdminYear ? adminIabf(state) : dancerIabf(state)
      return db.collection(cols.carpools).get().then(snap => {
        const slots = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        commit('setIABFCarpoolSlots', slots)
      }).catch((err) => {
        console.warn('loadCarpoolSlots failed:', err)
        commit('setIABFCarpoolSlots', [])
      })
    },
    // Admin-only: add a free-form name (faculty, guest, etc.) to a slot or
    // waitlist without touching the year dancers collection. id is generated client-side.
    async updateCarpoolRiderPhone ({ commit }, { slotId, riderId, phone }) {
      const slotRef = db.collection(IABF.carpools).doc(slotId)
      const slotSnap = await slotRef.get()
      if (!slotSnap.exists) return
      const slot = slotSnap.data()
      const dancers = (slot.dancers || []).map(d => {
        if (d.id !== riderId) return d
        const trimmed = String(phone || '').trim()
        const next = { ...d }
        if (trimmed) next.phone = trimmed
        else delete next.phone
        return next
      })
      await slotRef.update({ dancers })
    },
    async updateCarpoolRiderFlightTime ({ commit }, { slotId, riderId, flightTime }) {
      const slotRef = db.collection(IABF.carpools).doc(slotId)
      const slotSnap = await slotRef.get()
      if (!slotSnap.exists) return
      const slot = slotSnap.data()
      const dancers = (slot.dancers || []).map(d => {
        if (d.id !== riderId) return d
        const trimmed = String(flightTime || '').trim()
        const next = { ...d }
        if (trimmed) next.guestFlightTime = trimmed
        else delete next.guestFlightTime
        return next
      })
      await slotRef.update({ dancers })
    },
    async addGuestToCarpoolSlot ({ commit }, { slotId, name, location, requestedTime, airport }) {
      const slotRef = db.collection(IABF.carpools).doc(slotId)
      const slotSnap = await slotRef.get()
      const slot = slotSnap.data()
      const dancers = slot.dancers || []
      const id = 'guest-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8)
      const entry = { id, name, isGuest: true }
      if (slot.direction === 'arrival' && location) entry.dropoff = location
      if (slot.direction === 'departure' && location) entry.pickup = location
      if (requestedTime) entry.requestedTime = requestedTime
      if (airport) entry.airport = airport
      dancers.push(entry)
      await slotRef.update({ dancers })
    },
    async joinCarpoolSlot ({ commit, getters, state }, { slotId, direction, dancerId, dancerName, carpoolArrivalDropoff, carpoolDeparturePickup, requestedTime, airport }) {
      const cols = dancerIabf(state)
      const slotRef = db.collection(cols.carpools).doc(slotId)
      const dancerRef = yearPersonRef(state, getters, dancerId)
      const slotSnap = await slotRef.get()
      const slot = slotSnap.data()
      const dancers = slot.dancers || []
      if (dancers.find(d => d.id === dancerId)) return
      const dancerEntry = { id: dancerId, name: dancerName }
      if (direction === 'arrival' && carpoolArrivalDropoff) dancerEntry.dropoff = carpoolArrivalDropoff
      if (direction === 'departure' && carpoolDeparturePickup) dancerEntry.pickup = carpoolDeparturePickup
      if (requestedTime) dancerEntry.requestedTime = requestedTime
      if (airport) dancerEntry.airport = airport
      dancers.push(dancerEntry)
      await slotRef.update({ dancers })
      const update = { carpoolResponded: true }
      if (direction === 'arrival') {
        update.carpoolArrivalSlotId = slotId
        if (carpoolArrivalDropoff) update.carpoolArrivalDropoff = carpoolArrivalDropoff
      } else {
        update.carpoolDepartureSlotId = slotId
        if (carpoolDeparturePickup) update.carpoolDeparturePickup = carpoolDeparturePickup
      }
      await dancerRef.update(update)
      commit('patchEffectiveIABFDancer', { id: dancerId, ...update })
    },
    async leaveCarpoolSlot ({ commit, state, getters }, { slotId, direction, dancerId }) {
      const cols = dancerIabf(state)
      const slotRef = db.collection(cols.carpools).doc(slotId)
      const slotSnap = await slotRef.get()
      const dancers = (slotSnap.data().dancers || []).filter(d => d.id !== dancerId)
      await slotRef.update({ dancers })
      // Guest entries (faculty/non-dancer) have no dancer doc — skip the update.
      if (typeof dancerId === 'string' && dancerId.startsWith('guest-')) return
      const dancerRef = yearPersonRef(state, getters, dancerId)
      const update = {}
      if (direction === 'arrival') update.carpoolArrivalSlotId = null
      else update.carpoolDepartureSlotId = null
      await dancerRef.update(update)
      commit('patchEffectiveIABFDancer', { id: dancerId, ...update })
    },
    async declineCarpool ({ commit, state, getters }, { dancerId }) {
      const update = {
        carpoolResponded: true,
        carpoolDeclined: true
      }
      await yearPersonRef(state, getters, dancerId).update(update)
      commit('patchEffectiveIABFDancer', { id: dancerId, ...update })
    },
    async undeclineCarpool ({ commit, state, getters }, { dancerId }) {
      const update = {
        carpoolDeclined: false,
        carpoolResponded: false
      }
      await yearPersonRef(state, getters, dancerId).update(update)
      commit('patchEffectiveIABFDancer', { id: dancerId, ...update })
    },
    async updateDancerCarpoolDecline ({ commit, state, getters }, { dancerId, update }) {
      await yearPersonRef(state, getters, dancerId).update(update)
      commit('patchEffectiveIABFDancer', { id: dancerId, ...update })
    },
    async createCarpoolSlot ({ commit }, slot) {
      const ref = await db.collection(IABF.carpools).add({ ...slot, dancers: [], createdAt: new Date() })
      return ref.id
    },
    async deleteCarpoolSlot ({ commit }, slotId) {
      await db.collection(IABF.carpools).doc(slotId).delete()
    },
    async updateCarpoolSlot ({ commit }, { id, ...data }) {
      await db.collection(IABF.carpools).doc(id).update(data)
    },
    /**
     * Copy carpool slot structure from previous festival year into the
     * current admin year. Slots only (no dancers). Dates shift by the
     * difference between festival arrival days so 2026 → 2027 lands correctly.
     */
    async seedCarpoolSlots ({ state }) {
      const year = state.adminFestivalYear || IABF_DANCER_YEAR
      const prevYear = year - 1
      const targetCol = iabfForYear(year).carpools
      const sourceCol = iabfForYear(prevYear).carpools

      const existing = await db.collection(targetCol).limit(1).get()
      if (!existing.empty) {
        return { seeded: 0, error: `${year} already has carpool slots.` }
      }

      const snap = await db.collection(sourceCol).get()
      if (snap.empty) {
        return {
          seeded: 0,
          error: `No carpool slots found for ${prevYear}. Add slots manually or seed that year first.`
        }
      }

      const dayOffset = carpoolFestivalDayOffset(prevYear, year)
      const batch = db.batch()
      let order = 0
      let seeded = 0

      snap.docs.forEach((doc) => {
        const data = doc.data() || {}
        // Skip pure empties; keep waitlists and timed slots
        const ref = db.collection(targetCol).doc()
        batch.set(ref, {
          direction: data.direction || 'arrival',
          date: shiftCarpoolDateLabel(data.date, prevYear, dayOffset),
          airport: data.airport || '',
          time: data.time || '',
          pricePerPerson: data.pricePerPerson != null ? data.pricePerPerson : 0,
          maxSeats: data.maxSeats != null ? data.maxSeats : 4,
          dancers: [],
          isWaitlist: !!data.isWaitlist,
          active: data.active !== false,
          sortOrder: data.sortOrder != null ? data.sortOrder : order++,
          createdAt: new Date(),
          seededFromYear: prevYear
        })
        seeded++
      })

      await batch.commit()
      return { seeded, fromYear: prevYear, toYear: year }
    },
    saveGuestRegistration ({ commit, state }, data) {
      return db.collection(IABF.guestRegistrations).add({ ...data, createdAt: new Date() }).then(ref => {
        const newReg = { id: ref.id, ...data }
        commit('setGuestRegistrations', [...state.guestRegistrations, newReg])
        return ref.id
      })
    },
    createGuestCheckoutSession ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        fetch('https://us-central1-broche-ballet-online.cloudfunctions.net/createGuestCheckoutSession', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
        .then(r => r.json())
        .then(data => data.url ? resolve(data.url) : reject(new Error('No checkout URL returned')))
        .catch(reject)
      })
    },
    createCardSetupSession ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        fetch('https://us-central1-broche-ballet-online.cloudfunctions.net/createCardSetupSession', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
        .then(r => r.json())
        .then(data => data.url ? resolve(data.url) : reject(new Error('No checkout URL returned')))
        .catch(reject)
      })
    },
    createCombinedCheckoutSession ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        fetch('https://us-central1-broche-ballet-online.cloudfunctions.net/createCombinedCheckoutSession', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
        .then(r => r.json())
        .then(data => data.url ? resolve(data.url) : reject(new Error('No checkout URL returned')))
        .catch(reject)
      })
    },
    setGuestsPaid ({ commit, state }, guestIds) {
      const batch = db.batch()
      guestIds.forEach(id => {
        batch.update(db.collection(IABF.guestRegistrations).doc(id), { paymentStatus: 'paid', paidAt: new Date() })
      })
      return batch.commit().then(() => {
        commit('setGuestRegistrations', state.guestRegistrations.map(r =>
          guestIds.includes(r.id) ? { ...r, paymentStatus: 'paid' } : r
        ))
      })
    },
    setGuestsAddedToNext ({ commit, state }, { guestIds, paymentNum }) {
      const batch = db.batch()
      const addedAt = new Date()
      guestIds.forEach(id => {
        batch.update(db.collection(IABF.guestRegistrations).doc(id), {
          paymentStatus: 'added_to_next',
          addedToPaymentNum: paymentNum,
          addedToPaymentAt: addedAt
        })
      })
      return batch.commit().then(() => {
        commit('setGuestRegistrations', state.guestRegistrations.map(r =>
          guestIds.includes(r.id) ? { ...r, paymentStatus: 'added_to_next', addedToPaymentNum: paymentNum } : r
        ))
      })
    },
    async lookUpIABFDancer ({ commit }, payload) {
      commit('setLoading', true)
      try {
        // Try exact match first
        let snapshot = await db.collection(IABF.dancers).where('email', '==', payload.email.toLowerCase()).where('last4', '==', payload.last4).get()
        if (snapshot.empty) {
          // Fallback: case-insensitive email scan
          const allDocs = await db.collection(IABF.dancers).get()
          const match = allDocs.docs.find(doc => {
            const d = doc.data()
            return (d.email || '').toLowerCase() === payload.email.toLowerCase() && d.last4 === payload.last4
          })
          if (match) {
            await match.ref.update({ email: payload.email.toLowerCase() })
            commit('setCurrentIABFDancer', { id: match.id, firstName: match.data().firstName })
          } else {
            commit('setCurrentIABFDancer', {})
          }
        } else {
          const doc = snapshot.docs[0]
          commit('setCurrentIABFDancer', { id: doc.id, firstName: doc.data().firstName })
        }
        commit('setLoading', false)
      } catch (error) {
        commit('setLoading', false)
        throw error
      }
    },
    copyIABFerToFutureYear ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        console.log('copying ' + payload.id + ' to 2026')
        db.collection('iabf-dancers').doc(payload.id).get()
        .then(function (doc) {
          var dancer = {
            firstName: doc.data().firstName,
            lastName: doc.data().lastName,
            email: doc.data().email,
            phone: doc.data().phone,
            last4: doc.data().last4,
            foodsNotConsumed: doc.data().foodsNotConsumed || '',
            foodAllergies: doc.data().foodAllergies || '',
            coffee: doc.data().coffee || '',
            dietaryNotes: doc.data().dietaryNotes || '',
            diet: doc.data().diet || '',
            dietaryRestrictionsEntered: doc.data().dietaryRestrictionsEntered || false,
            emergencyContactName: doc.data().emergencyContactName || '',
            emergencyContactPhone: doc.data().emergencyContactPhone || '',
            emergencyContactRelationship: doc.data().emergencyContactRelationship || '',
            emergencyContactEntered: doc.data().emergencyContactEntered || false,
            travelDetailsEntered: false
          }
          db.collection(IABF.dancers).add(dancer)
          .then(function (docRef) {
            console.log('Document written with ID: ', docRef.id)
            commit('createIABFDancer', {
              ...dancer,
              id: docRef.id
            })
            resolve()
          })
          .catch(function (error) {
            reject(error)
          })
        }).catch(
          error => {
            reject(error)
          }
        )
      })
    },
    copy2025IABFerToFutureYear ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        console.log('copying ' + payload.id + ' to 2026')
        db.collection('iabf-dancers-2025').doc(payload.id).get()
        .then(function (doc) {
          var dancer = {
            firstName: doc.data().firstName,
            lastName: doc.data().lastName,
            email: doc.data().email,
            phone: doc.data().phone,
            last4: doc.data().last4,
            foodsNotConsumed: doc.data().foodsNotConsumed || '',
            foodAllergies: doc.data().foodAllergies || '',
            coffee: doc.data().coffee || '',
            dietaryNotes: doc.data().dietaryNotes || '',
            diet: doc.data().diet || '',
            dietaryRestrictionsEntered: doc.data().dietaryRestrictionsEntered || false,
            emergencyContactName: doc.data().emergencyContactName || '',
            emergencyContactPhone: doc.data().emergencyContactPhone || '',
            emergencyContactRelationship: doc.data().emergencyContactRelationship || '',
            emergencyContactEntered: doc.data().emergencyContactEntered || false,
            travelDetailsEntered: false
          }
          db.collection(IABF.dancers).add(dancer)
          .then(function (docRef) {
            console.log('Document written with ID: ', docRef.id)
            commit('createIABFDancer', {
              ...dancer,
              id: docRef.id
            })
            resolve()
          })
          .catch(function (error) {
            reject(error)
          })
        }).catch(
          error => {
            reject(error)
          }
        )
      })
    },
    async updatePieceCompetitionDivision ({ state }, { pieceId, division, useAdminYear }) {
      if (!pieceId) return
      await db.collection(piecesCol(state, useAdminYear)).doc(pieceId).update({ competition: division || 'None' })
    },
    async removeDancerFromCompetitionPiece ({ state }, { pieceId, dancerId, useAdminYear }) {
      // Pull dancer out of a piece's competitionEntrants. When she was the
      // last entrant, also flip competitionEntry off and clear the
      // division so the piece doesn't linger as an empty entry.
      if (!pieceId || !dancerId) return
      const ref = db.collection(piecesCol(state, useAdminYear)).doc(pieceId)
      const snap = await ref.get()
      if (!snap.exists) return
      const data = snap.data() || {}
      const existing = Array.isArray(data.competitionEntrants) ? data.competitionEntrants : []
      const next = existing.filter(id => id !== dancerId)
      const update = { competitionEntrants: next }
      if (next.length === 0) {
        update.competitionEntry = false
        update.competition = 'None'
      }
      await ref.update(update)
    },
    async clearCompetitionSurveyForDancer ({ state }, { dancerId, newPieceId, useAdminYear }) {
      // Clear the dancer's self-assessment survey so she can re-submit
      // it (answers like "cabriole as the challenging movement" are
      // piece-specific). Keep competitionRegistered=true so she stays
      // visible in the registered list. Optionally swap her
      // competitionPieceId to a freshly chosen piece.
      if (!dancerId) return
      const update = { competitionSurvey: null }
      if (newPieceId !== undefined) update.competitionPieceId = newPieceId || null
      await db.collection(dancersCol(state, useAdminYear)).doc(dancerId).update(update)
    },
    async addDancerToCompetitionPiece ({ state }, { pieceId, dancerId, useAdminYear }) {
      // Adds the dancer to the piece's competitionEntrants set (and flags
      // the piece as a competition entry if it isn't already). Used by the
      // admin Pick Piece flow for orphaned competition registrations.
      if (!pieceId || !dancerId) return
      const ref = db.collection(piecesCol(state, useAdminYear)).doc(pieceId)
      const snap = await ref.get()
      if (!snap.exists) return
      const existing = Array.isArray(snap.data().competitionEntrants) ? snap.data().competitionEntrants : []
      if (existing.includes(dancerId)) {
        if (snap.data().competitionEntry !== true) {
          await ref.update({ competitionEntry: true })
        }
        return
      }
      await ref.update({
        competitionEntry: true,
        competitionEntrants: [...existing, dancerId]
      })
    },
    /**
     * Save 1st/2nd/3rd + score on an operable-year competition piece, and
     * dual-write iabf-competition-results/{year} so dancer history updates.
     * Replaces podium rows for this piece; leaves special awards alone.
     */
    async saveCompetitionRowResult ({ state }, payload) {
      const pieceId = payload && payload.pieceId
      if (!pieceId) {
        return
      }
      const useAdminYear = !!(payload && payload.useAdminYear)
      const placement = String((payload && payload.placement) || '').trim()
      const score = String((payload && payload.score) || '').trim()
      const division = String((payload && payload.division) || '').trim()
      const pieceTitle = String((payload && payload.pieceTitle) || '').trim()
      const dancers = Array.isArray(payload && payload.dancers) ? payload.dancers : []
      await db.collection(piecesCol(state, useAdminYear)).doc(pieceId).update({
        competitionPlace: placement,
        competitionScore: score
      })
      const cols = useAdminYear ? adminIabf(state) : dancerIabf(state)
      const year = Number((cols && cols.year) || state.adminFestivalYear || IABF_DANCER_YEAR)
      if (!Number.isFinite(year) || year < 2000) {
        return { placement, score }
      }
      const ref = db.collection(COMPETITION_RESULTS_COLLECTION).doc(String(year))
      const snap = await ref.get()
      const existing = snap.exists && Array.isArray((snap.data() || {}).results)
        ? (snap.data() || {}).results
        : []
      const keep = existing
        .map(normalizeCompetitionResult)
        .filter(Boolean)
        .filter((r) => !shouldReplaceResultForPiece(r, {
          pieceId,
          pieceTitle,
          division,
          dancers
        }))
      const nextRows = []
      if (placement || score) {
        const notes = score ? `${score} pts` : ''
        if (!dancers.length) {
          const row = normalizeCompetitionResult({
            pieceTitle,
            division,
            placement,
            notes,
            pieceId,
            score,
            source: 'competition-row'
          })
          if (row) {
            nextRows.push(row)
          }
        } else {
          dancers.forEach((d) => {
            const row = normalizeCompetitionResult({
              email: d.email,
              firstName: d.firstName,
              lastName: d.lastName,
              pieceTitle,
              division,
              placement,
              notes,
              pieceId,
              dancerId: d.dancerId || d.id || '',
              score,
              source: 'competition-row'
            })
            if (row) {
              nextRows.push(row)
            }
          })
        }
      }
      const clean = keep.concat(nextRows)
      const prevData = snap.exists ? (snap.data() || {}) : {}
      const specialAwards = (Array.isArray(prevData.specialAwards) ? prevData.specialAwards : [])
        .map(normalizeSpecialAward)
        .filter(Boolean)
      await ref.set({
        festivalYear: year,
        results: clean,
        specialAwards,
        updatedAt: Date.now(),
        rowCount: clean.length
      })
      return { placement, score }
    },
    submitCompetitionRegistration ({ commit, getters }, { dancerId, pieceId, survey }) {
      commit('setLoading', true)
      return new Promise((resolve, reject) => {
        const fields = {
          competitionPieceId: pieceId,
          competitionRegistered: true,
          competitionSurvey: survey,
          competitionDeclined: false,
          competitionDeclinedAt: null
        }
        db.collection(IABF.dancers).doc(dancerId).update(fields)
        .then(() => {
          if (getters.currentIABFDancer && getters.currentIABFDancer.id === dancerId) {
            commit('setCurrentIABFDancer', { ...getters.currentIABFDancer, ...fields })
          }
        })
        .then(() => {
          // Get dancer and piece info for webhook
          return Promise.all([
            db.collection(IABF.dancers).doc(dancerId).get(),
            db.collection(IABF.pieces).doc(pieceId).get()
          ])
        })
        .then(([dancerDoc, pieceDoc]) => {
          var dancerData = dancerDoc.data()
          var pieceData = pieceDoc.data()
          var webhookPayload = {
            dancerName: dancerData.firstName + ' ' + dancerData.lastName,
            dancerEmail: dancerData.email,
            dancerId: dancerId,
            pieceName: pieceData.title + ' — ' + pieceData.songArtist,
            pieceType: pieceData.pieceType,
            survey: survey
          }
          return fetch('https://hooks.zapier.com/hooks/catch/2220729/uch3cs6/', {
            method: 'POST',
            body: JSON.stringify(webhookPayload)
          })
        })
        .then(() => {
          commit('setLoading', false)
          resolve()
        })
        .catch(error => {
          console.error('Error submitting competition registration:', error)
          commit('setLoading', false)
          reject(error)
        })
      })
    },
    /**
     * Dancer submits a showcase piece. Creates a Firestore piece with
     * approvalStatus: 'pending' so it appears on their dashboard immediately
     * and on the admin queue until the IABF team approves it.
     */
    async submitPieceRegistration ({ commit, getters, state }, { dancerId, piece }) {
      commit('setLoading', true)
      try {
        const cols = dancerIabf(state)
        const dancerSnap = await db.collection(cols.dancers).doc(dancerId).get()
        if (!dancerSnap.exists) {
          throw new Error('Dancer not found')
        }
        const dancerData = dancerSnap.data() || {}
        if (isSoloPiece(piece)) {
          const existingSnap = await db.collection(cols.pieces)
            .where('dancers', 'array-contains', dancerId)
            .get()
          const existing = existingSnap.docs.map((doc) => doc.data() || {})
          if (soloPieceCount(existing) >= MAX_SOLO_PIECES) {
            throw new Error(
              'You already have 3 solos. A small group is still welcome — or email hello@iabf.dance if you need to replace a solo.'
            )
          }
        }
        const programTitle = piece.programTitle ||
          `${piece.title || ''}${piece.songArtist ? ' — ' + piece.songArtist : ''}`.trim()
        const pieceDoc = {
          title: String(piece.title || '').trim(),
          songArtist: String(piece.songArtist || '').trim(),
          pieceType: piece.pieceType || '',
          groupSize: piece.groupSize || '',
          groupMemberNames: piece.groupMemberNames || '',
          programDancer: piece.programDancer || '',
          programTeacher: piece.programTeacher || '',
          programTitle,
          dancers: [dancerId],
          namedDancers: piece.programDancer ||
            `${dancerData.firstName || ''} ${dancerData.lastName || ''}`.trim(),
          musicReceived: false,
          lengthInSeconds: 0,
          competition: 'None',
          approvalStatus: 'pending',
          submittedByDancerId: dancerId,
          submissionNotes: piece.notes || '',
          submittedAt: new Date(),
          createdAt: new Date()
        }
        const ref = await db.collection(cols.pieces).add(pieceDoc)
        // Submitting a piece means they are performing — clear opt-out / stale all-set
        if (dancerData.showcasePrepStatus === 'not_performing' ||
            dancerData.showcasePrepStatus === 'all_set') {
          try {
            await db.collection(cols.dancers).doc(dancerId).update({
              showcasePrepStatus: null,
              showcasePrepStatusAt: null,
              updatedAt: Date.now()
            })
            if (getters.currentIABFDancer && getters.currentIABFDancer.id === dancerId) {
              commit('setCurrentIABFDancer', {
                ...getters.currentIABFDancer,
                showcasePrepStatus: null,
                showcasePrepStatusAt: null
              })
            }
          } catch (e) {
            console.warn('Could not clear showcasePrepStatus after piece submit:', e)
          }
        }
        // Notify admin (existing Zapier path)
        try {
          await fetch('https://hooks.zapier.com/hooks/catch/2220729/uctgi1w/', {
            method: 'POST',
            body: JSON.stringify({
              changeType: 'Piece submitted — pending approval',
              dancerName: `${dancerData.firstName || ''} ${dancerData.lastName || ''}`.trim(),
              dancerEmail: dancerData.email || '',
              dancerId,
              pieceId: ref.id,
              title: pieceDoc.title,
              songArtist: pieceDoc.songArtist,
              pieceType: pieceDoc.pieceType,
              groupSize: pieceDoc.groupSize,
              groupMemberNames: pieceDoc.groupMemberNames,
              programDancer: pieceDoc.programDancer,
              programTeacher: pieceDoc.programTeacher,
              notes: pieceDoc.submissionNotes,
              approvalStatus: 'pending'
            })
          })
        } catch (e) {
          console.warn('Piece submit webhook failed:', e)
        }
        commit('setLoading', false)
        return { id: ref.id, ...pieceDoc }
      } catch (error) {
        console.error('Error submitting piece registration:', error)
        commit('setLoading', false)
        throw error
      }
    },
    /** Admin: mark a dancer-submitted piece as approved for the public lineup. */
    async approveShowcasePiece ({ state }, { pieceId }) {
      if (!pieceId) {
        throw new Error('Missing piece id')
      }
      const cols = adminIabf(state)
      await db.collection(cols.pieces).doc(pieceId).update({
        approvalStatus: 'approved',
        approvedAt: new Date()
      })
    },
    async updateCompetitionSurvey (_ctx, { dancerId, survey }) {
      // For dancers whose admin-cleared survey needs to be redone. Writes
      // just the survey; piece + registered flag stay as-is. No webhook so
      // it doesn't look like a fresh registration to the admin side.
      if (!dancerId) return
      await db.collection(IABF.dancers).doc(dancerId).update({
        competitionSurvey: survey || null
      })
    },
    /**
     * Change which piece competes. Clears the self-assessment survey so the
     * dancer must re-submit answers for the new piece.
     */
    async updateCompetitionPiece ({ commit, getters, dispatch }, { dancerId, pieceId }) {
      if (!dancerId || !pieceId) {
        throw new Error('Missing dancer or piece.')
      }
      commit('setLoading', true)
      try {
        const dancerRef = db.collection(IABF.dancers).doc(dancerId)
        const dancerSnap = await dancerRef.get()
        if (!dancerSnap.exists) {
          throw new Error('Dancer not found')
        }
        const prev = dancerSnap.data() || {}
        const oldPieceId = prev.competitionPieceId || null
        const fields = {
          competitionPieceId: pieceId,
          competitionRegistered: true,
          competitionSurvey: null,
          competitionDeclined: false,
          competitionDeclinedAt: null,
          updatedAt: Date.now()
        }
        await dancerRef.update(fields)
        if (getters.currentIABFDancer && getters.currentIABFDancer.id === dancerId) {
          commit('setCurrentIABFDancer', { ...getters.currentIABFDancer, ...fields })
        }
        // Keep piece-level entrant lists in sync when possible
        if (oldPieceId && oldPieceId !== pieceId) {
          try {
            await dispatch('removeDancerFromCompetitionPiece', { pieceId: oldPieceId, dancerId })
          } catch (e) {
            console.warn('removeDancerFromCompetitionPiece failed', e)
          }
        }
        try {
          await dispatch('addDancerToCompetitionPiece', { pieceId, dancerId })
        } catch (e) {
          console.warn('addDancerToCompetitionPiece failed', e)
        }
        try {
          const pieceDoc = await db.collection(IABF.pieces).doc(pieceId).get()
          const pieceData = pieceDoc.exists ? (pieceDoc.data() || {}) : {}
          await fetch('https://hooks.zapier.com/hooks/catch/2220729/uch3cs6/', {
            method: 'POST',
            body: JSON.stringify({
              dancerName: `${prev.firstName || ''} ${prev.lastName || ''}`.trim(),
              dancerEmail: prev.email || '',
              dancerId,
              pieceName: `${pieceData.title || ''} — ${pieceData.songArtist || ''}`.trim(),
              pieceType: pieceData.pieceType || '',
              changeType: 'Competition piece changed — survey cleared'
            })
          })
        } catch (e) {
          console.warn('Competition piece change webhook failed', e)
        }
        commit('setLoading', false)
      } catch (error) {
        console.error('Error updating competition piece:', error)
        commit('setLoading', false)
        throw error
      }
    },
    loadIABFPieces ({ commit, state }, payload) {
      return new Promise((resolve, reject) => {
        commit('setLoading', true)
        const colName = piecesCol(state, !!(payload && payload.useAdminYear))
        db.collection(colName).get().then((querySnapshot) => {
          var pieces = []
          querySnapshot.forEach((doc) => {
            const data = doc.data() || {}
            if (data.canceledByDancer) {
              return
            }
            pieces.push({
              title: data.title,
              songArtist: data.songArtist,
              programDancer: data.programDancer,
              programTeacher: data.programTeacher,
              programTitle: data.programTitle,
              musicReceived: data.musicReceived,
              lengthInSeconds: data.lengthInSeconds,
              pieceType: data.pieceType,
              groupSize: data.groupSize,
              dancers: data.dancers,
              competition: data.competition,
              competitionEntry: data.competitionEntry || false,
              competitionEntrants: data.competitionEntrants || [],
              competitionPlace: data.competitionPlace || '',
              competitionScore: data.competitionScore || '',
              showcaseBlock: data.showcaseBlock || null,
              showcasePlacement: data.showcasePlacement || null,
              showcaseOrder: data.showcaseOrder || null,
              namedDancers: data.namedDancers || '',
              musicUrl: data.musicUrl || '',
              musicFileName: data.musicFileName || '',
              performanceRecordingUrl: data.performanceRecordingUrl || '',
              lightingSoundNotes: data.lightingSoundNotes || '',
              startPosition: data.startPosition || '',
              meaningToMe: data.meaningToMe || '',
              approvalStatus: data.approvalStatus || 'approved',
              submissionNotes: data.submissionNotes || '',
              submittedAt: data.submittedAt || null,
              createdAt: data.createdAt || null,
              submittedByDancerId: data.submittedByDancerId || null,
              id: doc.id
            })
          })
          commit('setLoadedIABFPieces', pieces)
          commit('setLoading', false)
          resolve()
        }).catch(
          error => {
            reject(error)
          }
        )
      })
    },
    async setPieceNamedDancers (_ctx, { pieceId, namedDancers }) {
      if (!pieceId) return
      await db.collection(IABF.pieces).doc(pieceId).update({ namedDancers: namedDancers || '' })
    },
    async setPieceMusic (_ctx, { pieceId, musicUrl, musicFileName }) {
      if (!pieceId) return
      const payload = {}
      if (musicUrl !== undefined) payload.musicUrl = musicUrl || ''
      if (musicFileName !== undefined) payload.musicFileName = musicFileName || ''
      await db.collection(IABF.pieces).doc(pieceId).update(payload)
    },
    async setPiecePerformanceRecording ({ commit, state }, { pieceId, url, useAdminYear }) {
      if (!pieceId) return
      const clean = String(url || '').trim()
      const colName = piecesCol(state, !!useAdminYear)
      await db.collection(colName).doc(pieceId).update({ performanceRecordingUrl: clean })
      commit('patchLoadedIABFPiece', { id: pieceId, fields: { performanceRecordingUrl: clean } })
    },
    // Corps pieces (Alice in Wonderland, Becoming, Waltz of the Hours,
    // Le Jazz Hot, Garland Waltz) aren't rows in iabf-pieces-2026 — they
    // live as casting flags on each dancer. So their performance video
    // URLs are stored separately, keyed by the corps "field" name
    // (matches dancer.<field>.participating).
    // Admin pieces/show-runner pass { useAdminYear: true } so 2026 wrap-up
    // recordings land on iabf-config-2026 (what festival history reads).
    // Dancer dashboard omits the flag and uses the enrollment year.
    subscribeCorpsRecordings ({ commit, state }, payload) {
      const useAdminYear = !!(payload && payload.useAdminYear)
      const configCol = useAdminYear ? adminIabf(state).config : dancerIabf(state).config
      const unsub = db.collection(configCol).doc('performance-recordings').onSnapshot(doc => {
        const data = doc.exists ? (doc.data() || {}) : {}
        commit('setCorpsRecordings', data.corps || {})
        commit('setCorpsNotes', data.notes || {})
        commit('setCorpsStartPositions', data.startPositions || {})
      })
      return unsub
    },
    async setCorpsRecording ({ state }, { field, url, useAdminYear }) {
      if (!field) return
      const configCol = useAdminYear ? adminIabf(state).config : dancerIabf(state).config
      const ref = db.collection(configCol).doc('performance-recordings')
      const clean = String(url || '').trim()
      // Merge so each call only touches its own key.
      await ref.set({ corps: { [field]: clean } }, { merge: true })
    },

    /**
     * Load all competition results for a festival year (admin + history).
     * Stored as iabf-competition-results/{year} → { results: [...] }.
     */
    async loadCompetitionResultsForYear (_ctx, year) {
      const y = Number(year)
      if (!Number.isFinite(y) || y < 2000) {
        return []
      }
      try {
        const doc = await db.collection(COMPETITION_RESULTS_COLLECTION).doc(String(y)).get()
        if (!doc.exists) {
          return []
        }
        const data = doc.data() || {}
        const list = Array.isArray(data.results) ? data.results : []
        return list.map(normalizeCompetitionResult).filter(Boolean)
      } catch (e) {
        console.warn('loadCompetitionResultsForYear failed', y, e)
        return []
      }
    },

    async loadCompetitionYearDoc (_ctx, year) {
      const y = Number(year)
      if (!Number.isFinite(y) || y < 2000) {
        return { results: [], specialAwards: [] }
      }
      try {
        const doc = await db.collection(COMPETITION_RESULTS_COLLECTION).doc(String(y)).get()
        if (!doc.exists) {
          return { results: [], specialAwards: [] }
        }
        const data = doc.data() || {}
        const results = (Array.isArray(data.results) ? data.results : [])
          .map(normalizeCompetitionResult)
          .filter(Boolean)
        const specialAwards = (Array.isArray(data.specialAwards) ? data.specialAwards : [])
          .map(normalizeSpecialAward)
          .filter(Boolean)
        return { results, specialAwards }
      } catch (e) {
        console.warn('loadCompetitionYearDoc failed', y, e)
        return { results: [], specialAwards: [] }
      }
    },

    /**
     * Replace competition results for a year (CSV import).
     * @param {{ year: number, results: object[] }} payload
     */
    async saveCompetitionResultsForYear (_ctx, { year, results }) {
      const y = Number(year)
      if (!Number.isFinite(y) || y < 2000) {
        throw new Error('Invalid festival year')
      }
      const ref = db.collection(COMPETITION_RESULTS_COLLECTION).doc(String(y))
      const snap = await ref.get()
      const prev = snap.exists ? (snap.data() || {}) : {}
      const clean = (Array.isArray(results) ? results : [])
        .map(normalizeCompetitionResult)
        .filter(Boolean)
      const specialAwards = (Array.isArray(prev.specialAwards) ? prev.specialAwards : [])
        .map(normalizeSpecialAward)
        .filter(Boolean)
      await ref.set({
        festivalYear: y,
        results: clean,
        specialAwards,
        updatedAt: Date.now(),
        rowCount: clean.length
      })
      return clean
    },

    /**
     * Save Judges' Choice / named special awards for a year.
     * Dual-writes dancer history rows (one per entrant on the winning piece).
     * @param {{ year: number, awards: object[] }} payload
     */
    async saveCompetitionSpecialAwards (_ctx, { year, awards }) {
      const y = Number(year)
      if (!Number.isFinite(y) || y < 2000) {
        throw new Error('Invalid festival year')
      }
      const cleanAwards = (Array.isArray(awards) ? awards : [])
        .map(normalizeSpecialAward)
        .filter(Boolean)
      const ref = db.collection(COMPETITION_RESULTS_COLLECTION).doc(String(y))
      const snap = await ref.get()
      const prev = snap.exists ? (snap.data() || {}) : {}
      const existing = Array.isArray(prev.results) ? prev.results : []
      const keep = existing
        .map(normalizeCompetitionResult)
        .filter(Boolean)
        .filter((r) => !isNamedSpecialAwardResult(r))
      const nextRows = []
      for (const award of (Array.isArray(awards) ? awards : [])) {
        const name = String(award.name || '').trim()
        const pieceId = String(award.pieceId || '').trim()
        if (!name || !pieceId) {
          continue
        }
        const dancers = Array.isArray(award.dancers) ? award.dancers : []
        const pieceTitle = String(award.pieceTitle || '').trim()
        const division = 'Special Award'
        const people = dancers.length
          ? dancers
          : [{ firstName: '', lastName: '', email: '', dancerId: '' }]
        people.forEach((d) => {
          const row = normalizeCompetitionResult({
            email: d.email,
            firstName: d.firstName,
            lastName: d.lastName,
            pieceTitle,
            division,
            placement: name,
            notes: '',
            pieceId,
            dancerId: d.dancerId || d.id || '',
            source: 'special-award'
          })
          if (row) {
            nextRows.push(row)
          }
        })
      }
      const clean = keep.concat(nextRows)
      await ref.set({
        festivalYear: y,
        results: clean,
        specialAwards: cleanAwards,
        updatedAt: Date.now(),
        rowCount: clean.length
      })
      return { specialAwards: cleanAwards, results: clean }
    },

    /**
     * Load showcase performance archive for a year
     * (iabf-showcase-history/{year} → { pieces: [...] }).
     */
    async loadShowcaseHistoryForYear (_ctx, year) {
      const y = Number(year)
      if (!Number.isFinite(y) || y < 2000) {
        return []
      }
      try {
        const doc = await db.collection(SHOWCASE_HISTORY_COLLECTION).doc(String(y)).get()
        if (!doc.exists) {
          return []
        }
        const data = doc.data() || {}
        const list = Array.isArray(data.pieces) ? data.pieces : []
        return list.map((p, i) => ({
          id: p.id || `${y}-${i + 1}`,
          title: String(p.title || '').trim(),
          dancerNames: Array.isArray(p.dancerNames) ? p.dancerNames : [],
          dancerNamesRaw: String(p.dancerNamesRaw || ''),
          dancerPersonIds: Array.isArray(p.dancerPersonIds) ? p.dancerPersonIds.filter(Boolean) : [],
          recordingUrl: String(p.recordingUrl || '').trim()
        })).filter(p => p.title)
      } catch (e) {
        console.warn('loadShowcaseHistoryForYear failed', y, e)
        return []
      }
    },

    /**
     * Replace showcase history pieces for a year (admin editor).
     * @param {{ year: number, pieces: object[] }} payload
     */
    async saveShowcaseHistoryForYear (_ctx, { year, pieces }) {
      const y = Number(year)
      if (!Number.isFinite(y) || y < 2000) {
        throw new Error('Invalid festival year')
      }
      const clean = (Array.isArray(pieces) ? pieces : []).map((p, i) => {
        const title = String((p && p.title) || '').trim()
        if (!title) {
          return null
        }
        const dancerPersonIds = []
        const seen = {}
        ;(Array.isArray(p.dancerPersonIds) ? p.dancerPersonIds : []).forEach(id => {
          const pid = String(id || '').trim()
          if (!pid || seen[pid]) {
            return
          }
          seen[pid] = true
          dancerPersonIds.push(pid)
        })
        const dancerNames = (Array.isArray(p.dancerNames) ? p.dancerNames : [])
          .map(s => String(s || '').trim())
          .filter(Boolean)
        return {
          id: (p && p.id) || `${y}-${i + 1}`,
          title,
          dancerNames,
          dancerNamesRaw: dancerNames.join(', '),
          dancerPersonIds,
          recordingUrl: String((p && p.recordingUrl) || '').trim()
        }
      }).filter(Boolean)
      const withUrl = clean.filter(p => p.recordingUrl).length
      await db.collection(SHOWCASE_HISTORY_COLLECTION).doc(String(y)).set({
        festivalYear: y,
        pieces: clean,
        updatedAt: Date.now(),
        source: 'admin-editor',
        pieceCount: clean.length,
        recordingCount: withUrl
      })
      return { pieces: clean }
    },

    async loadIabfPeopleDirectory () {
      const snap = await db.collection(IABF_PEOPLE_COLLECTION).get()
      const people = []
      snap.forEach(doc => {
        const d = doc.data() || {}
        if (d.mergedInto) {
          return
        }
        const email = String(d.email || '').toLowerCase()
        if (email.indexOf('merged+') === 0 || email.indexOf('@iabf.invalid') !== -1) {
          return
        }
        if (email.indexOf('+') !== -1) {
          return
        }
        const firstName = String(d.firstName || '').trim()
        const lastName = String(d.lastName || '').trim()
        const label = [firstName, lastName].filter(Boolean).join(' ')
        if (!label) {
          return
        }
        people.push({
          id: doc.id,
          firstName,
          lastName,
          email: String(d.email || '').trim(),
          label
        })
      })
      people.sort((a, b) => a.label.localeCompare(b.label))
      return people
    },

    /**
     * Multi-year festival history for the logged-in dancer (or payload identity).
     * One panel per year: showcase pieces (+ recordings) and competition awards.
     *
     * @param {{ email?: string, firstName?: string, lastName?: string, yearsAttended?: number[], enrollmentYear?: number, personId?: string } | undefined} payload
     * @returns {Promise<{ years: Array, identity: object }>}
     */
    async loadDancerFestivalHistory ({ getters, commit, state }, payload = {}) {
      const dancer = getters.currentIABFDancer || {}
      const person = getters.currentIABFPerson || {}
      const masquerading = !!state.masqueradeDancerId
      const email = normalizeEmail(
        payload.email ||
        dancer.email ||
        (!masquerading && getters.user && getters.user.email) ||
        ''
      )
      const uid = dancer.uid ||
        (!masquerading && getters.user && getters.user.id) ||
        ''
      const firstName = payload.firstName || dancer.firstName || (!masquerading && person.firstName) || ''
      const lastName = payload.lastName || dancer.lastName || (!masquerading && person.lastName) || ''
      const yearsAttended = payload.yearsAttended ||
        dancer.yearsAttended ||
        (!masquerading && person.yearsAttended) ||
        []
      // Only force-include a year when there is a real active enrollment in session.
      // (Do not use iabfDancerYear alone — that always injects "current" even after delete.)
      const hasLiveEnrollment = !!(dancer.id && !dancer.isCanceledAccount)
      const enrollmentYear = hasLiveEnrollment
        ? (payload.enrollmentYear || dancer.festivalYear || getters.iabfDancerYear || null)
        : (payload.enrollmentYear || null)
      const personId = payload.personId || dancer.personId || (!masquerading && person.id) || null
      const related = await relatedHistoryPersonIds({ personId, uid, email })
      const personIds = related.personIds
      const years = yearsForFestivalHistory({
        yearsAttended: [].concat(yearsAttended, related.yearsAttended),
        enrollmentYear
      })
      const identity = { email, firstName, lastName, personId, personIds }
      // 2022–2023 have no enrollment collection; yearsAttended is often incomplete
      // after email-linking. Still load archive years and keep them if content matches.
      const yearsToLoad = Array.from(new Set(years.concat(ARCHIVE_FESTIVAL_YEARS)))
        .sort((a, b) => b - a)

      const yearPanels = await Promise.all(yearsToLoad.map(async (year) => {
        const y = Number(year)
        let enrollmentId = null
        let pieces = []
        const pieceByKey = new Map()

        const addKeyed = (map, row, titleField) => {
          const title = row[titleField] || row.title || row.name
          if (!row || !title) {
            return
          }
          const key = pieceTitleKey(title)
          if (!key) {
            return
          }
          const existingKey = findCollapsibleTitleKey(map, key) || key
          const prev = map.get(existingKey)
          if (prev) {
            map.set(existingKey, mergeHistoryPieceRow(prev, { ...row, title }))
            return
          }
          map.set(key, { ...row, title })
        }
        const addPiece = (row) => addKeyed(pieceByKey, row, 'title')
        const corpsByKey = new Map()
        const addCorps = (row) => addKeyed(corpsByKey, {
          ...row,
          name: row.name || row.title,
          title: row.name || row.title
        }, 'name')

        const dancersCol = dancersCollectionName(y)
        const historyPersonId = payload.personId || person.id || dancer.personId || null
        let enrollmentData = null
        if (dancersCol) {
          const enrollmentDoc = await lookupHistoryEnrollment(dancersCol, {
            uid,
            email,
            rawEmail: String(payload.email || dancer.email || '').trim(),
            personId: historyPersonId,
            personIds
          })
          if (enrollmentDoc) {
            enrollmentId = enrollmentDoc.id
            enrollmentData = enrollmentDoc.data() || {}
          }
        }
        if (enrollmentId) {
          try {
            const pieceSnap = await db.collection(piecesCollectionName(y))
              .where('dancers', 'array-contains', enrollmentId)
              .get()
            pieceSnap.docs.forEach(doc => {
              const d = doc.data() || {}
              addPiece({
                id: doc.id,
                title: d.title || '(untitled)',
                pieceType: d.pieceType || 'Showcase piece',
                songArtist: d.songArtist || '',
                competition: d.competition || '',
                performanceRecordingUrl: (d.performanceRecordingUrl || '').trim(),
                source: 'enrollment'
              })
            })
          } catch (e) {
            console.warn('history pieces load failed', y, e)
          }
        }

        // Archive import (2022–2025 bulk history) — match by full dancer name.
        try {
          const histDoc = await db.collection(SHOWCASE_HISTORY_COLLECTION).doc(String(y)).get()
          if (histDoc.exists) {
            const hist = histDoc.data() || {}
            const histPieces = Array.isArray(hist.pieces) ? hist.pieces : []
            histPieces.forEach((hp, idx) => {
              if (!showcaseHistoryPieceMatchesDancer(hp, identity)) {
                return
              }
              const row = {
                id: hp.id || `hist-${y}-${idx}`,
                title: hp.title || '(untitled)',
                name: hp.title || '(untitled)',
                pieceType: 'Showcase piece',
                songArtist: '',
                competition: '',
                performanceRecordingUrl: (hp.recordingUrl || '').trim(),
                source: 'showcase-history'
              }
              if (isArchiveGroupPiece(hp) && !findCollapsibleTitleKey(pieceByKey, pieceTitleKey(row.title))) {
                addCorps(row)
              } else {
                addPiece(row)
              }
            })
          }
        } catch (e) {
          console.warn('history showcase archive load failed', y, e)
        }

        pieces = Array.from(pieceByKey.values())

        let corpsRecordings = {}
        try {
          const cols = iabfForYear(y)
          if (cols && cols.config) {
            const recDoc = await db.collection(cols.config).doc('performance-recordings').get()
            if (recDoc.exists) {
              corpsRecordings = (recDoc.data() || {}).corps || {}
            }
          }
        } catch (e) {
          console.warn('history corps recordings load failed', y, e)
        }
        corpsPiecesFromEnrollment(enrollmentData, corpsRecordings).forEach(addCorps)
        const corpsPieces = Array.from(corpsByKey.values())
          .filter(row => !findCollapsibleTitleKey(pieceByKey, pieceTitleKey(row.name || row.title)))
          .map(row => ({
            id: row.id,
            name: row.name || row.title,
            field: row.field || '',
            role: row.role || '',
            performanceRecordingUrl: row.performanceRecordingUrl || ''
          }))

        let competitionAll = []
        try {
          const doc = await db.collection(COMPETITION_RESULTS_COLLECTION).doc(String(y)).get()
          if (doc.exists) {
            const data = doc.data() || {}
            competitionAll = (Array.isArray(data.results) ? data.results : [])
              .map(normalizeCompetitionResult)
              .filter(Boolean)
          }
        } catch (e) {
          console.warn('history competition load failed', y, e)
        }
        const competition = dedupeCompetitionResults(
          competitionAll.filter(r => competitionResultMatchesDancer(r, identity))
        )

        const hasContent = pieces.length > 0 || competition.length > 0 || corpsPieces.length > 0
        return {
          year: y,
          enrollmentId,
          // True only when this year's enrollment doc still exists
          isRegistered: !!enrollmentId,
          pieces,
          corpsPieces,
          competition,
          isCurrentYear: y === IABF_DANCER_YEAR,
          hasContent
        }
      }))

      // Only show years we can prove: a dancer/faculty enrollment, or
      // showcase/corps/competition content. Empty 2022 rows were appearing
      // from yearsAttended stubs and counting as an extra IABF year.
      // Display only — do not rewrite yearsAttended from matched pieces.
      const filtered = yearPanels.filter(p => !!(p.hasContent || p.enrollmentId))
      return { years: filtered, identity }
    },
    async recalculatePersonAttendanceYears ({ commit, getters }, payload = {}) {
      let person = payload.person || getters.currentIABFPerson
      const dancer = payload.dancer || getters.currentIABFDancer || {}
      if ((!person || !person.id) && payload.personId) {
        person = await loadPersonById(db, payload.personId)
      }
      if ((!person || !person.id) && (dancer.email || payload.email)) {
        person = await findPersonByEmail(db, dancer.email || payload.email)
      }
      if (!person || !person.id) {
        throw new Error('No person record to recalculate.')
      }
      return recalculateAttendanceYearsForPerson(commit, person, dancer)
    },

    async publishCorpsCasts (_ctx, { corpsCasts }) {
      await db.collection(IABF.config).doc('showcase-runtime').set({ corpsCasts }, { merge: true })
    },
    async loadShowcaseConfig (_ctx) {
      const doc = await db.collection(IABF.config).doc('showcase-runtime').get()
      return doc.exists ? (doc.data() || {}) : {}
    },
    async setCorpsSongField (_ctx, { name, field, value }) {
      if (!name || !field) return
      const ref = db.collection(IABF.config).doc('showcase-runtime')
      await ref.set({ corpsSongs: { [name]: { [field]: value || '' } } }, { merge: true })
    },
    async setCorpsMusicTracks (_ctx, { name, tracks }) {
      // tracks is an ordered array; replaces the whole list for this corps.
      if (!name) return
      const ref = db.collection(IABF.config).doc('showcase-runtime')
      await ref.set({
        corpsMusic: { [name]: { tracks: tracks || [] } }
      }, { merge: true })
    },
    async setCurrentSlot (_ctx, { slotKey, intermissionStartedAt }) {
      const ref = db.collection(IABF.config).doc('showcase-runtime')
      const payload = { currentSlotKey: slotKey || null }
      if (intermissionStartedAt !== undefined) {
        payload.intermissionStartedAt = intermissionStartedAt
      }
      await ref.set(payload, { merge: true })
    },
    async freezeShowcaseOrder ({ commit }, { blocksOfPieces }) {
      // blocksOfPieces is { [blockKey]: [piece, piece, …] } already in the
      // currently-rendered order. For each piece (except those pinned as
      // 'last'), write back a numeric showcaseOrder matching its current
      // position so the dynamic auto-ordering no longer reshuffles things.
      // 'last' pieces are left alone so they stay anchored to the end even
      // if a new piece gets added to the block later.
      const batch = db.batch()
      const updatedLocal = []
      for (const block of Object.keys(blocksOfPieces || {})) {
        const ordered = blocksOfPieces[block] || []
        let pos = 0
        for (const p of ordered) {
          if (p.showcaseOrder === 'last') continue
          pos += 1
          if (p.showcaseOrder !== pos) {
            batch.update(db.collection(IABF.pieces).doc(p.id), { showcaseOrder: pos })
            updatedLocal.push({ id: p.id, value: pos })
          }
        }
      }
      batch.set(db.collection(IABF.config).doc('showcase-runtime'), {
        orderFrozenAt: Date.now()
      }, { merge: true })
      await batch.commit()
      // Mirror to the in-memory cache so the UI doesn't have to refetch.
      for (const u of updatedLocal) {
        commit('patchLoadedIABFPiece', { id: u.id, fields: { showcaseOrder: u.value } })
      }
    },
    async unfreezeShowcaseOrder ({ commit }) {
      // Clear every piece's numeric showcaseOrder so autoOrder takes back
      // over. Pieces tagged 'last' stay tagged.
      const snap = await db.collection(IABF.pieces).get()
      const batch = db.batch()
      const cleared = []
      snap.docs.forEach(doc => {
        const v = doc.data().showcaseOrder
        if (typeof v === 'number') {
          batch.update(doc.ref, { showcaseOrder: null })
          cleared.push(doc.id)
        }
      })
      batch.set(db.collection(IABF.config).doc('showcase-runtime'), {
        orderFrozenAt: null
      }, { merge: true })
      await batch.commit()
      for (const id of cleared) {
        commit('updateLoadedIABFPiece', { id, showcaseOrder: null })
      }
    },
    async updatePieceShowcaseAssignment (_ctx, { pieceId, showcaseBlock, showcasePlacement, showcaseOrder }) {
      if (!pieceId) return
      const payload = {}
      if (showcaseBlock !== undefined) payload.showcaseBlock = showcaseBlock || null
      if (showcasePlacement !== undefined) payload.showcasePlacement = showcasePlacement || null
      if (showcaseOrder !== undefined) {
        if (showcaseOrder === null || showcaseOrder === '') {
          payload.showcaseOrder = null
        } else if (typeof showcaseOrder === 'string' && showcaseOrder.trim().toLowerCase() === 'last') {
          payload.showcaseOrder = 'last'
        } else {
          const n = Number(showcaseOrder)
          payload.showcaseOrder = (Number.isFinite(n) && n >= 1) ? n : null
        }
      }
      await db.collection(IABF.pieces).doc(pieceId).update(payload)
    },
    generateClassesForDate ({ commit }, payload) {
      console.log('generating classes for ' + payload.dayOfWeek + ' on date ' + payload.date.toDate())
      const spring2026 = {
        'Monday': [
          {
            'title': 'Private Lessons with Julie Available',
            'start-time': '11:45',
            'end-time': '12:45',
            'description': 'Looking for personalized feedback, help with your progress, or a custom-tailored class? This is your time to work 1:1 with Julie and get exactly what you need to grow. Important: If you\'ve already booked a private lesson for this time, do not use the Zoom link here. Please use the private link sent directly to your inbox.',
            'link': 'https://brocheballet-scheduling.as.me/schedule/8217481a',
            'link-text': 'Book here'
          },
          {
            'title': 'All-level Ballet Barre',
            'start-time': '13:00',
            'end-time': '13:45',
            'description': 'Kick off your week with a ballet barre for all dancers. Simple combinations, limited demi pointe, and great music.'
          },
          {
            'title': 'Seated Pre-pointe - bring a Theraband or Resistance Band',
            'start-time': '13:50',
            'end-time': '14:00',
            'description': 'Whether you\'re preparing to go en pointe, building stronger feet, or warming up before pointe class, this focused session is for you. Join us to wake up your toes, activate your intrinsic foot muscles, and build the strength and control that support a healthy ballet practice.'
          },
          {
            'title': 'Pointe Strength Warmup + Artistry en Pointe: Fun & Beautiful Combos for Strength & Grace',
            'start-time': '14:05',
            'end-time': '13:35',
            'description': 'A beautiful & musical class. We\'ll begin with a gentle warm-up, then move into fun and artistic combinations with two hands for support. We\'ll close with a beautiful, expressive adagio that invites grace and artistry into your movement.'
          },
          {
            'title': '16-week Fall 2025 Beginner Pointe Cohort',
            'start-time': '18:30',
            'end-time': '19:15',
            'description': 'Private closed group for dancers approved to go en pointe. Please do not use this Zoom link. Use the link in our private room.'
          }
        ],
        'Tuesday': [
          {
            'title': 'Private Lessons with Julie Available',
            'start-time': '16:30',
            'end-time': '17:30',
            'description': 'Looking for personalized feedback, help with your progress, or a custom-tailored class? This is your time to work 1:1 with Julie and get exactly what you need to grow. Important: If you’ve already booked a private lesson for this time, do not use the Zoom link here. Please use the private link sent directly to your inbox.',
            'link': 'https://brocheballet-scheduling.as.me/schedule/8217481a',
            'link-text': 'Book here'
          },
          {
            'title': 'Seated Pre-pointe - bring a Theraband or Resistance Band',
            'start-time': '17:45',
            'end-time': '18:00',
            'description': 'Whether you\'re preparing to go en pointe, building stronger feet, or warming up before pointe class, this focused session is for you. Join us to wake up your toes, activate your intrinsic foot muscles, and build the strength and control that support a healthy ballet practice.'
          },
          {
            'title': 'Pointe Conditioning, Strength and Technique at the Barre',
            'start-time': '18:05',
            'end-time': '18:35',
            'description': '2 Hands on the Barre Pointe strengthening exercises and combinations. Work towards strength, stamina and speed in your pointe shoes.'
          },
          {
            'title': 'Slow & Gentle Barre - No Demi Pointe (Also great for pointe shoes)',
            'start-time': '18:40',
            'end-time': '19:15',
            'description': 'Gentle, relaxing and enjoyable barre with beautiful, slow combinations. No demi pointe.'
          }
        ],
        'Wednesday': [
          {
            'title': 'Private Lessons with Julie Available',
            'start-time': '12:00',
            'end-time': '12:45',
            'description': 'Looking for personalized feedback, help with your progress, or a custom-tailored class? This is your time to work 1:1 with Julie and get exactly what you need to grow. Important: If you\'ve already booked a private lesson for this time, do not use the Zoom link here. Please use the private link sent directly to your inbox.',
            'link': 'https://brocheballet-scheduling.as.me/schedule/8217481a',
            'link-text': 'Book here'
          },
          {
            'title': 'Technique Barre - Dive into Details for Posture, Turnout, and Alignment',
            'start-time': '13:05',
            'end-time': '13:55',
            'description': 'Super detailed technical breakdowns. Very slow and simple combinations designed to build up a single technical concept each week.'
          },
          {
            'title': 'Section 1 of Waltz of the Hours - Coppelia (Level is adv. beginner if on flat, intermediate if en pointe)',
            'start-time': '14:00',
            'end-time': '14:35',
            'description': 'Let\'s learn and refine the variation Waltz of the Hours from Coppelia: https://www.youtube.com/watch?v=in15bw0fD7Y'
          },
          {
            'title': 'Private Lessons with Julie Available',
            'start-time': '17:00',
            'end-time': '17:45',
            'description': 'Looking for personalized feedback, help with your progress, or a custom-tailored class? This is your time to work 1:1 with Julie and get exactly what you need to grow. Important: If you\'ve already booked a private lesson for this time, do not use the Zoom link here. Please use the private link sent directly to your inbox.',
            'link': 'https://brocheballet-scheduling.as.me/schedule/8217481a',
            'link-text': 'Book here'
          },
          {
            'title': 'Just Pliés and Tendus',
            'start-time': '18:00',
            'end-time': '18:25',
            'description': 'Enjoy a little ballet in your evening with 1 Plié combination and 3 Tendu combinations.'
          },

          {
            'title': 'Short Follow-along Pointe Warmup',
            'start-time': '18:30',
            'end-time': '18:40',
            'description': 'A quick pointe warmup for if you\'re doing the variations class en pointe'
          },
          {
            'title': 'Section 1 of Waltz of the Hours - Coppelia (Level is adv. beginner if on flat, intermediate if en pointe)',
            'start-time': '18:40',
            'end-time': '19:15',
            'description': 'Let\'s learn and refine the variation Waltz of the Hours from Coppelia: https://www.youtube.com/watch?v=in15bw0fD7Y'
          }
        ],
        'Thursday': [
          {
            'title': 'Private Lessons with Julie Available',
            'start-time': '12:00',
            'end-time': '12:45',
            'description': 'Looking for personalized feedback, help with your progress, or a custom-tailored class? This is your time to work 1:1 with Julie and get exactly what you need to grow. Important: If you\'ve already booked a private lesson for this time, do not use the Zoom link here. Please use the private link sent directly to your inbox.',
            'link': 'https://brocheballet-scheduling.as.me/schedule/8217481a',
            'link-text': 'Book here'
          },
          {
            'title': '16-week Fall 2025 Beginner Pointe Cohort',
            'start-time': '13:00',
            'end-time': '13:45',
            'description': 'Private closed group for dancers approved to go en pointe. Please do not use this Zoom link. Use the link in our private room.'
          },
          {
            'title': 'Private Lessons with Julie Available',
            'start-time': '16:45',
            'end-time': '17:30',
            'description': 'Looking for personalized feedback, help with your progress, or a custom-tailored class? This is your time to work 1:1 with Julie and get exactly what you need to grow. Important: If you\'ve already booked a private lesson for this time, do not use the Zoom link here. Please use the private link sent directly to your inbox.',
            'link': 'https://brocheballet-scheduling.as.me/schedule/8217481a',
            'link-text': 'Book here'
          },
          {
            'title': 'Beginner Follow-along Barre with 2 Hands on the Barre',
            'start-time': '17:45',
            'end-time': '18:10',
            'description': 'Classic Barre exercises (plié, tendu, dégagé, rond de jambe, fondu, and développé), but without the barre. Center Barre is a great way to build confidence away from your barre without worrying abou turns or tricky footwork.'
          },
          {
            'title': 'Advanced Barre for Strength & Stamina - Replay Class - Julie will watch & coach while you follow along with the class in the description.',
            'start-time': '18:15',
            'end-time': '19:00',
            'description': ''
          },
          {
            'title': 'Advanced Ballet Center - Replay Class - Julie will watch & coach while you follow along with the class in the description.',
            'start-time': '19:00',
            'end-time': '19:25',
            'description': ''
          }
        ],
        'Friday': [
          {
            'title': 'Artistry Barre',
            'start-time': '14:30',
            'end-time': '15:15',
            'description': 'Develop your artistry, musicality and expression as a dancer. Creative combinations to explore dynamics, characters, and movement qualities.'
          },
          {
            'title': 'Artistry Center Port de Bras Combination',
            'start-time': '15:15',
            'end-time': '15:30',
            'description': 'Enjoy a beautiful port de bras réverénce and artistry combination in the center'
          }
        ],
        'Saturday': [
          {
            'title': 'Beginner Ballet Barre',
            'start-time': '11:15',
            'end-time': '12:00',
            'description': 'Slow and methodical ballet barre for dancers new to ballet.',
            'link': 'https://app.brocheballetonline.com/programs/uFd8pZ0upnaOuteJphU4/sections/RW6OPFqTJCyMT2caw6m5-/p/0/v/0',
            'link-text': 'Recommended to complete "Absolute Beginner Barre 8-part Orientation" in Sticky Buns before joining'
          },
          {
            'title': 'Intermediate Ballet Barre',
            'start-time': '12:05',
            'end-time': '12:55',
            'description': 'A spicy barre to kick off your weekend. Expect a fast-paced class with minimal talking between combinations and short explanations of combinations.'
          },
          {
            'title': 'Pointe: Beyond the Beginner Level (ONLY BARRE, NO CENTER)',
            'start-time': '13:00',
            'end-time': '13:30',
            'description': 'This class bridges the gap between beginner and intermediate pointe. In this class, you’ll build the strength and confidence to try one-handed exercises, transitions away from the barre, and short combinations that start to feel more like real dancing. Perfect for pointe students looking to take their next step.'
          }
        ]
      }

      var classesToAdd = []
      if (payload.rotate1or2 === '4') {
        if (spring2026[payload.dayOfWeek]) {
          spring2026[payload.dayOfWeek].forEach((balletClass) => {
            classesToAdd.push(balletClass)
          })
        }
      }

      classesToAdd.forEach((balletClass) => {
        var startTime = payload.date.set({
          hour: (balletClass['start-time'].match(/(\d+):(\d+)/)[1]),
          minute: balletClass['start-time'].match(/(\d+):(\d+)/)[2]
        }).toDate()
        var endTime = payload.date.set({
          hour: (balletClass['end-time'].match(/(\d+):(\d+)/)[1]),
          minute: balletClass['end-time'].match(/(\d+):(\d+)/)[2]
        }).toDate()

        const add = {
          title: balletClass.title,
          link: balletClass['link'] || '',
          linkText: balletClass['link-text'] || '',
          description: balletClass.description,
          startDate: startTime,
          endDate: endTime
        }
        db.collection('balletClasses').add(add)
        .then(function (docRef) {
          console.log('Document written with ID: ', docRef.id)
          commit('createBalletClass', {
            ...balletClass,
            id: docRef.id
          })
        })
        .catch(function (error) {
          console.error('Error adding document: ', error)
        })
      })
    },
    loadUpcomingBalletClasses ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        var upcomingBalletClasses = []
        db.collection('balletClasses')
          .where('startDate', '>=', payload.startDate.toDate())
          .where('startDate', '<=', payload.endDate.toDate())
          .get()
          .then((querySnapshot) => {
            querySnapshot.forEach((doc) => {
              const balletClass = doc.data()
              upcomingBalletClasses.push({
                startDate: balletClass.startDate.toDate(),
                endDate: balletClass.endDate.toDate(),
                name: balletClass.title,
                description: balletClass.description,
                rotation: balletClass.rotation || '',
                balletClassId: doc.id
              })
            })
          commit('setUpcomingBalletClasses', upcomingBalletClasses)
          resolve()
        }).catch(
          error => {
            reject(error)
          }
        )
      })
    },
    createVideo ({ commit }, payload) {
      const video = {
        title: payload.title,
        imageUrl: payload.imageUrl,
        videoUrl: payload.videoUrl,
        description: payload.description,
        date: payload.date
      }
      db.collection('videos').add(video)
      .then(function (docRef) {
        commit('createVideo', {
          ...video,
          id: docRef.id
        })
      })
      .catch(function (error) {
        console.error('Error adding document: ', error)
      })
    },
    createBalletClass ({ commit }, payload) {
      const balletClass = {
        title: payload.title,
        description: payload.description,
        startDate: payload.startDate,
        endDate: payload.endDate,
        rotation: payload.rotation || ''
      }
      db.collection('balletClasses').add(balletClass)
      .then(function (docRef) {
        console.log('Document written with ID: ', docRef.id)
        commit('createBalletClass', {
          ...balletClass,
          id: docRef.id
        })
      })
      .catch(function (error) {
        console.error('Error adding document: ', error)
      })
    },
    updateBalletClassRecommendations ({ commit }, payload) {
      const shortenedPageUrl = Buffer.from(payload.classUrl).toString('base64')
      const recommendedClasses = {
        id: payload.id,
        shortenedPageUrl: shortenedPageUrl,
        classUrl: payload.classUrl
      }
      db.collection('recommended-classes').doc(shortenedPageUrl).set(recommendedClasses)
      .then(function () {
        console.log('Document updated with ID: ', payload.id)
      })
      .catch(function (error) {
        console.error('Error adding document: ', error)
      })
    },
    updateIABFPiece ({ commit, state }, payload) {
      var dancerIds = []
      if (payload.dancer1) {
        dancerIds.push(payload.dancer1)
      }
      if (payload.dancer2) {
        dancerIds.push(payload.dancer2)
      }
      if (payload.dancer3) {
        dancerIds.push(payload.dancer3)
      }
      if (payload.dancer4) {
        dancerIds.push(payload.dancer4)
      }
      if (payload.dancer5) {
        dancerIds.push(payload.dancer5)
      }
      if (payload.dancer6) {
        dancerIds.push(payload.dancer6)
      }
      if (payload.dancer7) {
        dancerIds.push(payload.dancer7)
      }
      if (payload.dancer8) {
        dancerIds.push(payload.dancer8)
      }
      if (payload.dancer9) {
        dancerIds.push(payload.dancer9)
      }
      if (payload.dancer10) {
        dancerIds.push(payload.dancer10)
      }
      if (payload.dancer11) {
        dancerIds.push(payload.dancer11)
      }
      if (payload.dancer12) {
        dancerIds.push(payload.dancer12)
      }
      const piece = {
        id: payload.id,
        title: payload.title,
        songArtist: payload.songArtist,
        programDancer: payload.programDancer,
        programTeacher: payload.programTeacher,
        programTitle: payload.programTitle,
        musicReceived: payload.musicReceived,
        lengthInSeconds: payload.lengthInSeconds,
        pieceType: payload.pieceType,
        groupSize: payload.groupSize,
        dancers: dancerIds,
        competition: payload.competition,
        showcaseBlock: payload.showcaseBlock || null,
        showcasePlacement: payload.showcasePlacement || null,
        namedDancers: payload.namedDancers || '',
        musicUrl: payload.musicUrl || '',
        musicFileName: payload.musicFileName || ''
      }
      return db.collection(piecesCol(state, true)).doc(payload.id).set(piece)
      .then(function () {
        console.log('Document updated with ID: ', payload.id)
      })
      .catch(function (error) {
        console.error('Error adding document: ', error)
        throw error
      })
    },
    updateIABFDancerGroupRoles ({ commit }, payload) {
      db.collection(IABF.dancers).doc(payload.id).update({
        firstRole: payload.firstRole,
        secondRole: payload.secondRole
      })
      .then(function () {
        console.log('Document updated with ID: ', payload.id)
      })
      .catch(function (error) {
        console.error('Error adding document: ', error)
      })
    },
    async updateIABFDancer ({ getters, state }, payload) {
      const cols = adminIabf(state)
      const dancersCol = (cols && cols.dancers) || IABF.dancers
      const nextEmail = normalizeEmail(payload.email)
      let prevRoom = ''
      try {
        const snap = await db.collection(dancersCol).doc(payload.id).get()
        const prev = snap.exists ? (snap.data() || {}) : {}
        prevRoom = prev.room || ''
        const prevEmail = normalizeEmail(prev.email)
        if (nextEmail && nextEmail !== prevEmail) {
          await changePersonPrimaryEmail(db, {
            personId: prev.personId || (getters.currentIABFPerson && getters.currentIABFPerson.id) || null,
            newEmail: nextEmail,
            oldEmail: prevEmail
          })
        }
      } catch (e) {
        console.error('IABF person email change failed:', e)
        throw e
      }
      const phone = String(payload.phone || '')
      return db.collection(dancersCol).doc(payload.id).update({
        id: payload.id,
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: nextEmail || payload.email,
        phone: payload.phone,
        last4: phone ? phone.substring(Math.max(0, phone.length - 4)) : '',
        addressStreet: payload.addressStreet || '',
        addressStreet2: payload.addressStreet2 || '',
        addressCity: payload.addressCity || '',
        addressState: payload.addressState || '',
        addressZip: payload.addressZip || '',
        addressCountry: payload.addressCountry || 'US',
        registrationDate: payload.registrationDate,
        earlyBirdGift: payload.earlyBirdGift || 'None',
        registrationGift1: payload.registrationGift1,
        registrationGift2: payload.registrationGift2,
        registrationGift3: payload.registrationGift3,
        registrationGift4: payload.registrationGift4 || 'None',
        room: payload.room,
        roomNumber: isOffsiteLodgingRoom(payload.room)
          ? ''
          : String(payload.roomNumber || '').trim(),
        primaryRoomId: payload.primaryRoomId,
        initialPayment: payload.initialPayment,
        initialPaymentLink: payload.initialPaymentLink,
        installments: payload.installments,
        payment1Due: payload.payment1Due,
        payment1Status: payload.payment1Status,
        payment1Link: payload.payment1Link,
        payment2Due: payload.payment2Due,
        payment2Status: payload.payment2Status,
        payment2Link: payload.payment2Link,
        payment3Due: payload.payment3Due,
        payment3Status: payload.payment3Status,
        payment3Link: payload.payment3Link,
        payment4Due: payload.payment4Due,
        payment4Status: payload.payment4Status,
        payment4Link: payload.payment4Link,
        payment5Due: payload.payment5Due,
        payment5Status: payload.payment5Status,
        payment5Link: payload.payment5Link,
        payment6Due: payload.payment6Due,
        payment6Status: payload.payment6Status,
        payment6Link: payload.payment6Link,
        stripeLink: payload.stripeLink
    })
      .then(async function () {
        console.log('Document updated with ID: ', payload.id)
        try {
          await adjustIabfStayInventory({
            year: cols.year,
            fromRoom: prevRoom,
            toRoom: payload.room
          })
        } catch (e) {
          console.warn('updateIABFDancer inventory adjust failed', e)
        }
        return refreshRegistrationInventory()
      })
      .catch(function (error) {
        console.error('Error adding document: ', error)
        throw error
      })
    },
    async updateDancerRoomNumber ({ state }, payload) {
      const id = payload && payload.id
      const cols = adminIabf(state)
      const dancersCol = (cols && cols.dancers) || IABF.dancers
      if (!id) {
        throw new Error('Missing dancer')
      }
      const roomNumber = String(payload.roomNumber || '').trim()
      await db.collection(dancersCol).doc(id).set({
        roomNumber,
        updatedAt: Date.now()
      }, { merge: true })
      const found = (state.loadedIABFDancers || []).find(d => d.id === id)
      if (found) {
        found.roomNumber = roomNumber
      }
    },
    refreshIabfRoomInventory () {
      return refreshRegistrationInventory()
    },
    recountIabfRoomInventory () {
      return recountRegistrationInventory()
    },
    async loadIabfPersonIdentity (_, { personId, email } = {}) {
      if (personId) {
        const byId = await loadPersonById(db, personId)
        if (byId) {
          return byId
        }
      }
      if (email) {
        return findPersonByEmail(db, email)
      }
      return null
    },
    async changeIabfPersonEmail ({ getters, commit }, { personId, newEmail, oldEmail } = {}) {
      const result = await changePersonPrimaryEmail(db, {
        personId: personId || (getters.currentIABFPerson && getters.currentIABFPerson.id) || null,
        newEmail,
        oldEmail
      })
      const person = await loadPersonById(db, result.personId)
      if (person && getters.currentIABFPerson && getters.currentIABFPerson.id === person.id) {
        commit('setCurrentIABFPerson', person)
      }
      if (person && getters.currentIABFDancer && getters.currentIABFDancer.personId === person.id) {
        commit('setCurrentIABFDancer', {
          ...getters.currentIABFDancer,
          email: result.email,
          personId: person.id
        })
      }
      return { ...result, person }
    },
    async linkIabfPersonEmail ({ getters, commit }, { personId, aliasEmail } = {}) {
      const result = await linkPersonEmailAlias(db, {
        personId: personId || (getters.currentIABFPerson && getters.currentIABFPerson.id) || null,
        aliasEmail
      })
      const person = await loadPersonById(db, result.personId)
      if (person && getters.currentIABFPerson && getters.currentIABFPerson.id === person.id) {
        commit('setCurrentIABFPerson', person)
      }
      return { ...result, person }
    },
    createIABFPiece ({ commit, state }, payload) {
      var dancerIds = []
      if (payload.dancer1) {
        dancerIds.push(payload.dancer1)
      }
      if (payload.dancer2) {
        dancerIds.push(payload.dancer2)
      }
      if (payload.dancer3) {
        dancerIds.push(payload.dancer3)
      }
      if (payload.dancer4) {
        dancerIds.push(payload.dancer4)
      }
      if (payload.dancer5) {
        dancerIds.push(payload.dancer5)
      }
      if (payload.dancer6) {
        dancerIds.push(payload.dancer6)
      }
      if (payload.dancer7) {
        dancerIds.push(payload.dancer7)
      }
      if (payload.dancer8) {
        dancerIds.push(payload.dancer8)
      }
      if (payload.dancer9) {
        dancerIds.push(payload.dancer9)
      }
      if (payload.dancer10) {
        dancerIds.push(payload.dancer10)
      }
      if (payload.dancer11) {
        dancerIds.push(payload.dancer11)
      }
      if (payload.dancer12) {
        dancerIds.push(payload.dancer12)
      }
      const piece = {
        title: payload.title,
        songArtist: payload.songArtist,
        programDancer: payload.programDancer,
        programTeacher: payload.programTeacher,
        programTitle: payload.programTitle,
        musicReceived: payload.musicReceived,
        lengthInSeconds: payload.lengthInSeconds,
        pieceType: payload.pieceType,
        groupSize: payload.groupSize,
        dancers: dancerIds,
        approvalStatus: 'approved'
      }
      return db.collection(piecesCol(state, true)).add(piece)
      .then(function (docRef) {
        console.log('Document written with ID: ', docRef.id)
        commit('createIABFPiece', {
          ...piece,
          id: docRef.id
        })
      })
      .catch(function (error) {
        console.error('Error adding document: ', error)
        throw error
      })
    },
    updateBalletClass ({ commit }, payload) {
      const balletClass = {
        id: payload.id,
        title: payload.title,
        description: payload.description,
        startDate: payload.startDate,
        endDate: payload.endDate,
        rotation: payload.rotation || ''
      }
      db.collection('balletClasses').doc(payload.id).set(balletClass)
      .then(function () {
        console.log('Document updated with ID: ', payload.id)
      })
      .catch(function (error) {
        console.error('Error adding document: ', error)
      })
    },
    deleteIABFPiece ({ commit, state }, payload) {
      db.collection(piecesCol(state, true)).doc(payload).delete()
      .then(function () {
        console.log('Document deleted with ID: ', payload)
      })
      .catch(function (error) {
        console.error('Error deleting document: ', error)
      })
    },
    /**
     * Hard-delete a dancer enrollment for the admin festival year.
     * Also drops that year from the linked person's yearsAttended so Festival
     * History no longer treats them as registered for a year that was removed.
     * Cascades: intro card, travel details, showcase pieces, carpool memberships.
     */
    async deleteIABFDancer ({ commit, state, getters }, payload) {
      const id = typeof payload === 'string' ? payload : (payload && payload.id)
      if (!id) {
        throw new Error('Missing dancer id')
      }
      const cols = adminIabf(state)
      const dancersCol = (cols && cols.dancers) || IABF.dancers
      const festivalYear = Number((cols && cols.year) || state.adminFestivalYear) || IABF_DANCER_YEAR
      const ref = db.collection(dancersCol).doc(id)
      let personId = null
      let travelDetailsId = null
      let deletedRoom = ''
      try {
        const snap = await ref.get()
        if (snap.exists) {
          const d = snap.data() || {}
          personId = d.personId || null
          travelDetailsId = d.travelDetailsId || null
          deletedRoom = d.room || ''
          // Fallback: resolve person by email if enrollment never got personId
          if (!personId && d.email) {
            try {
              const person = await resolvePersonForEnrollment(db, { email: d.email, personId: null })
              if (person && person.id) {
                personId = person.id
              }
            } catch (e) {
              console.warn('deleteIABFDancer person lookup failed', e)
            }
          }
        }
      } catch (e) {
        console.warn('deleteIABFDancer read before delete failed', e)
      }

      await cleanupDeletedDancerRelated(cols, id, travelDetailsId)

      await ref.delete()
      console.log('Document deleted with ID: ', id, 'col', dancersCol)

      try {
        await sweepOrphanedYearData(cols)
      } catch (e) {
        console.warn('deleteIABFDancer orphan sweep failed', e)
      }
      try {
        await adjustIabfStayInventory({
          year: festivalYear,
          fromRoom: deletedRoom,
          totalDelta: -1
        })
      } catch (e) {
        console.warn('deleteIABFDancer inventory adjust failed', e)
      }
      await refreshRegistrationInventory()

      if (personId) {
        try {
          const nextYears = await removeYearFromPerson(db, personId, festivalYear)
          if (getters.currentIABFPerson && getters.currentIABFPerson.id === personId) {
            commit('setCurrentIABFPerson', {
              ...getters.currentIABFPerson,
              yearsAttended: nextYears || []
            })
          }
        } catch (e) {
          console.warn('deleteIABFDancer could not update person yearsAttended', e)
        }
      }

      // Clear session if this was the logged-in dancer's enrollment
      if (getters.currentIABFDancer && getters.currentIABFDancer.id === id) {
        commit('setCurrentIABFDancer', {})
      }
      return { id, festivalYear, personId }
    },
    /**
     * Take one person off an archive-year roster (2022–2025).
     * Other festival years stay. Does not delete the person record.
     * A canceled-dancers row keeps a later attendance rebuild from
     * putting that year back (2022/2023 come from a pasted roster).
     */
    async removeDancerFromArchiveYear ({ state }, row) {
      const year = Number(state.adminFestivalYear)
      if (!isArchiveYear(year)) {
        throw new Error('Pick an archive year before removing someone.')
      }
      const person = row || {}
      const archiveOnly = !!person.archiveOnly
      const personId = String(person.personId || (archiveOnly ? person.id : '') || '').trim()
      const enrollmentId = archiveOnly ? '' : String(person.id || '').trim()
      const email = normalizeEmail(person.email || '')
      if (!personId && !enrollmentId) {
        throw new Error('Missing person')
      }
      const cols = iabfForYear(year)
      const cancelId = enrollmentId || personId
      await db.collection(cols.canceledDancers).doc(cancelId).set({
        firstName: person.firstName || '',
        lastName: person.lastName || '',
        email,
        personId: personId || null,
        cancelReason: 'Removed from archive roster',
        canceledAt: Date.now(),
        originalDancerId: enrollmentId || null,
        archiveRemoval: true,
        depositOnFileCents: 0,
        paymentsOnFileCents: 0,
        festivalYear: year,
        createdAt: Date.now(),
        updatedAt: Date.now()
      })
      if (cols.dancers && enrollmentId) {
        const ref = db.collection(cols.dancers).doc(enrollmentId)
        let travelDetailsId = null
        try {
          const snap = await ref.get()
          if (snap.exists) {
            travelDetailsId = (snap.data() || {}).travelDetailsId || null
          }
        } catch (e) {
          console.warn('removeDancerFromArchiveYear read failed', e)
        }
        await cleanupDeletedDancerRelated(cols, enrollmentId, travelDetailsId)
        await ref.delete()
        try {
          await sweepOrphanedYearData(cols)
        } catch (e) {
          console.warn('removeDancerFromArchiveYear orphan sweep failed', e)
        }
      }
      if (personId) {
        await removeYearFromPerson(db, personId, year)
      }
      return { year, personId, enrollmentId }
    },
    async sweepOrphanedDancerRelated ({ state }) {
      const cols = adminIabf(state)
      await sweepOrphanedYearData(cols)
    },
    deleteBalletClass ({ commit }, payload) {
      db.collection('balletClasses').doc(payload).delete()
      .then(function () {
        console.log('Document deleted with ID: ', payload)
      })
      .catch(function (error) {
        console.error('Error deleting document: ', error)
      })
    },
    markAttendance ({ commit }, payload) {
      payload.selectedDancers.forEach(function (item, index, arr) {
        const data = {
          balletClassId: payload.balletClassId,
          balletClassName: payload.balletClassTitle,
          dancerId: item.value,
          dancerName: item.text
        }
        const attendanceId = payload.balletClassId + '--' + item.value
        db.collection('balletClassAttendance').doc(attendanceId).set(data)
        .then(function () {
          commit('setLoadedDancersForClass', data)
          router.push('/ballet-classes')
        })
        .catch(function (error) {
          console.error('Error adding document: ', error)
        })
      })
    },
    async createIABFDancer ({ commit, state }, payload) {
      const email = normalizeEmail(payload.email)
      if (!email) {
        throw new Error('Email is required')
      }
      const phone = String(payload.phone || '').trim()
      const festivalYear = Number(state.adminFestivalYear) || IABF_DANCER_YEAR
      const cols = adminIabf(state)
      if (!cols.dancers) {
        throw new Error(`No dancer enrollment collection for ${festivalYear}`)
      }
      // Firestore rejects undefined field values — only write defined data.
      const dancer = {
        firstName: String(payload.firstName || '').trim(),
        lastName: String(payload.lastName || '').trim(),
        email,
        phone,
        last4: phone ? phone.slice(-4) : '',
        stripeLink: payload.stripeLink || '',
        registrationDate: payload.registrationDate || '',
        registrationGift1: payload.registrationGift1 || 'None',
        registrationGift2: payload.registrationGift2 || 'None',
        registrationGift3: payload.registrationGift3 || 'None',
        registrationGift4: payload.registrationGift4 || 'None',
        earlyBirdGift: 'None',
        room: payload.room || '',
        roomNumber: isOffsiteLodgingRoom(payload.room)
          ? ''
          : String(payload.roomNumber || '').trim(),
        primaryRoomId: payload.primaryRoomId || '',
        initialPayment: payload.initialPayment === '' || payload.initialPayment == null
          ? 0
          : Number(payload.initialPayment) || 0,
        initialPaymentLink: payload.initialPaymentLink || '',
        installments: !!payload.installments,
        payment1Due: Number(payload.payment1Due) || 0,
        payment2Due: Number(payload.payment2Due) || 0,
        payment3Due: Number(payload.payment3Due) || 0,
        payment4Due: Number(payload.payment4Due) || 0,
        payment5Due: Number(payload.payment5Due) || 0,
        payment6Due: Number(payload.payment6Due) || 0,
        festivalYear,
        createdAt: Date.now(),
        updatedAt: Date.now()
      }
      try {
        // Person first so the enrollment is born with personId (iabf-* only).
        const personId = await findOrCreatePerson(db, dancer, {
          yearsAttended: [festivalYear],
          festivalYear
        })
        dancer.personId = personId
        const docRef = await db.collection(cols.dancers).add(dancer)
        console.log('Document written with ID: ', docRef.id, 'col', cols.dancers)
        commit('createIABFDancer', {
          ...dancer,
          id: docRef.id
        })
        try {
          const person = await loadPersonById(db, personId)
          await recalculateAttendanceYearsForPerson(commit, person, {
            ...dancer,
            id: docRef.id
          })
        } catch (e) {
          console.warn('createIABFDancer attendance years failed', e)
        }
        try {
          await adjustIabfStayInventory({
            year: festivalYear,
            toRoom: dancer.room,
            totalDelta: 1
          })
        } catch (e) {
          console.warn('createIABFDancer inventory adjust failed', e)
        }
        await refreshRegistrationInventory()
        return { id: docRef.id, ...dancer }
      } catch (error) {
        console.error('Error adding document: ', error)
        throw error
      }
    },
    async loadIabfFaculty ({ state }) {
      const y = Number(state.adminFestivalYear) || IABF_DANCER_YEAR
      const colName = facultyCollection(y)
      if (!colName) {
        return []
      }
      const snap = await db.collection(colName).get()
      return snap.docs.map((doc) => facultyAsDancer({ id: doc.id, ...doc.data() }, y))
        .sort((a, b) => String(a.lastName || '').localeCompare(String(b.lastName || '')))
    },
    async createIabfFaculty ({ state }, payload) {
      const email = normalizeEmail(payload.email)
      if (!email) {
        throw new Error('Email is required')
      }
      const festivalYear = Number(state.adminFestivalYear) || IABF_DANCER_YEAR
      const colName = facultyCollection(festivalYear)
      if (!colName) {
        throw new Error('No faculty collection for this year.')
      }
      const existing = await db.collection(colName).where('email', '==', email).limit(1).get()
      if (!existing.empty) {
        throw new Error('That email is already on the faculty list for this year.')
      }
      const firstName = String(payload.firstName || '').trim()
      const lastName = String(payload.lastName || '').trim()
      const phone = String(payload.phone || '').trim()
      const personId = await findOrCreatePerson(db, {
        firstName,
        lastName,
        email,
        phone
      }, { yearsAttended: [], festivalYear })
      const person = await loadPersonById(db, personId)
      const yearsFaculty = Array.from(new Set([
        ...(person && person.yearsFaculty ? person.yearsFaculty : []),
        festivalYear
      ].map(Number).filter((n) => Number.isFinite(n) && n > 0))).sort((a, b) => a - b)
      const roles = Array.from(new Set([
        ...((person && person.roles) || []),
        'faculty'
      ]))
      await db.collection(IABF_PEOPLE_COLLECTION).doc(personId).set({
        yearsFaculty,
        roles,
        updatedAt: Date.now()
      }, { merge: true })
      const rec = {
        firstName,
        lastName,
        email,
        phone,
        personId,
        festivalYear,
        personType: 'faculty',
        isFaculty: true,
        room: String(payload.room || '').trim(),
        arrivalDate: String(payload.arrivalDate || '').trim(),
        departureDate: String(payload.departureDate || '').trim(),
        honorariumUsd: facultyMoneyNum(payload.honorariumUsd),
        travelReimbursedUsd: facultyMoneyNum(payload.travelReimbursedUsd),
        otherReimbursedUsd: facultyMoneyNum(payload.otherReimbursedUsd),
        payNotes: String(payload.payNotes || '').trim(),
        yearsAtIabf: facultyYearsAtIabfNum(payload.yearsAtIabf) || 1,
        createdAt: Date.now(),
        updatedAt: Date.now()
      }
      const ref = await db.collection(colName).add(rec)
      try {
        await adjustIabfStayInventory({
          year: festivalYear,
          toRoom: rec.room
        })
      } catch (e) {
        console.warn('createIabfFaculty inventory adjust failed', e)
      }
      await refreshRegistrationInventory()
      return { id: ref.id, ...rec }
    },
    async updateIabfFaculty ({ state }, payload) {
      const id = payload && payload.id
      const y = Number(state.adminFestivalYear) || IABF_DANCER_YEAR
      const colName = facultyCollection(y)
      if (!colName || !id) {
        throw new Error('Missing faculty record.')
      }
      const ref = db.collection(colName).doc(id)
      const snap = await ref.get()
      if (!snap.exists) {
        throw new Error('Faculty record not found.')
      }
      const prev = snap.data() || {}
      const patch = { updatedAt: Date.now() }
      if (Object.prototype.hasOwnProperty.call(payload, 'room')) {
        patch.room = String(payload.room || '').trim()
        if (isOffsiteLodgingRoom(patch.room) || !patch.room) {
          patch.roomNumber = ''
        }
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'roomNumber')) {
        patch.roomNumber = String(payload.roomNumber || '').trim()
        if (isOffsiteLodgingRoom(Object.prototype.hasOwnProperty.call(patch, 'room') ? patch.room : prev.room)) {
          patch.roomNumber = ''
        }
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'honorariumUsd')) {
        patch.honorariumUsd = facultyMoneyNum(payload.honorariumUsd)
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'travelReimbursedUsd')) {
        patch.travelReimbursedUsd = facultyMoneyNum(payload.travelReimbursedUsd)
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'otherReimbursedUsd')) {
        patch.otherReimbursedUsd = facultyMoneyNum(payload.otherReimbursedUsd)
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'payNotes')) {
        patch.payNotes = String(payload.payNotes || '').trim()
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'arrivalDate')) {
        patch.arrivalDate = String(payload.arrivalDate || '').trim()
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'departureDate')) {
        patch.departureDate = String(payload.departureDate || '').trim()
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'yearsAtIabf')) {
        patch.yearsAtIabf = facultyYearsAtIabfNum(payload.yearsAtIabf)
      }
      await ref.set(patch, { merge: true })
      if (Object.prototype.hasOwnProperty.call(patch, 'yearsAtIabf')) {
        const introCol = (adminIabf(state) || {}).dancerIntros
        if (introCol) {
          try {
            const introRef = db.collection(introCol).doc(id)
            const introSnap = await introRef.get()
            if (introSnap.exists) {
              await introRef.set({
                yearsAtIabf: patch.yearsAtIabf,
                showYearsAtIabf: patch.yearsAtIabf > 0,
                updatedAt: new Date()
              }, { merge: true })
            }
          } catch (e) {
            console.warn('faculty intro yearsAtIabf sync failed', e)
          }
        }
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'room')) {
        try {
          await adjustIabfStayInventory({
            year: y,
            fromRoom: prev.room,
            toRoom: patch.room
          })
        } catch (e) {
          console.warn('updateIabfFaculty inventory adjust failed', e)
        }
        await refreshRegistrationInventory()
      }
      return { id, ...prev, ...patch }
    },
    async deleteIabfFaculty ({ state }, id) {
      const y = Number(state.adminFestivalYear) || IABF_DANCER_YEAR
      const colName = facultyCollection(y)
      if (!colName || !id) {
        return
      }
      const ref = db.collection(colName).doc(id)
      const snap = await ref.get()
      const room = snap.exists ? ((snap.data() || {}).room || '') : ''
      await ref.delete()
      try {
        await adjustIabfStayInventory({
          year: y,
          fromRoom: room
        })
      } catch (e) {
        console.warn('deleteIabfFaculty inventory adjust failed', e)
      }
      await refreshRegistrationInventory()
    },
    createDancer ({ commit }, payload) {
      const dancer = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email
      }
      db.collection('dancers').add(dancer)
      .then(function (docRef) {
        console.log('Document written with ID: ', docRef.id)
        commit('createDancer', {
          ...dancer,
          id: docRef.id
        })
      })
      .catch(function (error) {
        console.error('Error adding document: ', error)
      })
    },
    getDancersForClass ({ commit }, payload) {
      db.collection('balletClassAttendance').where('balletClassId', '==', payload).get()
      .then(function (querySnapshot) {
          querySnapshot.forEach(function (doc) {
            // doc.data() is never undefined for query doc snapshots
            const data = {
              balletClassId: doc.data().balletClassId,
              balletClassName: doc.data().balletClassName,
              dancerId: doc.data().dancerId,
              dancerName: doc.data().dancerName
            }
            commit('setLoadedDancersForClass', data)
          })
      })
      .catch(function (error) {
          console.log('Error getting documents: ', error)
      })
    },
    loadBookmarkedVideosForCurrentDancer ({ commit }, payload) {
      commit('setLoading', true)
      if (this.state.currentDancer !== null) {
        db.collection('bookmarks-dancers').where('dancerId', '==', this.state.currentDancer.id).get()
        .then(function (querySnapshot) {
          var bookmarks = []
          querySnapshot.forEach(function (doc) {
            const bookmarkedVideo = {
              bookmarkId: doc.data().bookmarkId,
              dateAdded: doc.data().dateAdded,
              videoSlug: doc.data().videoSlug,
              videoThumbURL: doc.data().videoThumbURL || '',
              videoURL: doc.data().videoURL,
              title: doc.data().title,
              pageURL: doc.data().pageURL,
              id: doc.id
            }
            bookmarks.push(bookmarkedVideo)
          })
          commit('setBookmarkedVideosForCurrentDancer', bookmarks)
        })
      }
      commit('setLoading', false)
    },
    loadRecommendations ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        var recommendations = []
        db.collection('videos').where('pageURL', 'in', payload).get()
        .then(function (querySnapshot) {
          querySnapshot.forEach((doc) => {
            recommendations.push(doc.data())
          })
          commit('setLoadedRecommendations', recommendations)
          resolve()
        }).catch(error => {
          reject(error)
        })
      })
    },
    loadBalletClassRecommendations ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        db.collection('recommended-classes').where('id', '==', payload).get()
        .then(function (querySnapshot) {
          querySnapshot.forEach((doc) => {
            commit('setCurrentBalletClassRecommendations', doc.data())
          })
          resolve()
        }).catch(error => {
          reject(error)
        })
      })
    },
    loadBalletClass ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        db.collection('balletClasses').doc(payload).get()
        .then(function (doc) {
          commit('setCurrentBalletClass', doc.data())
          resolve()
        }).catch(error => {
          reject(error)
        })
      })
    },
    loadVideoWatchHistoryForCurrentDancer ({ commit }, payload) {
      commit('setLoading', true)
      if (this.state.currentDancer !== null) {
        db.collection('dancers-videos').where('dancerId', '==', this.state.currentDancer.id).orderBy('dateAdded', 'desc').get()
        .then(function (querySnapshot) {
          var watchedVideos = []
          querySnapshot.forEach(function (doc) {
            const watchedVideo = {
              dateAdded: doc.data().dateAdded,
              videoSlug: doc.data().videoSlug,
              videoThumbURL: doc.data().videoThumbURL,
              videoURL: doc.data().videoHref,
              title: doc.data().title,
              pageURL: doc.data().pageURL,
              id: doc.id
            }
            watchedVideos.push(watchedVideo)
          })
          commit('setVideoWatchHistoryForCurrentDancer', watchedVideos)
        })
      }
      commit('setLoading', false)
    },
    loadAttendanceForCurrentDancer ({ commit }, payload) {
      var attendanceForDancer = []
      if (this.state.currentDancer !== null) {
        db.collection('balletClassAttendance').where('dancerId', '==', this.state.currentDancer.id).get()
        .then(function (querySnapshot) {
          querySnapshot.forEach(function (doc) {
            attendanceForDancer.push(doc.data())
          })
        })
      }
      commit('setLoadedAttendanceForCurrentDancer', attendanceForDancer)
    },
    logDancerInByEmail ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        db.collection('dancers').where('email', '==', payload).get()
        .then(function (querySnapshot) {
          querySnapshot.forEach(function (doc) {
            const currentDancer = {
              id: doc.id,
              email: doc.data().email,
              firstName: doc.data().firstName,
              lastName: doc.data().lastName
            }
            commit('setCurrentDancer', currentDancer)
            resolve()
          })
        }).catch(
          error => {
            reject(error)
          }
        )
      })
    },
    logDancerInByMemberstackID ({ commit }, payload) {
      return new Promise((resolve, reject) => {
        db.collection('dancers').doc(payload).get()
        .then((doc) => {
          if (doc.exists) {
            const currentDancer = {
              id: doc.id,
              email: doc.data().email,
              firstName: doc.data().firstName,
              lastName: doc.data().lastName
            }
            commit('setCurrentDancer', currentDancer)
            resolve()
          } else {
            console.log('No such dancer')
          }
        }).catch(
          error => {
            reject(error)
          }
        )
      })
    },
    async signInIABFDancer ({ commit, dispatch, getters }, { email, password }) {
      commit('setLoading', true)
      commit('clearError')
      try {
        const result = await auth.signInWithEmailAndPassword(email, password)
        const user = {
          id: result.user.uid,
          email: result.user.email
        }
        commit('setUser', user)
        await dispatch('loadIABFDancerByUid', result.user.uid)
        // If no active enrollment, try by email (active years, then canceled + deposit)
        if (!getters.currentIABFDancer || !getters.currentIABFDancer.id) {
          await dispatch('linkIABFDancerByEmail', {
            email: email || result.user.email,
            uid: result.user.uid
          })
        }
        if (!getters.currentIABFDancer || !getters.currentIABFDancer.id) {
          commit('setLoading', false)
          throw new Error(
            'We signed you in, but couldn’t find an IABF registration, faculty invitation, or held deposit for this email. ' +
            'If you need help, email hello@iabf.dance.'
          )
        }
        commit('setLoading', false)
      } catch (error) {
        commit('setLoading', false)
        if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
          throw new Error('Invalid email or password.')
        }
        if (error && error.message && String(error.message).indexOf('couldn’t find an IABF') !== -1) {
          throw error
        }
        if (error && error.message && String(error.message).indexOf("couldn't find an IABF") !== -1) {
          throw error
        }
        throw new Error(error.message || 'Sign-in failed. Please try again.')
      }
    },
    async signUpIABFDancer ({ commit, getters, dispatch }, { email, password }) {
      commit('setLoading', true)
      commit('clearError')
      try {
        // Verify email exists as active dancer OR canceled with deposit (Cloud Function)
        const verifyEmail = functions.httpsCallable('verifyIABFDancerEmail')
        const verifyResult = await verifyEmail({ email })
        if (!verifyResult.data.exists) {
          commit('setLoading', false)
          throw new Error(
            'We couldn’t find an IABF registration, faculty invitation, or held deposit with that email.'
          )
        }
        // Create Firebase auth account (shared with brocheballetonline.com)
        const result = await auth.createUserWithEmailAndPassword(email, password)
        const user = {
          id: result.user.uid,
          email: result.user.email
        }
        commit('setUser', user)
        // Link uid to active enrollment, or load canceled deposit account
        await dispatch('linkIABFDancerByEmail', {
          email,
          uid: result.user.uid
        })
        if (!getters.currentIABFDancer || !getters.currentIABFDancer.id) {
          commit('setLoading', false)
          throw new Error(
            'Account created, but we couldn’t load your IABF record. Email hello@iabf.dance.'
          )
        }
        commit('setLoading', false)
      } catch (error) {
        commit('setLoading', false)
        if (error.code === 'auth/email-already-in-use') {
          throw new Error('An account with this email already exists. Try signing in instead.')
        }
        throw error
      }
    },
    async linkIABFDancerByEmail ({ commit }, { email, uid }) {
      const found = await findLoginAccount({ uid, email })
      if (found && found.kind === 'faculty') {
        await hydrateFacultyRecord(commit, found.doc, found.year, uid)
        return
      }
      if (found && found.kind === 'dancer') {
        const enrollment = await linkUidToEnrollmentAndPerson(found.doc, uid)
        await hydrateEnrollmentWithPerson(commit, enrollment, found.year)
        return
      }
      let person = null
      try {
        person = await findPersonByEmail(db, email)
      } catch (e) {
        console.warn('linkIABFDancerByEmail person lookup failed', e)
      }
      // No active enrollment — canceled dancer with deposit on file?
      const canceled = await findCanceledDancerByEmail(email, person && person.id)
      if (canceled) {
        // Best-effort: store uid on canceled doc for next login by uid
        if (uid && canceled.collection && canceled.id) {
          try {
            await db.collection(canceled.collection).doc(canceled.id).set({
              uid,
              updatedAt: Date.now()
            }, { merge: true })
          } catch (e) {
            console.warn('Could not write uid on canceled dancer:', e)
          }
        }
        const account = canceledAccountFromRecord(canceled)
        commit('setIabfDancerYear', canceled.year)
        commit('setCurrentIABFPerson', person || null)
        commit('setCurrentIABFDancer', account)
      }
    },
    async loadIABFDancerByUid ({ commit, getters }, uid) {
      // Avoid flipping global loading for nested login hydrate (prevents stuck spinner)
      if (!uid) {
        return
      }
      // Prefer newest year (2027), then wrap-up years (2026, …).
      // Check faculty in that same year before falling back to an older dancer
      // row — otherwise a 2026 dancer ticket hides a 2027 faculty invite.
      const email = normalizeEmail((getters.user && getters.user.email) || '')
      const found = await findLoginAccount({ uid, email })
      if (found && found.kind === 'faculty') {
        await hydrateFacultyRecord(commit, found.doc, found.year, uid)
        return
      }
      if (found && found.kind === 'dancer') {
        const enrollment = await linkUidToEnrollmentAndPerson(found.doc, uid)
        await hydrateEnrollmentWithPerson(commit, enrollment, found.year)
        return
      }
      let person = null
      try {
        person = await findPersonByUid(db, uid)
        if (!person && email) {
          person = await findPersonByEmail(db, email)
        }
      } catch (e) {
        console.warn('loadIABFDancerByUid person lookup failed', e)
      }
      if (person && person.id) {
        const byPerson = await findLoginAccount({ uid, email, personId: person.id })
        if (byPerson && byPerson.kind === 'faculty') {
          await hydrateFacultyRecord(commit, byPerson.doc, byPerson.year, uid)
          return
        }
        if (byPerson && byPerson.kind === 'dancer') {
          const enrollment = await linkUidToEnrollmentAndPerson(byPerson.doc, uid)
          await hydrateEnrollmentWithPerson(commit, enrollment, byPerson.year)
          return
        }
        const canceled = await findCanceledDancerByEmail(email, person.id)
        if (canceled) {
          if (uid && canceled.collection && canceled.id) {
            try {
              await db.collection(canceled.collection).doc(canceled.id).set({
                uid,
                updatedAt: Date.now()
              }, { merge: true })
            } catch (e) {
              console.warn('Could not write uid on canceled dancer:', e)
            }
          }
          const account = canceledAccountFromRecord(canceled)
          commit('setIabfDancerYear', canceled.year)
          commit('setCurrentIABFPerson', person)
          commit('setCurrentIABFDancer', account)
          return
        }
      }
      // Canceled accounts may have uid after first login
      for (const c of canceledDancerCollections()) {
        try {
          const snapshot = await db.collection(c.name).where('uid', '==', uid).limit(1).get()
          if (!snapshot.empty) {
            const doc = snapshot.docs[0]
            const account = canceledAccountFromRecord({
              id: doc.id,
              data: doc.data(),
              year: c.year,
              collection: c.name
            })
            commit('setIabfDancerYear', c.year)
            commit('setCurrentIABFPerson', null)
            commit('setCurrentIABFDancer', account)
            return
          }
        } catch (e) {
          console.warn('canceled uid lookup failed for', c.name, e)
        }
      }
      // No enrollment — clear any stale session dancer (e.g. after admin delete).
      // Keep person profile so Festival History still has past yearsAttended.
      commit('setCurrentIABFDancer', {})
      if (person && person.id) {
        commit('setCurrentIABFPerson', person)
        return
      }
      try {
        const byUid = await findPersonByUid(db, uid)
        commit('setCurrentIABFPerson', byUid || null)
      } catch (e) {
        console.warn('loadIABFDancerByUid person fallback failed', e)
        commit('setCurrentIABFPerson', null)
      }
    },
    signUserUp ({ commit }, payload) {
      commit('setLoading', true)
      commit('clearError')
      auth.createUserWithEmailAndPassword(payload.email, payload.password)
        .then(
          user => {
            commit('setLoading', false)
            const newUser = {
              id: user.uid,
              email: user.email,
              attendedClasses: []
            }
            commit('setUser', newUser)
          }
        ).catch(
          error => {
            commit('setLoading', false)
            commit('setError', error)
          }
        )
    },
    async signUserIn ({ commit, dispatch, getters }, payload) {
      commit('setLoading', true)
      commit('clearError')
      try {
        const result = await auth.signInWithEmailAndPassword(payload.email, payload.password)
        const newUser = {
          id: result.user.uid,
          email: result.user.email,
          attendedClasses: []
        }
        commit('setUser', newUser)
        // Hydrate dancer enrollment after auth. Permission errors here must not
        // block login (admins have no enrollment; rules races are non-fatal).
        try {
          await dispatch('loadIABFDancerByUid', result.user.uid)
          if (!getters.currentIABFDancer || !getters.currentIABFDancer.id) {
            await dispatch('linkIABFDancerByEmail', {
              email: result.user.email,
              uid: result.user.uid
            })
          }
        } catch (hydrateErr) {
          console.warn('Post-login dancer hydrate failed:', hydrateErr)
        }
        commit('setLoading', false)
      } catch (error) {
        commit('setLoading', false)
        // Only surface real auth failures on the login form
        if (!isPermissionError(error)) {
          commit('setError', error)
        } else {
          console.warn('signUserIn permission noise (ignored):', error)
        }
      }
    },
    async autoSignIn ({ commit, dispatch, getters }, payload) {
      const newUser = {
        id: payload.uid,
        email: payload.email,
        attendedClasses: []
      }
      commit('setUser', newUser)
      // Rehydrate currentIABFDancer so a page refresh on the storefront
      // (or any IABF-dashboard page) doesn't drop the dancer's benefits.
      try {
        await dispatch('loadIABFDancerByUid', payload.uid)
        if (!getters.currentIABFDancer || !getters.currentIABFDancer.id) {
          await dispatch('linkIABFDancerByEmail', {
            email: payload.email,
            uid: payload.uid
          })
        }
      } catch (e) { /* non-fatal — admin users won't have a dancer doc */ }
    },
    async loadPhotoshootConfig ({ state }, { useAdminYear } = {}) {
      const cols = useAdminYear === false ? dancerIabf(state) : adminIabf(state)
      if (!cols || !cols.config) {
        return { ...DEFAULT_PHOTOSHOOT_CONFIG }
      }
      const snap = await db.collection(cols.config).doc('photoshoot').get()
      if (!snap.exists) {
        return { ...DEFAULT_PHOTOSHOOT_CONFIG }
      }
      return { ...DEFAULT_PHOTOSHOOT_CONFIG, ...(snap.data() || {}) }
    },
    async savePhotoshootConfig ({ state }, payload) {
      const cols = adminIabf(state)
      const indoorLocation = String((payload && payload.indoorLocation) || '').trim() || DEFAULT_PHOTOSHOOT_CONFIG.indoorLocation
      const outdoorLocation = String((payload && payload.outdoorLocation) || '').trim() || DEFAULT_PHOTOSHOOT_CONFIG.outdoorLocation
      const defaultDirectorName = String((payload && payload.defaultDirectorName) || '').trim() || DEFAULT_PHOTOSHOOT_CONFIG.defaultDirectorName
      const defaultDirectorId = String((payload && payload.defaultDirectorId) || '').trim()
      const defaultEditorName = String((payload && payload.defaultEditorName) || '').trim()
      const defaultEditorId = String((payload && payload.defaultEditorId) || '').trim()
      const defaultEditorUid = String((payload && payload.defaultEditorUid) || '').trim()
      const defaultEditorEmail = String((payload && payload.defaultEditorEmail) || '').trim().toLowerCase()
      const cancelUntilIso = String((payload && payload.cancelUntilIso) || '').trim()
      const dancerBookingOpen = !!(payload && payload.dancerBookingOpen)
      await db.collection(cols.config).doc('photoshoot').set({
        indoorLocation,
        outdoorLocation,
        defaultDirectorName,
        defaultDirectorId,
        defaultEditorName,
        defaultEditorId,
        defaultEditorUid,
        defaultEditorEmail,
        slotPriceCents: DEFAULT_PHOTOSHOOT_CONFIG.slotPriceCents,
        editedPhotosPerSlot: DEFAULT_PHOTOSHOOT_CONFIG.editedPhotosPerSlot,
        extraPhotoCents: DEFAULT_PHOTOSHOOT_CONFIG.extraPhotoCents,
        allProofsCents: DEFAULT_PHOTOSHOOT_CONFIG.allProofsCents,
        cancelUntilIso,
        dancerBookingOpen,
        updatedAt: Date.now()
      }, { merge: true })
      return {
        indoorLocation,
        outdoorLocation,
        defaultDirectorName,
        defaultDirectorId,
        defaultEditorName,
        defaultEditorId,
        defaultEditorUid,
        defaultEditorEmail,
        cancelUntilIso,
        dancerBookingOpen
      }
    },
    async loadPhotoshootSlots ({ state }, { useAdminYear } = {}) {
      const cols = useAdminYear === false ? dancerIabf(state) : adminIabf(state)
      if (!cols || !cols.photoshootSlots) {
        return []
      }
      const snap = await db.collection(cols.photoshootSlots).get()
      return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const d = String(a.date || '').localeCompare(String(b.date || ''))
          if (d !== 0) {
            return d
          }
          const set = String(a.setting || '').localeCompare(String(b.setting || ''))
          if (set !== 0) {
            return set
          }
          return (Number(a.startMin) || 0) - (Number(b.startMin) || 0)
        })
    },
    async addPhotoshootBlock ({ state }, block) {
      const cols = adminIabf(state)
      const date = String((block && block.date) || '').trim()
      const setting = (block && block.setting) === 'outdoor' ? 'outdoor' : 'indoor'
      const windows = splitBlockIntoSlots(block && block.start, block && block.end)
      if (!date || !windows.length) {
        throw new Error('Pick a date and a time range of at least 15 minutes.')
      }
      const configSnap = await db.collection(cols.config).doc('photoshoot').get()
      const config = { ...DEFAULT_PHOTOSHOOT_CONFIG, ...(configSnap.exists ? configSnap.data() : {}) }
      const directorName = String((block && block.directorName) || config.defaultDirectorName || 'Julie Gill').trim()
      const directorId = String((block && block.directorId) || config.defaultDirectorId || '').trim()
      const location = locationForSetting(config, setting)
      const blockId = date + '_' + setting + '_' + windows[0].startMin + '_' + Date.now()
      const refs = windows.map((w) => {
        const id = photoshootSlotId(date, setting, w.startMin)
        return { w, id, ref: db.collection(cols.photoshootSlots).doc(id) }
      })
      const existing = await Promise.all(refs.map((r) => r.ref.get()))
      const batch = db.batch()
      let added = 0
      let skipped = 0
      refs.forEach((r, i) => {
        if (existing[i].exists) {
          skipped += 1
          return
        }
        batch.set(r.ref, {
          date,
          startMin: r.w.startMin,
          endMin: r.w.endMin,
          setting,
          location,
          directorName,
          directorId,
          kind: 'shoot',
          status: 'open',
          holdUntil: null,
          holdDancerId: null,
          bookingId: null,
          bookedDancerId: null,
          bookedDancerName: '',
          blockId,
          createdAt: Date.now()
        })
        added += 1
      })
      if (added) {
        await batch.commit()
      }
      return { added, skipped, blockId }
    },
    async updatePhotoshootSlot ({ state }, { id, fields }) {
      const cols = adminIabf(state)
      if (!id) {
        throw new Error('Missing slot')
      }
      const patch = { ...(fields || {}), updatedAt: Date.now() }
      await db.collection(cols.photoshootSlots).doc(id).set(patch, { merge: true })
    },
    async deletePhotoshootSlot ({ state }, id) {
      const cols = adminIabf(state)
      if (!id) {
        return
      }
      await db.collection(cols.photoshootSlots).doc(id).delete()
    },
    async deletePhotoshootBlock ({ state }, blockId) {
      const cols = adminIabf(state)
      if (!blockId) {
        return 0
      }
      const snap = await db.collection(cols.photoshootSlots).where('blockId', '==', blockId).get()
      const batch = db.batch()
      snap.docs.forEach((doc) => {
        batch.delete(doc.ref)
      })
      await batch.commit()
      return snap.size
    },
    async loadPhotoshootBookings ({ state }, { useAdminYear } = {}) {
      const cols = useAdminYear === false ? dancerIabf(state) : adminIabf(state)
      if (!cols || !cols.photoshootBookings) {
        return []
      }
      const snap = await db.collection(cols.photoshootBookings).get()
      return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
    },
    async loadMyPhotoshootBookings ({ state, getters }) {
      const cols = dancerIabf(state)
      if (!cols || !cols.photoshootBookings) {
        return []
      }
      const uid = getters.user && getters.user.uid
      const dancerId = getters.currentIABFDancer && getters.currentIABFDancer.id
      let snap
      if (uid) {
        snap = await db.collection(cols.photoshootBookings).where('uid', '==', uid).get()
      } else if (dancerId) {
        snap = await db.collection(cols.photoshootBookings).where('dancerId', '==', dancerId).get()
      } else {
        return []
      }
      return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
    },
    async loadPhotoshootPhotos ({ state }, { useAdminYear, bookingId, dancerId } = {}) {
      const cols = useAdminYear === false ? dancerIabf(state) : adminIabf(state)
      if (!cols || !cols.photoshootPhotos) {
        return []
      }
      let query = db.collection(cols.photoshootPhotos)
      if (bookingId) {
        query = query.where('bookingId', '==', bookingId)
      } else if (dancerId) {
        query = query.where('dancerId', '==', dancerId)
      }
      const snap = await query.get()
      return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (Number(a.capturedAt) || Number(a.createdAt) || 0) - (Number(b.capturedAt) || Number(b.createdAt) || 0))
    },
    async loadMyPhotoshootPhotos ({ state, getters }) {
      const cols = dancerIabf(state)
      if (!cols || !cols.photoshootPhotos) {
        return []
      }
      const uid = getters.user && getters.user.uid
      const dancerId = getters.currentIABFDancer && getters.currentIABFDancer.id
      let snap
      if (uid) {
        snap = await db.collection(cols.photoshootPhotos).where('uid', '==', uid).get()
      } else if (dancerId) {
        snap = await db.collection(cols.photoshootPhotos).where('dancerId', '==', dancerId).get()
      } else {
        return []
      }
      return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (Number(a.capturedAt) || Number(a.createdAt) || 0) - (Number(b.capturedAt) || Number(b.createdAt) || 0))
    },
    async addPhotoshootPhotoDocs ({ state }, photos) {
      const cols = adminIabf(state)
      const rows = Array.isArray(photos) ? photos : []
      if (!rows.length) {
        return []
      }
      const created = []
      const chunk = 400
      for (let i = 0; i < rows.length; i += chunk) {
        const batch = db.batch()
        rows.slice(i, i + chunk).forEach((row) => {
          const ref = db.collection(cols.photoshootPhotos).doc()
          const doc = {
            bookingId: row.bookingId || '',
            dancerId: row.dancerId || '',
            personId: row.personId || null,
            uid: row.uid || '',
            dancerEmail: String(row.dancerEmail || '').toLowerCase(),
            dancerName: row.dancerName || '',
            kind: row.kind || 'proof',
            storagePath: row.storagePath || '',
            downloadUrl: row.downloadUrl || '',
            fileName: row.fileName || '',
            capturedAt: Number(row.capturedAt) || Date.now(),
            hidden: false,
            hiddenBy: null,
            rating: null,
            selected: false,
            edited: false,
            editedUrl: '',
            editorId: row.editorId || '',
            editorName: row.editorName || '',
            editorUid: row.editorUid || '',
            editorEmail: String(row.editorEmail || '').toLowerCase(),
            festivalYear: cols.year,
            createdAt: Date.now(),
            updatedAt: Date.now()
          }
          batch.set(ref, doc)
          created.push({ id: ref.id, ...doc })
        })
        await batch.commit()
      }
      return created
    },
    async updatePhotoshootPhoto ({ state }, { id, fields, useAdminYear }) {
      const cols = useAdminYear === false ? dancerIabf(state) : adminIabf(state)
      if (!id) {
        throw new Error('Missing photo')
      }
      const patch = { ...(fields || {}), updatedAt: Date.now() }
      await db.collection(cols.photoshootPhotos).doc(id).set(patch, { merge: true })
    },
    async movePhotoshootPhotos ({ state }, { photoIds, booking }) {
      const cols = adminIabf(state)
      const ids = Array.isArray(photoIds) ? photoIds.filter(Boolean) : []
      if (!ids.length || !booking) {
        return 0
      }
      const batch = db.batch()
      ids.forEach((id) => {
        batch.set(db.collection(cols.photoshootPhotos).doc(id), {
          bookingId: booking.id || '',
          dancerId: booking.dancerId || '',
          personId: booking.personId || null,
          uid: booking.uid || '',
          dancerEmail: String(booking.dancerEmail || '').toLowerCase(),
          dancerName: booking.dancerName || '',
          editorId: booking.editorId || '',
          editorName: booking.editorName || '',
          editorUid: booking.editorUid || '',
          editorEmail: String(booking.editorEmail || '').toLowerCase(),
          updatedAt: Date.now()
        }, { merge: true })
      })
      await batch.commit()
      if (booking.id) {
        await db.collection(cols.photoshootBookings).doc(booking.id).set({
          proofsReady: true,
          proofCount: Number(booking.proofCount || 0) + ids.length,
          updatedAt: Date.now()
        }, { merge: true })
      }
      return ids.length
    },
    async assignPhotoshootEditor ({ state }, { bookingId, editor }) {
      const cols = adminIabf(state)
      if (!bookingId) {
        throw new Error('Missing booking')
      }
      const patch = {
        editorId: (editor && editor.id) || '',
        editorName: (editor && editor.name) || '',
        editorUid: (editor && editor.uid) || '',
        editorEmail: String((editor && editor.email) || '').toLowerCase(),
        updatedAt: Date.now()
      }
      await db.collection(cols.photoshootBookings).doc(bookingId).set(patch, { merge: true })
      const snap = await db.collection(cols.photoshootPhotos).where('bookingId', '==', bookingId).get()
      if (!snap.empty) {
        const batch = db.batch()
        snap.docs.forEach((doc) => {
          batch.set(doc.ref, patch, { merge: true })
        })
        await batch.commit()
      }
      return patch
    },
    async markPhotoshootBookingProofsReady ({ state }, { bookingId, addCount }) {
      const cols = adminIabf(state)
      if (!bookingId) {
        return
      }
      const ref = db.collection(cols.photoshootBookings).doc(bookingId)
      const snap = await ref.get()
      const current = snap.exists ? Number((snap.data() || {}).proofCount) || 0 : 0
      await ref.set({
        proofsReady: true,
        proofCount: current + Math.max(0, Number(addCount) || 0),
        updatedAt: Date.now()
      }, { merge: true })
    },
    async loadGroupPieces ({ state }, { useAdminYear } = {}) {
      const cols = useAdminYear === false ? dancerIabf(state) : adminIabf(state)
      if (!cols || !cols.groupPieces) {
        return []
      }
      const snap = await db.collection(cols.groupPieces).get()
      return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
    },
    async saveGroupPiece ({ state }, piece) {
      const cols = adminIabf(state)
      if (!cols || !cols.groupPieces) {
        throw new Error('No group-pieces collection for this year.')
      }
      const title = String((piece && piece.title) || '').trim()
      if (!title) {
        throw new Error('Give the piece a name.')
      }
      const payload = {
        title,
        facultyName: String((piece && piece.facultyName) || '').trim(),
        facultyId: String((piece && piece.facultyId) || '').trim(),
        facultyEmail: String((piece && piece.facultyEmail) || '').trim().toLowerCase(),
        facultyUid: String((piece && piece.facultyUid) || '').trim(),
        notes: String((piece && piece.notes) || '').trim(),
        openForRegistration: piece && piece.openForRegistration !== false,
        allDancerIds: Array.isArray(piece && piece.allDancerIds) ? piece.allDancerIds : [],
        dancers: Array.isArray(piece && piece.dancers) ? piece.dancers : [],
        rehearsals: Array.isArray(piece && piece.rehearsals) ? piece.rehearsals : [],
        updatedAt: Date.now()
      }
      if (piece && piece.id) {
        await db.collection(cols.groupPieces).doc(piece.id).set(payload, { merge: true })
        return { id: piece.id, ...payload }
      }
      payload.createdAt = Date.now()
      const ref = await db.collection(cols.groupPieces).add(payload)
      return { id: ref.id, ...payload }
    },
    async deleteGroupPiece ({ state }, id) {
      const cols = adminIabf(state)
      if (!id) {
        return
      }
      await db.collection(cols.groupPieces).doc(id).delete()
    },
    async markPhotoshootEditedReady ({ state }, bookingId) {
      const cols = adminIabf(state)
      if (!bookingId) {
        return
      }
      await db.collection(cols.photoshootBookings).doc(bookingId).set({
        editedReady: true,
        editedReadyAt: Date.now(),
        updatedAt: Date.now()
      }, { merge: true })
    },
    signout ({ commit }) {
      auth.signOut()
      commit('setUser', null)
      commit('setCurrentIABFDancer', null)
      commit('setCurrentIABFPerson', null)
      commit('setIabfDancerYear', null)
      router.push('/')
    },
    clearError ({ commit }) {
      commit('clearError')
    }
  },
  getters: {
    currentDancer (state) {
      return state.currentDancer
    },
    isAdmin (state) {
      const email = String(state.user && state.user.email || '').trim().toLowerCase()
      return email === 'julie@brocheballet.com' || email === 'jen@redaccordionstudios.com'
    },
    isFinanceAdmin (state) {
      const email = String(state.user && state.user.email || '').trim().toLowerCase()
      return email === 'julie@brocheballet.com'
    },
    currentIABFDancer (state) {
      if (state.masqueradeDancerId) {
        return state.masqueradeDancer ||
          (state.loadedIABFDancers || []).find(d => d.id === state.masqueradeDancerId) ||
          state.currentIABFDancer
      }
      return state.currentIABFDancer
    },
    currentIABFPerson (state) {
      return state.currentIABFPerson
    },
    adminFestivalYear (state) {
      return state.adminFestivalYear || IABF_DANCER_YEAR
    },
    /** Collection map for the admin year switcher. */
    adminIabf (state) {
      return iabfForYear(state.adminFestivalYear || IABF_DANCER_YEAR)
    },
    iabfWhatsappInviteUrl (state) {
      return (state.iabfCommunityLinks && state.iabfCommunityLinks.whatsappInviteUrl) || ''
    },
    /** Festival year of the logged-in dancer enrollment (2027, 2026 wrap-up, …). */
    iabfDancerYear (state, getters) {
      if (state.masqueradeDancerId) {
        const masq = getters.masqueradeDancer
        const y = Number((masq && masq.festivalYear) || state.adminFestivalYear)
        if (Number.isFinite(y) && y > 0) {
          return y
        }
      }
      return state.iabfDancerYear || IABF_DANCER_YEAR
    },
    /** Collection map for the logged-in dancer's year. */
    dancerIabf (state) {
      return dancerIabf(state)
    },
    iabfOperableYears () {
      return IABF_OPERABLE_YEARS.slice()
    },
    iabfAdminYears () {
      return IABF_ADMIN_YEARS.slice()
    },
    isAdminArchiveYear (state) {
      return isArchiveYear(state.adminFestivalYear || IABF_DANCER_YEAR)
    },
    masqueradeDancer (state) {
      if (!state.masqueradeDancerId) {
        return null
      }
      return state.masqueradeDancer ||
        (state.loadedIABFDancers || []).find(d => d.id === state.masqueradeDancerId) ||
        null
    },
    currentTravelDetails (state) {
      return state.currentTravelDetails
    },
    currentBalletClass (state) {
      return state.currentBalletClass
    },
    currentBalletClassRecommendations (state) {
      return state.currentBalletClassRecommendations
    },
    upcomingBalletClasses (state) {
      return state.upcomingBalletClasses
    },
    loadedTravelDetails (state) {
      return state.loadedTravelDetails
    },
    loadedAllTravelDetails (state) {
      return state.loadedAllTravelDetails
    },
    loadedAttendanceForCurrentDancer (state) {
      return state.loadedAttendanceForCurrentDancer
    },
    loadedVideos (state) {
      return state.loadedVideos.sort((videoA, videoB) => {
        return videoA.date > videoB.date
      })
    },
    loadedRecommendations (state) {
      return state.loadedRecommendations
    },
    loadedBalletClasses (state) {
      return state.loadedBalletClasses.sort((classA, classB) => {
        return classA.date > classB.date
      })
    },
    loadedBookmarksForCurrentDancer (state) {
      return state.loadedBookmarksForCurrentDancer
    },
    loadedVideoWatchHistoryForCurrentDancer (state) {
      return state.loadedWatchHistoryForCurrentDancer
    },
    loadedDancers (state) {
      return state.loadedDancers.sort((dancerA, dancerB) => {
        return dancerA.firstName > dancerB.firstName
      })
    },
    loadedIABFPiecesforDancer (state) {
      return state.loadedIABFPiecesforDancer
    },
    corpsRecordings (state) {
      return state.corpsRecordings || {}
    },
    corpsNotes (state) {
      return state.corpsNotes || {}
    },
    corpsStartPositions (state) {
      return state.corpsStartPositions || {}
    },
    loadedIABFDancers (state) {
      return state.loadedIABFDancers.sort((dancerA, dancerB) => {
        return dancerA.firstName > dancerB.firstName
      })
    },
    costumeInventory (state) {
      return state.costumeInventory.slice().sort((a, b) => a.name.localeCompare(b.name))
    },
    festivalInventory (state) {
      return (state.festivalInventory || []).slice()
    },
    costumeCloset (state) {
      return (state.costumeCloset || []).slice()
    },
    closetLoans (state) {
      return (state.closetLoans || []).slice()
    },
    dancerLedger (state) {
      return (state.dancerLedger || []).slice()
    },
    guestRegistrations (state) {
      return state.guestRegistrations
    },
    allGuestRegistrations (state) {
      return state.allGuestRegistrations
    },
    iabfCarpoolSlots (state) {
      return state.iabfCarpoolSlots
    },
    setupPlan (state) {
      return state.setupPlan
    },
    accountingProductLines (state) {
      return state.accountingProductLines
    },
    accountingTransactions (state) {
      return state.accountingTransactions
    },
    canceledDancers (state) {
      return state.canceledDancers
    },
    currentIABFPiece (state) {
      return state.currentIABFPiece
    },
    loadedIABFCorpsPieces (state) {
      return state.loadedIABFCorpsPieces
    },
    currentIABFCorpsPiece (state) {
      return state.currentIABFCorpsPiece
    },
    loadedIABFCorpsPiecesForDancer (state) {
      return state.loadedIABFCorpsPiecesForDancer
    },
    loadedIABFPieces (state) {
      return state.loadedIABFPieces.sort((pieceA, pieceB) => {
        return pieceA.pieceType > pieceB.pieceType
      })
    },
    featuredVideos (state, getters) {
      return getters.loadedVideos.slice(0, 5)
    },
    loadedDancersforClass (state) {
      return balletClassId => {
        return state.loadedDancersforClass[balletClassId]
      }
    },
    loadedVideo (state) {
      return videoId => {
        return state.loadedVideos.find((video) => {
          return video.id === videoId
        })
      }
    },
    loadedBalletClass (state) {
      return balletClassId => {
        return state.loadedBalletClasses.find((balletClass) => {
          return balletClass.id === balletClassId
        })
      }
    },
    user (state) {
      return state.user
    },
    loading (state) {
      return state.loading
    },
    error (state) {
      return state.error
    },
    layout (state) {
      return state.layout
    }
  }
})
